import NewOrderView from "./Views/newOrderView.js";
import * as model from "./model.js";
import { supabase } from "./supabase.js";
import { itemImageSrc } from "./itemVisual.js";
import "./tooltip.js";
import "./modalBackdrop.js";
import "./inputFilters.js";
import AuthView from "./Views/authView.js";
import MenuListView from "./Views/menuListView.js";
import MenuImportView from "./Views/menuImportView.js";
import NewMenuItemView from "./Views/newMenuItemView.js";
import NewOrderItemView from "./Views/newOrderItemView.js";
import OrderCheckOutView from "./Views/orderCheckoutView.js";
import newMenuItemView from "./Views/newMenuItemView.js";
import MenuEditView from "./Views/menuEditView.js";
import SettingsView from "./Views/settingsView.js";
import ReceiptView from "./Views/receiptView.js";
import CashflowView from "./Views/cashflowView.js";
import DiscountView from "./Views/discountView.js";
import PromoView from "./Views/promoView.js";
import LoyaltyView from "./Views/loyaltyView.js";
import { computeAllStreaks, computeStreak } from "./loyalty.js";
import GettingStartedView from "./Views/gettingStartedView.js";
import StaffView from "./Views/staffView.js";
import TimesheetView from "./Views/timesheetView.js";
import InventoryView from "./Views/inventoryView.js";
import OnShiftView from "./Views/onShiftView.js";
import KDSView from "./Views/kdsView.js";
import channel, { MSG } from "./channel.js";
import { init as initAnalytics, destroy as destroyAnalytics } from "./analytics.js";
import ReportsView from "./Views/reportsView.js";
import SupportView from "./Views/supportView.js";
import NotifView from "./Views/notificationView.js";
import OnboardingView from "./Views/onboardingView.js";
import { fetchReportsSalesRaw } from "./model.js";
import { getTaxSuggestion } from "./taxRates.js";
import { esc } from "./escape.js";
import { formatMoney, currencySymbol } from "./currency.js";
import { exemptLabel } from "./inclusiveTax.js";
import btPrinter from "./btPrinter.js";
import { encodeReceipt } from "./escpos.js";
import { getTokenInteractive } from "./turnstile.js";
import { formatInvoiceNumber } from "./invoiceNumber.js";
import * as sourceTracking from "./sourceTracking.js";
import { orderTypeLabel } from "./orderTypeLabel.js";

const modelState = model.state;
let item;

const _randomUUID = () => {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
};

// ── Toast notifications ───────────────────────────────────────────────────────

const showToast = function (message, type = 'error') {
  const toast = document.createElement('div');
  toast.className = `toast toast--${type}`;
  toast.textContent = message;
  document.body.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add('toast--visible'));
  setTimeout(() => {
    toast.classList.remove('toast--visible');
    toast.addEventListener('transitionend', () => toast.remove(), { once: true });
  }, 3500);
};

// ── App loading overlay ───────────────────────────────────────────────────────

const _appLoadingEl = document.getElementById('appLoadingOverlay');

const showLoadingScreen = function () {
  _appLoadingEl?.classList.remove('hidden');
};

const hideLoadingScreen = function () {
  _appLoadingEl?.classList.add('hidden');
};
//adding/displaying menu list

//to add edit/delete option
const controlMenuList = async function () {
  if (!model.hasPermission('manage_menu')) { showToast("You don't have access to the catalogue.", 'error'); return; }
  try {
    const state = model.state;
    MenuListView._categoriesOpen = false; // always open the catalogue collapsed/focused on items
    MenuListView._selectMode = false; // always open out of bulk-select mode
    MenuListView._selectedIds.clear();
    MenuListView.render(state);
    MenuListView._addHandlerCloseModal();
    NewMenuItemView._mapMenuCategoriesMarkUp(state.menuCategories);
  } catch (err) {
    showToast(err.message ?? err);
  }
};

// First-run prompt on the home page: show "Set up your menu" only when the
// catalogue is empty AND this user can manage the menu. Re-run on load and after
// any add/import/delete so it appears/disappears live.
const _updateMenuSetupBanner = function () {
  const banner = document.getElementById('menuSetupBanner');
  if (!banner) return;
  const empty = (model.state.menuItems?.length ?? 0) === 0;
  // The Getting Started card contains this same prompt as its first step
  // (identical banner styling), so the standalone banner yields while the
  // card is active - it still covers manage_menu users who can't see the
  // card, and returns if the catalogue empties after the card retires.
  const gs = model.state.settings;
  const checklistActive = !gs.gettingStartedDismissed && !gs.gettingStartedCompleted
    && model.hasPermission('manage_business');
  banner.classList.toggle('hidden', !(empty && model.hasPermission('manage_menu') && !checklistActive));
  // Menu mutations also move the checklist's "Add your first items" step.
  _updateGettingStarted();
};

// ── Getting Started checklist ────────────────────────────────────────────────
// Live onboarding steps computed from state; every row deep-links to the panel
// where the step gets done. Shown only to manage_business holders while the
// business is neither dismissed (the x, gettingStartedDismissed) nor completed
// (gettingStartedCompleted - written once every step is done). Deliberately
// independent of the menu setup banner above, which ALWAYS returns while the
// catalogue is empty. Phase 2 (todos): an activity feed replaces this card.

let _gsHasSale = false;          // sticky once true (a sale can't un-happen)
let _gsSalePromise = null;       // de-dupes the one-time has-any-sale DB check
let _gsShownIncomplete = false;  // celebrate only if the user watched it finish
let _gsFinishing = false;

const _gettingStartedSteps = function () {
  const s = model.state;
  return [
    { id: 'items',     label: 'Add your first items',                  done: (s.menuItems?.length ?? 0) > 0 },
    // Deliberately NOT keyed on "an adjustment exists": onboarding's tax
    // suggestion can create one before the owner has ever seen the panel.
    // The step is about actually reviewing your tax setup, so it checks off
    // when the Receipt Adjustments panel is opened (write-once flag below).
    { id: 'taxes',     label: 'Set up taxes & fees',                   done: !!s.settings.taxSetupSeen },
    { id: 'inventory', label: 'Set up inventory',                      done: !!s.settings.inventorySetupSeen },
    { id: 'receipt',   label: 'Set up your receipt',                   done: !!s.settings.receiptSetupSeen },
    // Also view-based, NOT "a second staff row exists": staff invitations may
    // become a paid-tier feature, so the checklist must never require one.
    { id: 'team',      label: 'Set up your team',                      done: !!s.settings.teamSetupSeen },
    { id: 'sale',      label: 'Make your first sale',                  done: _gsHasSale },
  ];
};

// ── Home activity feed ───────────────────────────────────────────────────────
// Real events (model.fetchActivityFeed) take over the Getting Started card once
// the checklist retires. Fetches are debounced + rate-limited and never join
// the boot Promise.all; while the tab is hidden refreshes collapse into one
// catch-up fetch on refocus (same pattern as the on-shift poll).

let _activityEvents = [];        // last fetched events (payloads power click-through)
let _activityLoaded = false;     // distinguishes "still fetching" from "genuinely empty"
let _activityDebounce = null;
let _activityLastFetch = 0;
let _activityInflight = false;
let _activityDirty = false;      // a refresh was requested while hidden/inflight

const _activityFeedActive = function () {
  const gs = model.state.settings;
  return model.hasPermission('manage_business')
    && (gs.gettingStartedDismissed || gs.gettingStartedCompleted);
};

// A row is clickable when the user can reach its drill-down target: the
// sale-receipt modal needs nothing extra (the feed is manage_business-only),
// the rest map to the target panel's own permission gate.
const _activityClickable = function (type) {
  if (type === 'sale' || type === 'void') return true;
  if (type === 'clock_in' || type === 'clock_out')
    return model.hasPermission('manage_pay') || model.hasPermission('manage_staff');
  if (type === 'expense') return model.hasPermission('manage_cashflow');
  if (type === 'customer_enrolled') return model.hasPermission('manage_loyalty');
  return model.hasPermission('manage_inventory'); // stock_adjust / low_stock / out_of_stock / po_received
};

const _activityDisplayEntries = () =>
  _activityEvents.map((ev) => ({ ...ev, clickable: _activityClickable(ev.type) }));

const _renderActivityFeed = function () {
  GettingStartedView.renderActivityFeed(_activityDisplayEntries(), { loaded: _activityLoaded });
  if (GettingStartedView.isActivityModalOpen())
    GettingStartedView.renderActivityModalList(_activityDisplayEntries());
};

const _fetchActivityNow = async function () {
  if (_activityInflight) { _activityDirty = true; return; }
  _activityInflight = true;
  _activityLastFetch = Date.now();
  try {
    _activityEvents = await model.fetchActivityFeed();
    _activityLoaded = true;
    _renderActivityFeed();
  } catch { /* keep the last good list */ }
  _activityInflight = false;
  if (_activityDirty) { _activityDirty = false; _requestActivityRefresh(); }
};

// Debounced (a burst of sales = one round of queries) + min 5s between fetches.
const _requestActivityRefresh = function () {
  if (!_activityFeedActive()) return;
  if (document.hidden) { _activityDirty = true; return; }
  clearTimeout(_activityDebounce);
  const wait = Math.max(1200, _activityLastFetch + 5000 - Date.now());
  _activityDebounce = setTimeout(_fetchActivityNow, wait);
};

document.addEventListener('visibilitychange', () => {
  if (!document.hidden && _activityDirty) { _activityDirty = false; _requestActivityRefresh(); }
});

// Relative labels ("Just now" -> "1m ago") age at render time, so tick a pure
// re-render of the cached events every minute - no queries, and the card has
// no entrance animation so the innerHTML swap can't blink.
setInterval(() => {
  if (!document.hidden && _activityLoaded && _activityFeedActive()) _renderActivityFeed();
}, 60000);

// ── Navbar notification bell ─────────────────────────────────────────────────
// Draft POs / pending shift corrections / pending leave requests - the three
// async "needs a manager's decision" queues in the app today. Gated on the
// SIGNED-IN account's own capabilities: hasPermission() reads state.currentStaff's
// role (model.js) and is never touched by Quick Switch Cashier, which only
// reassigns state.currentCashier - so switching to a manager cashier mid-shift
// can never reveal this to a non-manager account.
let _notifData = { pos: [], corrections: [], leave: [] };
let _notifLastFetch = 0;
let _closeNotifMenu = () => {}; // set once the dropdown is wired, see _addHandlers

const _refreshNotifications = async function () {
  const canPOs = model.hasPermission('manage_inventory');
  const canPay = model.hasPermission('manage_pay');
  if (!canPOs && !canPay) return; // stays hidden - never shown via currentCashier
  _notifLastFetch = Date.now();
  try {
    const [pos, corrections, leave] = await Promise.all([
      canPOs ? model.loadPurchaseOrders().then(() => model.state.purchaseOrders.filter((p) => p.status === 'draft')) : Promise.resolve([]),
      canPay ? model.fetchShiftEditRequests() : Promise.resolve([]),
      canPay ? model.fetchLeaveRequests().then((rows) => rows.filter((r) => r.status === 'pending')) : Promise.resolve([]),
    ]);
    _notifData = { pos, corrections, leave };
    NotifView.reveal();
    NotifView.render(_notifData, { canPOs, canPay });
  } catch { /* keep the last good counts; the next poll/open retries */ }
};

// Throttled refetch for the bell-open click - avoids double-fetching right
// after the boot fetch or on a rapid double-toggle.
const _requestNotifRefresh = function () {
  if (document.hidden) return;
  if (Date.now() - _notifLastFetch < 20000) return;
  _refreshNotifications();
};

// No realtime push for corrections/leave today (see the plan doc); a light
// poll covers all three, paused while the tab is hidden like the on-shift poll.
setInterval(() => { if (!document.hidden) _refreshNotifications(); }, 60000);

const controlNotifOpenCategory = async function (cat) {
  _closeNotifMenu();
  if (cat === 'po') {
    await controlOpenInventory();
    InventoryView.showDraftPOs();
  } else if (cat === 'corrections') {
    TimesheetView.setSection('corrections');
    await controlOpenTimesheets();
  } else if (cat === 'leave') {
    TimesheetView.setSection('leave');
    await controlOpenTimesheets();
  }
};

const controlNotifOpenItem = async function (cat, id) {
  _closeNotifMenu();
  if (cat === 'po') {
    await controlOpenInventory();
    InventoryView.focusPO(id);
  } else if (cat === 'corrections') {
    TimesheetView.setSection('corrections');
    await controlOpenTimesheets();
    TimesheetView.focusCorrection(id);
  } else if (cat === 'leave') {
    TimesheetView.setSection('leave');
    await controlOpenTimesheets();
    TimesheetView.focusLeaveRequest(id);
  }
};

const controlActivityClick = function (evtId) {
  const ev = _activityEvents.find((e) => e.id === evtId);
  if (!ev || !_activityClickable(ev.type)) return;
  // Drill-down replaces the list modal (panels sit below the modal backdrop).
  GettingStartedView.closeActivityModal();
  if (ev.type === 'sale' || ev.type === 'void') {
    // Payload is the full sales row, so this works straight from the home page;
    // showSaleReceipt itself shows the VOIDED banner and hides Reprint for voids.
    CashflowView.showSaleReceipt(ev.payload, () => controlReprintSale(ev.payload));
  } else if (ev.type === 'clock_in' || ev.type === 'clock_out') controlOpenTimesheets();
  else if (ev.type === 'expense') controlOpenCashflow();
  else if (ev.type === 'customer_enrolled') controlOpenLoyalty();
  else controlOpenInventory();
};

const _updateGettingStarted = async function () {
  if (!model.hasPermission('manage_business')) {
    GettingStartedView.hide();
    return;
  }
  const { gettingStartedDismissed, gettingStartedCompleted } = model.state.settings;
  if (gettingStartedDismissed || gettingStartedCompleted) {
    // Checklist retired (done or x-ed) - the card becomes the activity feed.
    _renderActivityFeed();
    _requestActivityRefresh();
    return;
  }
  if (!_gsHasSale) {
    if (_todayTransactionCount > 0) _gsHasSale = true;
    else {
      _gsSalePromise ??= model.hasAnySale().catch(() => false);
      _gsHasSale = await _gsSalePromise;
    }
  }
  const steps = _gettingStartedSteps();
  if (!steps.every((st) => st.done)) {
    _gsShownIncomplete = true;
    GettingStartedView.render(steps);
    return;
  }
  if (_gsFinishing) return;
  _gsFinishing = true;
  model.setBusinessSetting('gettingStartedCompleted', true);
  if (_gsShownIncomplete) {
    // The user watched the last step finish - celebrate briefly, then the
    // activity feed takes over the card.
    GettingStartedView.renderCelebrate(steps.length);
    setTimeout(() => { _renderActivityFeed(); _requestActivityRefresh(); }, 6000);
  } else {
    // Everything was already done before first paint - straight to the feed.
    _renderActivityFeed();
    _requestActivityRefresh();
  }
};

const _wireGettingStarted = function () {
  GettingStartedView._addHandlerDismiss(() => {
    model.setBusinessSetting('gettingStartedDismissed', true);
    // The x retires the CHECKLIST; the activity feed still gets the spot
    // (rendered by the banner updater below via _updateGettingStarted).
    _updateMenuSetupBanner(); // the standalone catalogue banner may take back over
  });
  GettingStartedView._addHandlerViewAll(() => GettingStartedView.showActivityModal(_activityDisplayEntries()));
  GettingStartedView._addHandlerActivityClick(controlActivityClick);
  GettingStartedView._addHandlerStep((step) => {
    if (step === 'items') controlMenuList();
    else if (step === 'taxes') controlOpenDiscounts();
    else if (step === 'inventory') controlOpenInventory();
    else if (step === 'receipt') { SettingsView.open(); controlOpenSettings(); SettingsView._switchTab('pos'); }
    else if (step === 'team') controlOpenStaff();
    else if (step === 'sale') controlNewOrder();
  });
  // "Set up your receipt" checks off when a manage_business holder actually
  // views Settings > POS (where the receipt toggles live) - write-once.
  SettingsView._onTabView = (tab) => {
    if (tab !== 'pos' || model.state.settings.receiptSetupSeen || !model.hasPermission('manage_business')) return;
    model.setBusinessSetting('receiptSetupSeen', true);
    _updateGettingStarted();
  };
};

// Bulk CSV menu import. Opens the import modal; the view handles parsing the
// file + assembling/previewing items, and calls back here on Confirm. We pass
// the current catalogue (names + categories, lowercased) so the preview can flag
// duplicates and brand-new categories. The actual write is one batched insert.
const controlUploadMenuCsv = function () {
  if (!model.hasPermission('manage_menu')) { showToast("You don't have access to the catalogue.", 'error'); return; }
  const ctx = {
    existingNames: new Set(model.state.menuItems.map((i) => i.itemName.toLowerCase())),
    existingCategories: new Set(model.state.menuCategories.map((c) => c.toLowerCase())),
  };
  MenuImportView.open(ctx, async (items, newCategories) => {
    const { added } = await model.bulkImportMenuItems(items, newCategories);
    MenuListView.refresh(model.state);
    NewMenuItemView._mapMenuCategoriesMarkUp(model.state.menuCategories);
    _updateMenuSetupBanner();
    showToast(`Imported ${added} item${added === 1 ? '' : 's'}.`, 'success');
  });
};

const controlBulkSetStatus = async function (status, ids) {
  if (!ids.length) { showToast('Select at least one item first.', 'info'); return; }
  try {
    await model.bulkUpdateItemStatus(ids, status);
    MenuListView._exitSelectMode();
    MenuListView.refresh(model.state);
    showToast(`${ids.length} item${ids.length > 1 ? 's' : ''} set to ${status}.`, 'success');
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlBulkDelete = async function (ids) {
  if (!ids.length) { showToast('Select at least one item first.', 'info'); return; }
  const n = ids.length;
  if (!confirm(`Delete ${n} item${n > 1 ? 's' : ''}? They will be removed from your catalogue but kept in past sales and reports (shown as "deleted").`)) return;
  try {
    await model.softDeleteItems(ids);
    MenuListView._exitSelectMode();
    MenuListView.refresh(model.state);
    _updateMenuSetupBanner();
    showToast(`${n} item${n > 1 ? 's' : ''} deleted.`, 'success');
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlDeleteMenuItem = async function (id) {
  try {
    await model.deleteMenuItem(id);
    MenuListView.refresh(model.state);
    _updateMenuSetupBanner();
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlShowEditMenu = async function (id) {
  try {
    item = model.state.menuItems.find((item) => item._id === id);
    const categories = model.state.menuCategories;

    // NOTE: don't call MenuEditView._clear() here - its _parentElement is
    // .modal-parent (the catalogue), so clearing it would delete the catalogue
    // behind the edit modal and you'd land on the home page on close. The edit
    // form lives in .edit-form-parent, which _insertEditMenuMarkup clears itself.
    const invCtx = model.hasPermission('manage_inventory') ? {
      canManage: true,
      items: model.state.inventoryItems,
      links: model.state.inventoryLinks.filter((l) => l.menuItemId === id),
      autoItem: model.state.inventoryItems.find((i) => i.linkedMenuItemId === id) ?? null,
      itemVariants: item.variants ?? [],
    } : null;
    // Tax status is only shown when the business runs tax-inclusive pricing -
    // inert/confusing otherwise for the majority (US, tax-exclusive) of users.
    const taxCtx = model.state.settings.taxInclusive
      ? { enabled: true, exemptLabel: exemptLabel() }
      : { enabled: false };
    MenuEditView._insertEditMenuMarkup(item, invCtx, taxCtx);
    MenuEditView._mapMenuCategoriesMarkUp(categories, item.category);
    MenuEditView._newEditCategoryToggle(async (name) => {
      try {
        await model.addCategory(name);
        NewMenuItemView._mapMenuCategoriesMarkUp(model.state.menuCategories);
        MenuEditView._mapMenuCategoriesMarkUp(model.state.menuCategories, name);
      } catch (err) {
        showToast(err.message ?? err);
      }
    });

    MenuEditView._updateItemData(async (data) => {
      if (data.category === "new-category") data.category = item.category;
      // Read the inventory section synchronously - the submit handler removes
      // the modal from the DOM right after this callback is invoked.
      const invCfg = MenuEditView.getInventoryConfig();
      try {
        await model.updateMenuItem(item._id, data);
        MenuListView.refresh(model.state); // refresh the catalogue behind the edit modal
        showToast("Menu item updated.", "success");
      } catch (err) {
        showToast(err.message ?? err);
        return;
      }
      // Inventory config saves after the item itself; a failure here must not
      // roll back the item save - it just toasts.
      if (invCfg) {
        try {
          await model.saveProductInventory(item._id, invCfg);
          NewOrderView.updateStockBadges?.(model.productStockStatuses());
          channel.postMessage({ type: MSG.INVENTORY_SYNC });
        } catch (err) {
          showToast(`Item saved, but stock tracking failed: ${err.message ?? err}`);
        }
      }

      const modal = document.querySelector(".item-modal-overlay");
      if (
        !modal.classList.contains("hidden") &&
        NewOrderItemView._basket?.id === item._id
      ) {
        const imgEl = modal.querySelector(".item-image");
        const updatedItem = model.state.menuItems.find(
          (i) => i._id === item._id,
        );
        if (imgEl) {
          imgEl.style.transition = "opacity 0.3s ease";
          imgEl.style.opacity = 0;
          setTimeout(() => {
            imgEl.src = itemImageSrc(updatedItem);
            imgEl.style.opacity = 1;
          }, 100);
        }
      }
    });
  } catch (err) {
    showToast(err.message ?? err);
  }
};

//adding new menu category
const controlAddNewCategory = async function (data) {
  try {
    await model.addCategory(data);
    NewMenuItemView._mapMenuCategoriesMarkUp(model.state.menuCategories);
    const newCategory = model.state.menuCategories[model.state.menuCategories.length - 1];
    const categorySelect = document.getElementById('categorySelect');
    if (categorySelect) categorySelect.value = newCategory;
  } catch (err) {
    showToast(err.message ?? err);
  }
};

//listening for buttons to close/open new menu item
const controlNewMenuButtonToggle = function () {
  NewMenuItemView._toggleModalClose();
  NewMenuItemView._toggleModalOpen();
};

//listens to uploadItem form button
const controlUploadItem = async function (data) {
  const invalidCategories = ["Select category", "new-category"];
  const categoryMissing = !data.category || invalidCategories.includes(data.category);
  if (categoryMissing) {
    if (data.newCategory?.trim()) {
      data.category = data.newCategory.trim();
    } else {
      data.category = "uncategorized";
    }
  }

  try {
    data.variants = newMenuItemView._addedVariants;
    await model.uploadNewMenuItem(data);
    MenuListView.refresh(model.state);
    _updateMenuSetupBanner();
  } catch (err) {
    showToast(err.message ?? err);
  }
};

//listens to new order button and renders the markup
const controlNewOrder = async function () {
  if (!model.hasPermission('take_orders')) { showToast("You don't have access to take orders.", 'error'); return; }
  try {
    NewOrderView.render(modelState);
  } catch (err) {
    showToast(err.message ?? err);
  }
};

//finds tbe item ID & fills in data according to the ID found
const controlDisplayMenuItem = function (id) {
  const item = model.state.menuItems.find((item) => item._id === id);
  if (!item) return;
  NewOrderItemView._itemModalContentUpdate(item);
};

const controlPushToModelCart = function () {
  if (NewOrderItemView._basket.length <= 0) {
    throw "No item selected yet";
  }
  NewOrderItemView._basket.quantity = NewOrderItemView._qty;
  NewOrderItemView._basket.selectedVariants = NewOrderItemView._variants;

  const basePrice = Number(NewOrderItemView._basket.price);
  const variantsTotal = NewOrderItemView._basket.selectedVariants.reduce(
    (acc, variant) => acc + Number(variant.variantPrice),
    0,
  );
  const quantity = Number(NewOrderItemView._basket.quantity);
  NewOrderItemView._basket.totalPrice = (basePrice + variantsTotal) * quantity;
  // Stable per-line identity - cart items otherwise carry only the menu item's
  // id, which two lines of the same product (or same product/different
  // variants) would share. Loyalty reward redemptions key off this.
  NewOrderItemView._basket.lineId = _randomUUID();

  model.state.cart.push(NewOrderItemView._basket);
  _broadcastCart();
  // Surgical cart patch (list + total + header pill, new row slides in) - the
  // rest of the POS screen stays put, so no full-screen re-render "blink" and
  // the active category tab survives.
  if (!NewOrderView.updateCart({ added: true })) NewOrderView.render(modelState);
  // Out-of-stock is a heads-up modal, never a block (counts drift; the sale
  // is real). The item is already in the cart; Continue just dismisses.
  const stockSt = model.productStockStatus(NewOrderItemView._basket.id);
  if (stockSt.state === 'out') NewOrderView.showStockNotice(stockSt.out);
};

//Listents to "checkout event" and wraps up transaction
const controlOrderCheckout = function () {
  try {
    if (model.state.cart.length === 0) throw `You must add an item to the cart`;

    const subtotal = modelState.cart.reduce((acc, item) => acc + item.totalPrice, 0);
    model.initReceiptAdjustments();
    const adjResult = model.calculateAdjustments(
      subtotal,
      model.state.currentReceiptAdjustments,
    );

    OrderCheckOutView._subtotal = subtotal;
    OrderCheckOutView._adjResult = adjResult;
    OrderCheckOutView._totalPrice = adjResult.finalTotal;
    OrderCheckOutView._orderType = model.state.settings.orderTypeEnabled ? 'dine-in' : null;
    OrderCheckOutView._paymentMethod = 'cash';
    // Checkout loyalty is its own toggleable capability (use_loyalty) - the
    // section renders only for holders (RLS enforces the same server-side).
    OrderCheckOutView._canUseLoyalty = model.hasPermission('use_loyalty');
    OrderCheckOutView.render(modelState);
    // Forward step: checkout slides in from the right
    document.querySelector('.modal-parent .pos-screen')?.classList.add('pos-screen--slide-fwd');
  } catch (err) {
    showToast(err.message ?? err);
  }
};

// Cart -> RPC item-line shape. Only cosmetic fields (line id, date) and the
// selection intent (item id, quantity, variant names) travel to the server -
// all pricing is recomputed there from the real menu_items/variants rows, so
// a tampered client-side price/totalPrice is never trusted.
const _toRpcItems = (cart) => cart.map((it) => ({
  item_id: it.id,
  quantity: it.quantity,
  selected_variants: (it.selectedVariants ?? []).map((v) => ({ variant_name: v.variantName })),
  line_id: it.lineId,
  date: it.date,
}));

// Splits the checkout's live adjustment list by source for record_sale():
// auto (business template refs - value override trusted, name/type/calc
// re-fetched server-side by id) and manual (fully trusted ad hoc entries -
// managerial discretion, out of scope for $ verification) travel as adjustment
// refs; loyalty_reward entries travel as cart-line references so the RPC can
// look up the server-recomputed line total to comp. Promo code and the $-off
// loyalty redemption are NOT sent as adjustment refs - the RPC recomputes
// both fresh from state (discount_codes / loyalty settings + balance) so
// their value can never be client-supplied.
const _splitAdjustmentsForRpc = function (adjustments) {
  const active = (adjustments ?? []).filter((a) => !a.removed);
  return {
    adjustmentTemplates: active
      .filter((a) => a.source === 'auto')
      .map((a) => ({ id: a.id, applied_value: a.appliedValue })),
    manualAdjustments: active
      .filter((a) => a.source === 'manual')
      .map((a) => ({
        name: a.name, type: a.type, calculation: a.calculation,
        value: a.value, applied_value: a.appliedValue, approved_by: a.approvedBy ?? null,
      })),
    loyaltyRewards: active
      .filter((a) => a.source === 'loyalty_reward')
      .map((a) => ({ reward_id: a.rewardId, cart_line_id: a.cartItemLineId })),
  };
};

const _buildSale = function () {
  const cashierName    = _cashierDisplayName(model.state.currentCashier) || model.state.username || '';
  const loggedInCashier = model.state.username || '';
  return {
    id: _randomUUID(),
    // Stamp each item's CURRENT tax class onto the sale record now, so a later
    // edit to the menu item's tax class never retroactively changes this receipt
    // (same reasoning as taxInclusiveRate below).
    items: modelState.cart.map((it) => ({ ...it, taxClass: model.resolveItemTaxClass(it.id) })),
    subtotal: OrderCheckOutView._subtotal ?? OrderCheckOutView._totalPrice,
    adjustments: OrderCheckOutView._adjResult?.lineItems ?? [],
    removedAdjustments: model.state.currentReceiptAdjustments.filter((a) => a.removed),
    showRemovedAdjustments: model.state.settings.showRemovedAdjustments,
    totalPrice: OrderCheckOutView._totalPrice,
    customerPayment: OrderCheckOutView._customerPayment,
    customerChange: OrderCheckOutView._customerChange,
    promoCode: model.state.currentPromoCode ?? null,
    storeName:       model.state.businessName ?? model.state.username,
    businessPhone:   model.state.businessPhone   ?? null,
    addressStreet:   model.state.businessAddressStreet   ?? null,
    addressCity:     model.state.businessAddressCity     ?? null,
    addressProvince: model.state.businessAddressProvince ?? null,
    addressZip:      model.state.businessAddressZip      ?? null,
    addressCountry:  model.state.businessAddressCountry  ?? null,
    cashierName,
    loggedInCashier,
    showCashier: model.state.settings.showCashierName !== false,
    showInvoiceNumber: model.state.settings.showInvoiceNumber !== false,
    showTicketNumber: model.state.settings.showTicketNumber !== false,
    taxInclusiveRate: model.state.settings.taxInclusive ? model.state.settings.taxInclusiveRate : null,
    orderType: OrderCheckOutView._orderType ?? 'dine-in',
    paymentMethod: OrderCheckOutView._paymentMethod ?? 'cash',
    customerId: model.state.currentLoyaltyCustomer?.id ?? null,
    loyaltyRedeemedPoints: model.loyaltyReservedPoints(),
    date: Date.now(),
  };
};

// ── Order Queue (KDS) ─────────────────────────────────────────────────────────

const _generateTicketNumber = function () {
  const active = new Set(modelState.orderQueue.map(o => o.ticketNumber).filter(Boolean));
  let n;
  do { n = Math.floor(Math.random() * 999) + 1; } while (active.has(n));
  return n;
};

let _kdsInterval = null;

const _ensureKDSTick = function () {
  if (_kdsInterval) return;
  _kdsInterval = setInterval(_tickKDS, 1000);
};

const _stopKDSTick = function () {
  clearInterval(_kdsInterval);
  _kdsInterval = null;
};

const _tickKDS = function () {
  if (modelState.orderQueue.length === 0) { _stopKDSTick(); return; }
  const now = Date.now();
  const { kdsYellowThreshold, kdsRedThreshold, kdsAutoCompleteThreshold } = model.state.settings;
  const autoThreshold = kdsAutoCompleteThreshold > 0 ? kdsAutoCompleteThreshold : Infinity;
  const expired = modelState.orderQueue.filter(
    o => Math.floor((now - o.startedAt) / 1000) >= autoThreshold
  );
  expired.forEach(o => controlMarkOrderDone(o.id, true));
  if (expired.length === 0) {
    KDSView.updateTimers(modelState.orderQueue, now, kdsYellowThreshold, kdsRedThreshold);
  }
};

// ── Undo "done" window ────────────────────────────────────────────────────────
const UNDO_WINDOW_MS = 10_000;
const _pendingDone = new Map(); // id → { timer, order, idx, timedOut, dismissToast }


const _undoMarkDone = function (id) {
  const pending = _pendingDone.get(id);
  if (!pending) return;
  clearTimeout(pending.timer);
  pending.dismissToast();
  _pendingDone.delete(id);
  const insertIdx = Math.min(pending.idx, modelState.orderQueue.length);
  modelState.orderQueue.splice(insertIdx, 0, pending.order);
  KDSView.renderQueue(modelState.orderQueue);
  _ensureKDSTick();
  channel.postMessage({
    type: MSG.KDS_QUEUE_SYNC,
    queue: modelState.orderQueue,
    thresholds: {
      yellow: model.state.settings.kdsYellowThreshold,
      red: model.state.settings.kdsRedThreshold,
    },
  });
};

const controlMarkOrderDone = function (id, timedOut = false) {
  const idx = modelState.orderQueue.findIndex(o => o.id === id);
  if (idx === -1 || _pendingDone.has(id)) return;
  const order = modelState.orderQueue[idx];

  modelState.orderQueue.splice(idx, 1);
  KDSView.renderQueue(modelState.orderQueue);
  if (modelState.orderQueue.length === 0) _stopKDSTick();
  channel.postMessage({
    type: MSG.KDS_QUEUE_SYNC,
    queue: modelState.orderQueue,
    thresholds: {
      yellow: model.state.settings.kdsYellowThreshold,
      red: model.state.settings.kdsRedThreshold,
    },
  });

  const dismissToast = KDSView.showUndoToast(order, () => _undoMarkDone(id), UNDO_WINDOW_MS);
  const timer = setTimeout(async () => {
    _pendingDone.delete(id);
    dismissToast();
    try {
      await model.recordServeTime(id, timedOut);
      _loadServingComparison();
    } catch (_) {}
  }, UNDO_WINDOW_MS);

  _pendingDone.set(id, { timer, order, idx, timedOut, dismissToast });
};

// Marks an order Ready for Pickup - it stays in the queue (only Mark Done
// removes it), so this is a simple immediate flip, no undo window needed.
const controlMarkOrderReady = function (id) {
  const order = modelState.orderQueue.find(o => o.id === id);
  if (!order || order.readyAt) return;
  order.readyAt = Date.now();
  KDSView.renderQueue(modelState.orderQueue);
  channel.postMessage({ type: MSG.KDS_ORDER_READY, id, readyAt: order.readyAt });
  model.markOrderReady(id).catch(() => {});
};

// Cashier-side revert for a mistaken Ready: sends the order back to Preparing
// on every screen (readyAt: null rides the same KDS_ORDER_READY message).
const controlUnmarkOrderReady = function (id) {
  const order = modelState.orderQueue.find(o => o.id === id);
  if (!order || !order.readyAt) return;
  order.readyAt = null;
  KDSView.renderQueue(modelState.orderQueue);
  channel.postMessage({ type: MSG.KDS_ORDER_READY, id, readyAt: null });
  model.unmarkOrderReady(id).catch(() => {});
};

const controlOpenKDSWindow = function () {
  const { width, height } = model.state.settings.kdsWindowSize;
  const win = window.open('kds-display.html', 'pointbunny-kds', `width=${width},height=${height},menubar=no,toolbar=no,location=no`);
  if (!win) showToast('Popup was blocked. Allow popups for this site to open the Queue Display.', 'error');
  else win.focus();
};

const controlOpenCFDWindow = function () {
  const { width, height } = model.state.settings.cfdWindowSize;
  const win = window.open('customer-display.html', 'pointbunny-cfd', `width=${width},height=${height},menubar=no,toolbar=no,location=no`);
  if (!win) showToast('Popup was blocked. Allow popups for this site to open the Customer Display.', 'error');
  else win.focus();
};

const controlOpenPickupWindow = function () {
  // Customer-facing pickup board - meant for a big screen, so no per-size
  // setting yet; it lays out fluidly at any window size.
  const win = window.open('pickup-display.html', 'pointbunny-pickup', 'width=1100,height=700,menubar=no,toolbar=no,location=no');
  if (!win) showToast('Popup was blocked. Allow popups for this site to open the Pickup Screen.', 'error');
  else win.focus();
};

// This POS device's terminal number (per-device, persisted). Stamped on every CFD
// broadcast so a customer display set to the same number renders only this terminal.
const _terminalNumber = () => localStorage.getItem('pointbunny_terminal_number') || '1';

// Sizes the terminal grid to the team (one terminal per person: owner + staff).
// Clamps a stale saved value down if the team shrank. Shared shape with the CFD.
const _populateTerminalOptions = async function (gridEl, valueEl) {
  let count = 1;
  try { count = await model.getActiveHeadcount(); } catch { /* default to 1 */ }
  let saved = Number(_terminalNumber()) || 1;
  if (saved > count) { saved = count; localStorage.setItem('pointbunny_terminal_number', String(count)); }
  gridEl.innerHTML = Array.from({ length: count }, (_, i) => {
    const n = i + 1;
    return `<button class="nav-terminal-chip${n === saved ? ' nav-terminal-chip--active' : ''}" type="button" data-value="${n}">${n}</button>`;
  }).join('');
  valueEl.textContent = String(saved);
};

const _broadcastCart = function () {
  const cart = modelState.cart;
  channel.postMessage({
    type: MSG.CFD_CART_UPDATE,
    terminalNumber: _terminalNumber(),
    cart,
    total: cart.reduce((s, i) => s + i.totalPrice, 0),
  });
};

// Selecting QR at checkout flashes the merchant QR full-screen on the customer
// display so the customer can scan it; switching back to Cash/Card hides it and
// reveals the cart again. Stamped with the terminal so only the paired CFD reacts.
const controlSetPaymentMethod = function (method) {
  channel.postMessage({
    type: MSG.CFD_SHOW_QR,
    terminalNumber: _terminalNumber(),
    show: method === 'qr',
    total: OrderCheckOutView._totalPrice ?? 0,
    // Send the QR image URL so a display on ANOTHER device (e.g. an iPad) can load
    // it - localStorage is per-device, so the display can't rely on its own copy.
    qrUrl: localStorage.getItem('pointbunny_cfd_qr') || null,
  });
};

// Force-hide the customer display's QR overlay (used when leaving the checkout/payment
// step by any path other than completing a QR sale - Back, or abandon/discard - so the
// QR doesn't linger on the display).
const _hideCFDQr = function () {
  channel.postMessage({ type: MSG.CFD_SHOW_QR, terminalNumber: _terminalNumber(), show: false });
};

const controlSaveKDSWindowSize = function (size) {
  model.state.settings.kdsWindowSize = size;
  localStorage.setItem('pointbunny_kds_window_size', JSON.stringify(size));
};

const controlSaveCFDWindowSize = function (size) {
  model.state.settings.cfdWindowSize = size;
  localStorage.setItem('pointbunny_cfd_window_size', JSON.stringify(size));
};

// CFD ad / Pickup ad / payment QR: the file uploads right away (needed to
// preview it), but the resulting URL is only STAGED - same deferred-save flow
// as every other Displays/POS setting - so it's not live until Save Changes.
const controlUploadCFDAd = async function (file) {
  try {
    const url = await model.uploadCFDAdImage(file);
    _stageSetting('cfdAdUrl', url);
    SettingsView.showCFDAdPreview(url);
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlRemoveCFDAd = function () {
  _stageSetting('cfdAdUrl', null);
  SettingsView.showCFDAdPreview(null);
};

const controlUploadPickupAd = async function (file) {
  try {
    const url = await model.uploadPickupAdImage(file);
    _stageSetting('pickupAdUrl', url);
    SettingsView.showPickupAdPreview(url);
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlRemovePickupAd = function () {
  _stageSetting('pickupAdUrl', null);
  SettingsView.showPickupAdPreview(null);
};

const controlUploadCFDQR = async function (file) {
  try {
    const url = await model.uploadCFDQRImage(file);
    _stageSetting('cfdQrUrl', url);
    SettingsView.showCFDQRPreview(url);
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlRemoveCFDQR = function () {
  _stageSetting('cfdQrUrl', null);
  SettingsView.showCFDQRPreview(null);
};

// ── Deferred settings draft (POS toggles + KDS thresholds) ────────────────────
// These have no Edit gate - any change stages immediately (state + local UI
// update right away, so the app behaves live) but the actual Supabase write
// waits for the shared footer's Save Changes, same as Profile/Business.
let _settingsDraftOriginal = null; // snapshot taken on the first unsaved change

const _stageSetting = function (key, value) {
  if (!_settingsDraftOriginal) _settingsDraftOriginal = model.snapshotBusinessSettings();
  model.stageBusinessSetting(key, value);
  SettingsView.showSettingsDirty();
};

const controlSaveSettingsDraft = async function () {
  // Paper width has no server component - it's the one staged "setting" that
  // doesn't live in model.snapshotBusinessSettings()/persistBusinessSettings().
  if (_pendingPaperWidth != null) {
    btPrinter.setPaperWidth(_pendingPaperWidth);
    _pendingPaperWidth = null;
  }
  await model.persistBusinessSettings();
  _settingsDraftOriginal = null;
  SettingsView.hideSettingsDirty();
  showToast('Settings saved', 'success');
};

const controlDiscardSettingsDraft = function () {
  if (_settingsDraftOriginal) {
    model.restoreBusinessSettings(_settingsDraftOriginal);
    _applyHomeStatsVisibility();
  }
  _settingsDraftOriginal = null;
  _pendingPaperWidth = null;
  SettingsView.hideSettingsDirty();
  _syncAllSettingsToggles();
  SettingsView.syncBtPaperWidth(btPrinter.paperWidth());
  SettingsView.syncDisplaySizes(model.state.settings.kdsWindowSize, model.state.settings.cfdWindowSize);
};

const controlSaveKDSThresholds = function ({ yellow, red, auto }) {
  _stageSetting('kdsYellowThreshold', yellow);
  _stageSetting('kdsRedThreshold', red);
  _stageSetting('kdsAutoCompleteThreshold', auto);
};

// ── Sale finalisation ─────────────────────────────────────────────────────────

// Records the sale via the server-side validating RPC (record_sale) and runs
// every post-sale side effect (queue, broadcasts, cart clear, counters). Item
// prices, promo code redemption, and loyalty point affordability are all
// recomputed/enforced INSIDE the RPC from real DB rows - see model.recordSale.
// The DB trigger still assigns the gapless per-business invoice number on
// insert; we read it back onto `sale.receiptNumber` so the receipt can be
// printed WITH the number afterwards. Does not print or show the success
// screen - the caller handles those once the number exists.
const _recordSale = async function (sale) {
  modelState.salesBasket.push(sale);
  const { adjustmentTemplates, manualAdjustments, loyaltyRewards } =
    _splitAdjustmentsForRpc(model.state.currentReceiptAdjustments);

  let result;
  try {
    result = await model.recordSale({
      items: _toRpcItems(modelState.cart),
      adjustmentTemplates,
      manualAdjustments,
      promoCode: sale.promoCode?.code ?? null,
      loyaltyRedeemIncrements: model.state.currentLoyaltyRedemption?.increments ?? 0,
      loyaltyRewards,
      customerId: sale.customerId,
      orderType: sale.orderType,
      paymentMethod: sale.paymentMethod,
      ticketNumber: sale.ticketNumber,
      customerPayment: sale.customerPayment,
      customerChange: sale.customerChange,
      cashierName: sale.cashierName,
      loggedInCashier: sale.loggedInCashier,
      date: sale.date,
    });
  } catch (recordError) {
    if (recordError.code === '22003' || recordError.message?.includes('numeric field overflow')) {
      throw new Error("That number doesn't look right. Check the payment amount and try again.");
    }
    // Also covers RPC-raised messages ("Invalid promo code", "This promo code
    // has reached its usage limit", "Not enough loyalty points for this
    // redemption", "Item no longer available", "Invalid variant...", etc.).
    // Nothing was committed, so the cart and checkout adjustments are
    // untouched - the cashier can fix the issue and hit Confirm again.
    throw recordError;
  }

  // Reconcile the local `sale` object with server-authoritative numbers -
  // everything printed/queued/broadcast from here on uses what was actually
  // stored, never the pre-commit client guess.
  sale.id               = result.id;
  sale.receiptNumber    = result.receiptNumber;
  sale.subtotal         = result.subtotal;
  sale.totalPrice       = result.totalPrice;
  sale.items            = result.items;
  sale.adjustments      = result.adjustments;
  sale.taxInclusiveRate = result.taxInclusiveRate;

  // Keep the Promo panel's live usage count in sync without a refetch (the
  // RPC already redeemed it atomically server-side).
  if (sale.promoCode) {
    const dc = model.state.discountCodes.find((d) => d.id === sale.promoCode.discountCodeId);
    if (dc) dc.usageCount += 1;
  }

  // Inventory consumption: unchanged mechanism, still fire-and-forget (a
  // stock failure must NEVER fail or delay the sale, same principle as
  // printing) - now computed from the SERVER-CORRECTED items for fidelity.
  // Skipped entirely while inventory tracking is off (master toggle).
  const invLines = model.state.settings.inventoryEnabled === false ? [] : model.computeConsumption(sale.items);
  if (invLines.length) {
    model.applyConsumptionLocally(invLines);
    NewOrderView.updateStockBadges(model.productStockStatuses());
    model.applyInventoryConsumption(sale.id, invLines, sale.cashierName)
      .then(() => channel.postMessage({ type: MSG.INVENTORY_SYNC }))
      .catch(() => {}); // sale already committed; drift is corrected manually
  }

  // NOTE: promo code redemption and loyalty earn/redeem are now validated and
  // applied INSIDE record_sale (atomically, pre-commit) - the separate
  // model.redeemDiscountCode / model.recordLoyaltyForSale calls that used to
  // live here are gone; loyalty balance changes reflect via the usual
  // realtime/refetch paths, same as any other server-side ledger write.
  modelState.orderQueue.push({
    id: sale.id,
    saleDate: new Date(sale.date).toISOString(),
    items: sale.items,
    startedAt: sale.date,
    totalPrice: sale.totalPrice,
    orderType: sale.orderType ?? 'dine-in',
    ticketNumber: sale.ticketNumber ?? null,
    receiptNumber: sale.receiptNumber,
    readyAt: null,
  });
  _ensureKDSTick();
  KDSView.renderQueue(modelState.orderQueue);
  channel.postMessage({
    type: MSG.KDS_QUEUE_SYNC,
    queue: modelState.orderQueue,
    thresholds: {
      yellow: model.state.settings.kdsYellowThreshold,
      red: model.state.settings.kdsRedThreshold,
    },
  });
  clearCart();
  channel.postMessage({ type: MSG.CFD_SALE_COMPLETE, terminalNumber: _terminalNumber() });
  model.clearReceiptAdjustments();
  _clearCashflowCache(); // new sale invalidates all cached periods
  // NOTE: the Today's Sales refresh is intentionally NOT called here. It is
  // fired from _showSaleSuccess instead, immediately before the success overlay
  // is shown, so its async DB await resolves with the overlay present and the
  // count-up animation + "vs yesterday" badge land when the dashboard is
  // revealed. Calling it here (record-then-print runs this well before the
  // overlay exists) made the animation play invisibly behind the checkout modal.
  // Bump the in-memory count now; the animation is enqueued from _showSaleSuccess
  // (after the sales card) so the two cards reveal one at a time, Sales first.
  if (_todayTransactionCount !== null) _todayTransactionCount++;
};

// Shows the animated success screen and auto-closes the checkout modal. The
// success chime plays here (not at record time) so it lands with the modal -
// checkout is now record-then-print, so _recordSale fires the moment Print is
// clicked, well before this.
const _showSaleSuccess = function (note = null) {
  KDSView.playNewOrderSound();
  // Start the dashboard refresh BEFORE showing the overlay. refreshTodaySalesDisplay
  // is async and yields at its DB await, so the overlay below is created first;
  // when the await resolves it sees the overlay and defers the count-up animation
  // (and the % roll) until the overlay closes and the dashboard is revealed.
  // Sales first, then Transactions - serialized so only one card moves at a time.
  refreshTodaySalesDisplay();
  _updateTransactionBadge();
  _gsHasSale = true; // "Make your first sale" step
  _updateGettingStarted(); // also refreshes the activity feed once retired
  OrderCheckOutView._showSuccess(note);
  OrderCheckOutView.closeRedeemModal(); // top-level modal, outside .modal-parent - doesn't auto-clear
  const successOverlay = document.querySelector('.success-overlay');
  const autoCloseTimer = setTimeout(() => {
    OrderCheckOutView._hideModal();
    successOverlay?.remove();
  }, 3500);
  const successCloseBtn = successOverlay?.querySelector('.modal-close');
  if (successCloseBtn) {
    successCloseBtn.addEventListener('click', () => {
      clearTimeout(autoCloseTimer);
      OrderCheckOutView._hideModal();
      successOverlay.remove();
    });
  }
};

// Streams the receipt to the connected Bluetooth thermal printer as ESC/POS
// bytes. A failure is surfaced as a toast but never throws - the sale is
// already recorded and can be reprinted from Sales history.
const _printToBluetooth = async function (sale) {
  try {
    const bytes = encodeReceipt(sale, { width: btPrinter.paperWidth() });
    await btPrinter.print(bytes);
  } catch (err) {
    showToast('Bluetooth print failed: ' + (err.message ?? err) + ' Reprint from Sales history if needed.', 'error');
  }
};

// Prints the receipt. Routes to the Bluetooth thermal printer when one is
// connected, otherwise to the normal HTML print. Returns a truthy promise, or
// null only when the HTML popup path is blocked. Paper width is ONE setting
// shared by both paths - a business has one physical printer/paper width
// regardless of which mechanism ends up sending it the bytes.
const _printReceipt = function (sale) {
  if (btPrinter.isConnected()) {
    return _printToBluetooth(sale);
  }
  return ReceiptView.print(sale, { width: btPrinter.paperWidth() });
};

// "Did it print?" confirmation. The sale is ALREADY recorded by this point, so a
// print failure never undoes it - the only action offered is a reprint (which
// reuses the same invoice number and does not create a duplicate sale). A true
// cancellation is a separate Void from Sales history.
const _askPrintConfirm = function (sale) {
  OrderCheckOutView.showConfirmModal({
    message: 'Did the receipt print successfully?',
    note: 'The sale is already recorded. If it did not print, tap Reprint - this will not duplicate the sale. You can turn this prompt off in Settings.',
    confirmLabel: 'Yes, Done',
    cancelLabel: 'No, Reprint',
    onConfirm: () => { _showSaleSuccess(); },
    onCancel: () => {
      const ok = _printReceipt(sale);
      if (!ok) showToast('Popup was blocked. Allow popups for this site and try again.', 'error');
      _askPrintConfirm(sale);
    },
  });
};

// The actual commit: record (assigns the invoice number), print, then the optional
// "did it print?" check. Split out from the review step below so nothing is written
// to the DB until the cashier confirms.
const _finalizeTransaction = async function () {
  const printBtn = document.getElementById('printReceiptBtn');
  if (printBtn) printBtn.disabled = true;
  try {
    if (modelState.cart.length <= 0) throw 'Cart is empty!';
    const sale = _buildSale();
    sale.ticketNumber = _generateTicketNumber();

    // 1. Record first so the sale gets its gapless invoice number, which we then
    //    print on the receipt. Once this succeeds the sale is committed; the
    //    charge button stays disabled so it can't be re-submitted.
    await _recordSale(sale);

    // 2. Printing turned off - just confirm the sale saved.
    if (!model.state.settings.printingEnabled) {
      _showSaleSuccess('Transaction saved. No receipt was printed. You can enable printing in Settings.');
      return;
    }

    // 3. Print the receipt (now carrying the invoice number). A blocked popup is
    //    not a sale failure - the sale is already safely recorded.
    const printPromise = _printReceipt(sale);
    if (!printPromise) {
      showToast('Sale recorded, but the receipt popup was blocked. Allow popups, then reprint from Sales history.', 'error');
      _showSaleSuccess('Sale recorded. The receipt could not open - allow popups, then reprint it from Sales history.');
      return;
    }

    await printPromise;

    // 4. Optional reprint check (never undoes the sale).
    if (model.state.settings.confirmPrint) {
      _askPrintConfirm(sale);
    } else {
      _showSaleSuccess();
    }
  } catch (err) {
    showToast(err.message ?? err);
    if (printBtn) printBtn.disabled = false;
  }
};

// Pre-commit review. Clicking Print/Record shows a preview of the exact receipt that
// will print so the cashier can go back and change the order BEFORE anything is
// recorded. "Go Back" just closes it (nothing is written); "Confirm" runs the commit.
// The preview sale has no invoice/ticket number yet - those are assigned on record.
const controlConcludeTransaction = function () {
  if (modelState.cart.length <= 0) { showToast('Cart is empty!'); return; }

  const printing = model.state.settings.printingEnabled;
  const previewSale = _buildSale();
  const html = ReceiptView.previewMarkup(previewSale, { width: btPrinter.paperWidth() });

  // Reassure the cashier that the numbers fill in on confirm - but only mention the
  // ones actually enabled, so a business that hides invoice numbers never sees them
  // referenced here.
  const showInv = previewSale.showInvoiceNumber;
  const showTkt = previewSale.showTicketNumber;
  let note = null;
  if (showInv && showTkt) note = 'Your invoice and ticket numbers are filled in automatically once you confirm.';
  else if (showInv)       note = 'Your invoice number is filled in automatically once you confirm.';
  else if (showTkt)       note = 'Your ticket number is filled in automatically once you confirm.';

  OrderCheckOutView.showReceiptPreview({
    html,
    confirmLabel: printing ? 'Confirm & Print' : 'Confirm & Record',
    note,
    onConfirm: () => { _finalizeTransaction(); },
  });
};

const clearCart = function () {
  model.state.cart = [];
  document.querySelector('.confirm-overlay')?.remove();
};

const controlCloseOrderModal = function (close) {
  OrderCheckOutView.closeRedeemModal(); // top-level modal, outside .modal-parent - doesn't auto-clear
  if (model.state.cart.length === 0) { close(); return; }
  NewOrderView.showConfirmModal({
    message: 'You have items in your cart. Close and discard them?',
    // Broadcast the now-empty cart so the customer display clears too - otherwise
    // the abandoned items linger on the CFD until the next transaction. Also hide
    // the QR overlay in case QR was the selected method when the order was abandoned.
    onConfirm: () => { clearCart(); _broadcastCart(); _hideCFDQr(); close(); },
  });
};

// ── Settings ──────────────────────────────────────────────────────────────────

// ── Help & Support ────────────────────────────────────────────────────────────

const controlOpenSupport = async function () {
  SupportView.open();
  try {
    await model.loadTickets();
    SupportView.renderTicketList(model.state.tickets);
    SupportView.syncUnreadBadge(model.state.tickets);
  } catch (err) {
    showToast(err.message ?? 'Could not load tickets.');
  }
};

const controlOpenTicket = async function (ticketId) {
  const ticket = model.state.tickets.find(t => t.id === ticketId);
  if (!ticket) return;
  SupportView.setCurrentTicket(ticketId);
  try {
    const replies = await model.loadTicketReplies(ticketId);
    SupportView.renderThread(ticket, replies);
    if (ticket.has_unread_reply) {
      await model.markRepliesRead(ticketId);
      SupportView.syncUnreadBadge(model.state.tickets);
    }
  } catch (err) {
    showToast(err.message ?? 'Could not load ticket.');
  }
};

const controlMarkTicketSolved = async function (ticketId) {
  if (!ticketId) return;
  const confirmed = confirm(
    'Mark this ticket as solved?\n\nOnce solved it cannot be reopened. If the issue comes back you\'ll need to submit a new ticket.'
  );
  if (!confirmed) return;
  try {
    await model.markTicketSolved(ticketId);
    const ticket = model.state.tickets.find(t => t.id === ticketId);
    const replies = await model.loadTicketReplies(ticketId);
    SupportView.renderThread(ticket, replies);
    SupportView.renderTicketList(model.state.tickets);
  } catch (err) {
    showToast(err.message ?? 'Could not update ticket.');
  }
};

// One click on an emoji sends the rating right away (no Submit button). The UI
// flips to the thank-you optimistically; on failure it rolls back to the emojis.
const controlSubmitRating = async function () {
  const ticketId = SupportView._currentTicketId;
  const rating = SupportView._selectedRating;
  if (!ticketId || !rating) return;
  SupportView.showRatingThanks(rating);
  try {
    await model.submitTicketRating(ticketId, rating, '');
  } catch (err) {
    SupportView.revertRating();
    showToast(err.message ?? 'Could not submit rating.');
  }
};

// Optional follow-up: patches the comment onto the already-sent rating.
const controlSubmitRatingComment = async function () {
  const ticketId = SupportView._currentTicketId;
  const rating = SupportView._selectedRating;
  const comment = SupportView.getRatingComment();
  if (!ticketId || !rating || !comment) return;
  try {
    await model.submitTicketRating(ticketId, rating, comment);
    SupportView.showCommentSaved();
    showToast('Thanks for the extra detail!');
  } catch (err) {
    showToast(err.message ?? 'Could not save your comment.');
  }
};

const controlSendReply = async function () {
  const ticketId = SupportView._currentTicketId;
  const message = SupportView._getReplyText();
  if (!ticketId || !message) return;
  SupportView.setReplySending(true);
  try {
    await model.submitTicketReply(ticketId, message);
    SupportView.clearReplyInput();
    const ticket = model.state.tickets.find(t => t.id === ticketId);
    const replies = await model.loadTicketReplies(ticketId);
    SupportView.renderThread(ticket, replies);
  } catch (err) {
    showToast(err.message ?? 'Could not send reply.');
  } finally {
    SupportView.setReplySending(false);
  }
};

const controlSubmitTicket = async function () {
  const { category, subject, message, files } = SupportView._getTicketFormData();
  if (!category) { SupportView.showTicketResult(false, 'Please select a category.'); return; }
  if (!subject)  { SupportView.showTicketResult(false, 'Please enter a subject.'); return; }
  if (!message)  { SupportView.showTicketResult(false, 'Please enter a message.'); return; }
  // Size/type are enforced + auto-downscaled centrally in model.submitTicket
  // (via prepareImageForUpload), so no inline size cap is needed here.
  SupportView.setTicketSubmitting(true);
  try {
    await model.submitTicket({ category, subject, message, files });
    SupportView.resetTicketForm();
    await model.loadTickets();
    SupportView.renderTicketList(model.state.tickets);
    SupportView.syncUnreadBadge(model.state.tickets);
    SupportView.showSuccessPanel();
    setTimeout(() => SupportView.showListPanel(), 2500);
  } catch (err) {
    SupportView.showTicketResult(false, err.message ?? 'Something went wrong. Please try again.');
  } finally {
    SupportView.setTicketSubmitting(false);
  }
};

const controlGenerateTimeclockToken = async function () {
  const btn = document.getElementById('payrollGenTokenBtn');
  const prevText = btn?.textContent;
  if (btn) { btn.disabled = true; btn.textContent = 'Generating…'; }
  try {
    const token = await model.generateTimeclockToken();
    TimesheetView.showTimeclockToken(token);
  } catch (err) {
    showToast('Failed to generate code', 'error');
    if (btn) btn.textContent = prevText ?? 'Generate Code';
  } finally {
    if (btn) btn.disabled = false;
  }
};

// Business Details changes are compliance-sensitive (address/tax jurisdiction),
// so those still require email OTP verification. Profile (name) changes don't -
// see controlSaveProfile below.
const controlSaveBusinessInfo = async function (data) {
  _settingsOTPData = data;
  const btn = document.getElementById('saveBusinessInfoBtn');
  if (btn) { btn.disabled = true; btn.textContent = 'Sending code…'; }
  try {
    const captchaToken = await getTokenInteractive();
    _settingsOTPEmail = await model.sendSettingsVerification(captchaToken);
    SettingsView.showOTPModal(_settingsOTPEmail);
  } catch (err) {
    SettingsView.showBusinessSaveStatus(false, err.message ?? 'Failed to send verification code');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Save Changes'; }
  }
};

const controlSaveProfile = async function ({ firstName, lastName }) {
  const btn = document.getElementById('saveProfileBtn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    await model.saveProfileInfo({ firstName, lastName });
    _updateCashierDisplay();
    SettingsView.exitProfileEditMode(false);
    showToast('Profile updated', 'success');
  } catch (err) {
    SettingsView.showProfileSaveStatus(false, err.message ?? 'Failed to save profile');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Save Changes'; }
  }
};

const controlEditProfile   = () => SettingsView.enterProfileEditMode();
const controlCancelProfile = () => SettingsView.exitProfileEditMode(true);
const controlEditBusiness   = () => SettingsView.enterBusinessEditMode();
const controlCancelBusiness = () => SettingsView.exitBusinessEditMode(true);

// Only Business Details routes through OTP now (see controlSaveBusinessInfo /
// controlSaveProfile above), so this always finalizes a business save.
const controlVerifySettingsOTP = async function (token) {
  try {
    await model.confirmSettingsVerification(_settingsOTPEmail, token);
    const { name, email, phone, timezone, address, city, state: stateVal, zip } = _settingsOTPData;
    const prevProvince = model.state.businessAddressProvince ?? '';
    const country      = model.state.businessAddressCountry;
    await model.saveBusinessInfo({ name, email, phone, timezone, addressStreet: address, addressCity: city, addressProvince: stateVal, addressZip: zip });
    SettingsView.hideOTPModal();
    SettingsView.exitBusinessEditMode(false);
    showToast('Business info saved', 'success');
    // If the province/state changed, remind the owner to review their tax rate.
    const changed = (stateVal ?? '').trim().toLowerCase() !== prevProvince.trim().toLowerCase();
    if (changed && (stateVal ?? '').trim()) {
      const suggestion = getTaxSuggestion({ country, province: stateVal });
      const msg = suggestion
        ? `Your business location changed. The typical rate for ${suggestion.label} is ${suggestion.rate}% - review your tax adjustment in Settings > Adjustments.`
        : 'Your business location changed. Your tax rate may be different now - review it in Settings > Adjustments.';
      SettingsView.showTaxReminder(msg);
    }
    _settingsOTPEmail = null;
    _settingsOTPData  = null;
  } catch (err) {
    SettingsView.showOTPModalError(err.message ?? 'Verification failed. Try again.');
  }
};

const controlCancelSettingsOTP = function () {
  SettingsView.hideOTPModal();
  _settingsOTPEmail = null;
  _settingsOTPData  = null;
};

// Re-populates every POS/KDS control from state.settings - used on open and
// after discarding a settings draft.
const _syncAllSettingsToggles = function () {
  SettingsView.syncPrintingToggle(model.state.settings.printingEnabled);
  SettingsView.syncConfirmPrintToggle(model.state.settings.confirmPrint);
  SettingsView.syncShowCashierToggle(model.state.settings.showCashierName);
  SettingsView.syncHideStatsToggle(model.state.settings.hideStatsFromStaff);
  SettingsView.syncOrderTypeToggle(model.state.settings.orderTypeEnabled);
  SettingsView.syncInvoiceNumberToggle(model.state.settings.showInvoiceNumber);
  SettingsView.syncTicketNumberToggle(model.state.settings.showTicketNumber);
  SettingsView.syncKDSThresholds(
    model.state.settings.kdsYellowThreshold,
    model.state.settings.kdsRedThreshold,
    model.state.settings.kdsAutoCompleteThreshold,
  );
};

const controlOpenSettings = function () {
  _settingsOTPEmail    = null;
  _settingsOTPData     = null;
  _settingsDraftOriginal = null;
  _pendingPaperWidth = null;
  SettingsView.openWithRole(model.state.role);
  SettingsView.syncProfileInfo({
    firstName: model.state.currentStaff?.firstName ?? '',
    lastName:  model.state.currentStaff?.lastName  ?? '',
    email:     model.state.currentStaff?.email ?? model.state.businessEmail ?? '',
  });
  if (model.hasPermission('manage_business')) {
    const isOwner = model.state.userId === model.state.businessId;
    SettingsView.syncBusinessInfo({
      name:     model.state.businessName            ?? '',
      email:    model.state.businessEmail           ?? model.state.currentStaff?.email ?? '',
      phone:    model.state.businessPhone           ?? '',
      timezone: model.state.businessTimezone        ?? null,
      address:  model.state.businessAddressStreet   ?? '',
      city:     model.state.businessAddressCity     ?? '',
      state:    model.state.businessAddressProvince ?? '',
      zip:      model.state.businessAddressZip      ?? '',
      country:  model.state.businessAddressCountry  ?? '',
      isOwner,
    });
  }
  SettingsView.syncShowRemovedToggle(model.state.settings.showRemovedAdjustments);
  _syncAllSettingsToggles();
  SettingsView.syncBtPaperWidth(btPrinter.paperWidth());
  _syncBtPrinterUI();
  // Best-effort: get a previously-paired printer ready before Test print is clicked.
  btPrinter.reconnect().catch(() => {});
  SettingsView.syncDisplaySizes(
    model.state.settings.kdsWindowSize,
    model.state.settings.cfdWindowSize,
  );
  SettingsView.render2FA(false); // optimistic default; corrected by the async check
  _refresh2FA();
};

const controlTogglePrinting = function (value) {
  _stageSetting('printingEnabled', value);
};

const controlToggleConfirmPrint = function (value) {
  _stageSetting('confirmPrint', value);
};

const controlToggleShowCashier = function (value) {
  _stageSetting('showCashierName', value);
};

// Hide home sales figures from staff without report access. The setting is
// business-level; who it hides FROM is permission-based (view_reports holders
// - incl. the owner - always see their numbers).
const _applyHomeStatsVisibility = function () {
  const hide = model.state.settings.hideStatsFromStaff === true && !model.hasPermission('view_reports');
  document.body.classList.toggle('hide-home-stats', hide);
};

const controlToggleHideStats = function (value) {
  _stageSetting('hideStatsFromStaff', value);
  _applyHomeStatsVisibility();
};

const controlToggleOrderType = function (value) {
  _stageSetting('orderTypeEnabled', value);
};

const controlToggleShowInvoiceNumber = function (value) {
  _stageSetting('showInvoiceNumber', value);
};

const controlToggleShowTicketNumber = function (value) {
  _stageSetting('showTicketNumber', value);
};

const _hasTaxLikeFeeAdjustment = function () {
  return (model.state.settings.adjustments || []).some(
    (a) => a.type === 'fee' && /tax|vat|gst/i.test(a.name || ''),
  );
};

// ── Tax Settings draft (mode + rate + name) ────────────────────────────────
// Same deferred-draft pattern as the Settings modal's POS/KDS toggles
// (CLAUDE.md "Deferred settings draft"): a change applies to state + local
// UI immediately (so the app keeps behaving live) but the Supabase write
// waits for the sticky footer's Save Tax Settings. Automatic Receipt
// Adjustments and the Tax-Exempt picker are NOT part of this draft - they
// keep their own existing save flows (instant per-item / own modal Save).
let _taxSettingsDraftOriginal = null; // snapshot taken on the first unsaved change

const _stageTaxSetting = function (key, value) {
  if (!_taxSettingsDraftOriginal) _taxSettingsDraftOriginal = model.snapshotBusinessSettings();
  model.stageBusinessSetting(key, value);
  DiscountView.showTaxSettingsDirty();
};

// taxMode ('none' | 'exclusive' | 'inclusive') is the source of truth; taxInclusive
// is kept in sync (== taxMode === 'inclusive') so every existing consumer that
// already checks the boolean (receiptView, escpos, cashflowView, orderCheckoutView,
// menuEditView, discountView) keeps working unchanged.
const controlSetTaxMode = function (mode) {
  _stageTaxSetting('taxMode', mode);
  _stageTaxSetting('taxInclusive', mode === 'inclusive');
  // Double-count / contradiction guards - both just warn, never block.
  if (mode === 'inclusive' && _hasTaxLikeFeeAdjustment()) {
    showToast('Heads up: you also have a tax added on top below. Remove it so tax is not counted twice.', 'error');
  } else if (mode === 'none' && _hasTaxLikeFeeAdjustment()) {
    showToast("Heads up: you have a tax-like fee added below, but Tax Settings says you don't charge tax. Remove it or switch modes so it isn't applied by mistake.", 'error');
  }
};

const controlSetTaxRate = function (value) {
  // Backstop for the view's change-event validation: never stage a nonsense rate.
  const val = Number(value);
  if (!Number.isFinite(val) || val <= 0 || val > 50) return;
  _stageTaxSetting('taxInclusiveRate', Math.round(val * 100) / 100);
};

const controlSetTaxExemptLabel = function (value) {
  _stageTaxSetting('taxExemptLabel', value);
};

const controlSetTaxLabel = function (value) {
  _stageTaxSetting('taxInclusiveLabel', value);
};

const controlSaveTaxSettingsDraft = async function () {
  await model.persistBusinessSettings();
  _taxSettingsDraftOriginal = null;
  DiscountView.hideTaxSettingsDirty();
  showToast('Tax settings saved', 'success');
};

const controlDiscardTaxSettingsDraft = function () {
  if (_taxSettingsDraftOriginal) model.restoreBusinessSettings(_taxSettingsDraftOriginal);
  _taxSettingsDraftOriginal = null;
  DiscountView.hideTaxSettingsDirty();
  DiscountView.syncTaxMode(model.state.settings.taxMode, model.state.settings.taxInclusiveRate, model.state.settings.taxInclusiveLabel, model.state.settings.taxExemptLabel);
};

// ── Bluetooth thermal printer ─────────────────────────────────────────────────
// No live "connected" status is tracked in the UI, and no manual "Connect" flow
// either - the printer was already granted Web Bluetooth permission once, so
// print()/reconnect() re-establish the connection on their own from then on.
// Test/Disconnect just act and report success or failure via toast.
const _syncBtPrinterUI = function () {
  SettingsView.syncBtPrinter({ supported: btPrinter.isSupported() });
};

const controlDisconnectBtPrinter = async function () {
  await btPrinter.disconnect();
  showToast('Bluetooth printer disconnected.', 'success');
};

// Paper width is device-level (localStorage, no server component) but still
// goes through the shared footer's Save/Discard like everything else in
// Settings - the click only stages the value + moves the toggle; the actual
// write happens in controlSaveSettingsDraft.
let _pendingPaperWidth = null;

const controlStagePaperWidth = function (value) {
  _pendingPaperWidth = Number(value) === 58 ? 58 : 80;
  SettingsView.showSettingsDirty();
};

const controlTestBtPrint = async function () {
  const sample = {
    date: new Date().toISOString(),
    storeName: model.state.businessName ?? model.state.username ?? 'Pointbunny',
    items: [{ itemName: 'Test Item', quantity: 1, totalPrice: 1, selectedVariants: [] }],
    subtotal: 1,
    totalPrice: 1,
    paymentMethod: 'cash',
    customerPayment: 1,
    customerChange: 0,
    adjustments: [],
    showInvoiceNumber: false,
    showTicketNumber: false,
    showCashier: false,
    addressCountry: model.state.businessAddressCountry ?? null,
  };
  try {
    // Test print previews the CURRENTLY SELECTED width, staged or not - it
    // would otherwise silently print the old saved width until Save Changes
    // is clicked, which looked like "the setting does nothing" during testing.
    const width = _pendingPaperWidth ?? btPrinter.paperWidth();
    await btPrinter.print(encodeReceipt(sample, { width }));
    showToast('Test sent to printer.', 'success');
  } catch (err) {
    showToast('Test print failed: ' + (err.message ?? err), 'error');
  }
};

const _refreshCategoryDropdowns = function () {
  NewMenuItemView._mapMenuCategoriesMarkUp(model.state.menuCategories);
  if (document.querySelector(".edit-field-select"))
    MenuEditView._mapMenuCategoriesMarkUp(model.state.menuCategories, "");
};

const controlAddCategoryFromSettings = async function (name) {
  try {
    await model.addCategory(name);
    // Only update the chips strip — no full re-render, no flash
    MenuListView._updateChips(model.state.menuCategories, name);
    _refreshCategoryDropdowns();
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlDeleteCategory = async function (name) {
  const itemsInCategory = model.state.menuItems.filter(
    (item) => item.category === name,
  );
  if (
    itemsInCategory.length > 0 &&
    !confirm(
      `"${name}" has ${itemsInCategory.length} menu item(s). Delete it anyway?`,
    )
  )
    return;
  try {
    await model.deleteCategory(name);
    // Soft in-place refresh: chips + item groups update with a content slide
    // instead of the whole modal replaying its entrance animation.
    MenuListView.refresh(model.state);
    _refreshCategoryDropdowns();
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const _renderAllAdjustments = function () {
  DiscountView.renderAdjustments(model.state.settings.adjustments);
};

const controlSaveAdjustment = async function (data) {
  if (!model.hasPermission('manage_adjustments')) { showToast("You don't have access to manage adjustments.", 'error'); return; }
  try {
    if (data.id) {
      await model.updateAdjustment(data.id, data);
    } else {
      await model.addAdjustment(data);
    }
    _renderAllAdjustments();
  } catch (err) {
    showToast(err.message ?? err);
  }
};

// Edit triggered from the Receipt Adjustments panel
const controlEditAdjFromPanel = function (id) {
  const adj = model.state.settings.adjustments.find((a) => a.id === id);
  if (!adj) return;
  DiscountView.showAdjustmentForm(adj);
};

const controlDeleteAdjustment = async function (id) {
  if (!model.hasPermission('manage_adjustments')) { showToast("You don't have access to manage adjustments.", 'error'); return; }
  try {
    await model.deleteAdjustment(id);
    _renderAllAdjustments();
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlToggleAdjustment = async function (id) {
  if (!model.hasPermission('manage_adjustments')) { showToast("You don't have access to manage adjustments.", 'error'); return; }
  try {
    await model.toggleAdjustment(id);
    _renderAllAdjustments();
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlShowRemoved = function (value) {
  model.state.settings.showRemovedAdjustments = value;
};

// ── Per-receipt adjustment controls ───────────────────────────────────────────

// Context the checkout view needs to decide which cart lines show a "Redeem
// free" pill and in what state - rebuilt fresh on every refresh since
// OrderCheckOutView._data is only re-set on a full render(), not on the
// in-place refreshes used throughout checkout.
const _loyaltyCartCtx = function () {
  const rewardsByItemId = new Map(model.state.loyaltyRewards.map((r) => [r.itemId, r]));
  const activeRewardAdj = model.state.currentReceiptAdjustments
    .filter((a) => !a.removed && a.source === 'loyalty_reward');
  const redeemedLineIds = new Set(activeRewardAdj.map((a) => a.cartItemLineId));
  // rewardId -> cartItemLineId for the Redeem modal's Remove button (the
  // modal lists the whole catalog by reward, not by an existing cart line).
  const redemptionByRewardId = new Map(activeRewardAdj.map((a) => [a.rewardId, a.cartItemLineId]));
  return {
    rewardsByItemId,
    redeemedLineIds,
    redemptionByRewardId,
    customer: model.state.currentLoyaltyCustomer,
    canRedeem: model.state.settings.loyaltyEnabled && model.hasPermission('use_loyalty'),
    reservedPoints: model.loyaltyReservedPoints(),
  };
};

// Active free-item reward redemptions, for the checkout section's applied
// summary. `name` reuses the friendly string model.js already stamped on the
// adjustment ("Free Espresso (Reward)") so there's no second source of truth.
const _loyaltyRewardRedemptions = function () {
  return model.state.currentReceiptAdjustments
    .filter((a) => !a.removed && a.source === 'loyalty_reward')
    .map((a) => ({ cartItemLineId: a.cartItemLineId, name: a.name }));
};

const _refreshCheckoutAdj = function () {
  const adjResult = model.calculateAdjustments(
    OrderCheckOutView._subtotal,
    model.state.currentReceiptAdjustments,
  );
  OrderCheckOutView._refreshAdjustments(
    OrderCheckOutView._subtotal,
    model.state.currentReceiptAdjustments,
    adjResult,
    model.state.settings.showRemovedAdjustments,
    model.state.currentPromoCode,
    {
      customer: model.state.currentLoyaltyCustomer,
      redemption: model.state.currentLoyaltyRedemption,
      rewardRedemptions: _loyaltyRewardRedemptions(),
    },
  );
  OrderCheckOutView._refreshCartItems(model.state.cart, _loyaltyCartCtx());
  // Keep the customer display's scan-to-pay amount in sync when the total changes
  // while QR is the active method (otherwise it shows the pre-adjustment total).
  if (OrderCheckOutView._paymentMethod === 'qr') controlSetPaymentMethod('qr');
};

const _canAdjustReceipt = () => model.hasPermission('apply_receipt_adjustment');
const _denyReceiptAdj = () => showToast("You don't have access to adjust receipts.", 'error');
// Name of the manager/admin who authorized the current one-off adjustment via
// override (null when the cashier did it themselves). Consumed on save.
let _receiptAdjApprover = null;

// Reusable "manager override" prompt. Builds its own overlay (so it works on top of
// any modal), verifies the entered account holds `requiredCap` (or is the owner),
// and calls onApproved(approverName). Mirrors the void override.
const _promptManagerOverride = function ({ requiredCap, onApproved }) {
  const modal = document.createElement('div');
  modal.className = 'cashflow-override-overlay';
  modal.style.zIndex = '100000';
  modal.innerHTML = `
    <div class="cashflow-override-card">
      <h3 class="cashflow-override-title">Manager Override Required</h3>
      <p class="cashflow-override-desc">Enter a manager or admin account to authorize this action.</p>
      <form autocomplete="off">
        <div class="cashflow-override-field">
          <label>Email</label>
          <input type="email" data-ov="email" placeholder="manager@email.com" required />
        </div>
        <div class="cashflow-override-field">
          <label>Password</label>
          <input type="password" data-ov="password" placeholder="••••••••" required />
        </div>
        <p class="cashflow-override-error hidden" data-ov="error"></p>
        <div class="cashflow-override-actions">
          <button type="button" class="btn" data-ov="cancel">Cancel</button>
          <button type="submit" class="btn primary" data-ov="submit">Authorize</button>
        </div>
      </form>
    </div>`;
  document.body.appendChild(modal);
  const close = () => modal.remove();
  const errEl = modal.querySelector('[data-ov="error"]');
  const submitBtn = modal.querySelector('[data-ov="submit"]');
  modal.querySelector('[data-ov="cancel"]').addEventListener('click', close);
  modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
  modal.querySelector('[data-ov="email"]').focus();
  modal.querySelector('form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = modal.querySelector('[data-ov="email"]').value.trim();
    const password = modal.querySelector('[data-ov="password"]').value;
    errEl.classList.add('hidden');
    submitBtn.disabled = true; submitBtn.textContent = 'Verifying…';
    try {
      // Project-wide CAPTCHA means this re-auth needs a token too (managed widget,
      // mostly invisible). Fetched per attempt - tokens are single-use.
      const captchaToken = await getTokenInteractive();
      const approver = await model.verifyOverrideCredentials(email, password, requiredCap, captchaToken);
      close();
      onApproved(approver);
    } catch (err) {
      errEl.textContent = err.message ?? 'Authorization failed.';
      errEl.classList.remove('hidden');
      submitBtn.disabled = false; submitBtn.textContent = 'Authorize';
    }
  });
};

const controlReceiptEdit = function (id) {
  if (!_canAdjustReceipt()) return _denyReceiptAdj();
  const adj = model.state.currentReceiptAdjustments.find((a) => a.id === id);
  if (!adj) return;
  OrderCheckOutView._showReceiptEditForm(adj);
};

const controlSaveReceiptOverride = function ({ id, value }) {
  if (!_canAdjustReceipt()) return _denyReceiptAdj();
  model.overrideReceiptAdjustment(id, value);
  _refreshCheckoutAdj();
};

const controlRemoveReceiptAdj = function (id) {
  if (!_canAdjustReceipt()) return _denyReceiptAdj();
  model.removeReceiptAdjustment(id);
  _refreshCheckoutAdj();
};

const controlShowReceiptAddManualForm = function () {
  if (_canAdjustReceipt()) {
    _receiptAdjApprover = null;
    OrderCheckOutView._showReceiptAddManualForm();
    return;
  }
  // Cashier lacks the capability - require a manager/admin to authorize. The
  // approver (who must hold apply_receipt_adjustment) is recorded on the adjustment.
  _promptManagerOverride({
    requiredCap: 'apply_receipt_adjustment',
    onApproved: (approver) => {
      _receiptAdjApprover = approver;
      OrderCheckOutView._showReceiptAddManualForm();
    },
  });
};

const controlSaveManualReceiptAdj = function (data) {
  if (!_canAdjustReceipt() && !_receiptAdjApprover) return _denyReceiptAdj();
  model.addManualReceiptAdjustment({ ...data, approvedBy: _receiptAdjApprover });
  if (_receiptAdjApprover) showToast(`One-off adjustment approved by ${_receiptAdjApprover}.`, 'success');
  _receiptAdjApprover = null;
  _refreshCheckoutAdj();
};
// ── Cart item deletion ────────────────────────────────────────────────────────

const controlGoBackToOrder = function () {
  _hideCFDQr(); // leaving checkout - don't leave the QR up on the customer display
  OrderCheckOutView.closeRedeemModal(); // top-level modal, outside .modal-parent - doesn't auto-clear
  NewOrderView.render(modelState);
  // Backward step: the order screen slides in from the left
  document.querySelector('.modal-parent .pos-screen')?.classList.add('pos-screen--slide-back');
};

const controlDeleteCartItemInOrder = async function (index) {
  // Slide the row out, then patch only the cart column (no full-screen re-render).
  await NewOrderView.animateCartRowOut(index);
  model.deleteCartItem(index);
  _broadcastCart();
  if (!NewOrderView.updateCart()) NewOrderView.render(modelState);
};

const controlDeleteCartItemInCheckout = function (index) {
  // A deleted line can't keep a dangling free-item redemption - strip it
  // before the line itself is gone.
  const removedLineId = model.state.cart[index]?.lineId;
  model.deleteCartItem(index);
  if (removedLineId) model.removeLoyaltyRewardRedemption(removedLineId);
  _broadcastCart();

  if (model.state.cart.length === 0) {
    // Last item removed at checkout: slide back to the (now empty) order screen
    NewOrderView.render(modelState);
    document.querySelector('.modal-parent .pos-screen')?.classList.add('pos-screen--slide-back');
    return;
  }

  // Route through _refreshCheckoutAdj (rather than a second hand-rolled copy
  // of the same recompute) so promoCode/loyalty context and cart-line reward
  // pills stay in sync - a prior version of this function skipped both.
  OrderCheckOutView._subtotal = model.state.cart.reduce((acc, item) => acc + item.totalPrice, 0);
  _refreshCheckoutAdj();
};

//listens to modal close button
const controlNewOrderModals = async function () {
  NewOrderItemView._closeItemModal();
  NewOrderView._addHandlerCloseModal(controlCloseOrderModal);
};

const _animateSalesTotal = function (el, fromVal, toVal, onDone) {
  const fmt = (n) => formatMoney(n);

  const card = el.closest(".stat-card");
  if (card) {
    card.classList.remove("stat-card--updating");
    void card.offsetWidth;
    card.classList.add("stat-card--updating");
    card.addEventListener("animationend", () => card.classList.remove("stat-card--updating"), { once: true });
  }

  const duration = 700;
  const start = performance.now();
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const tick = (now) => {
    const progress = Math.min((now - start) / duration, 1);
    el.textContent = fmt(fromVal + (toVal - fromVal) * easeOut(progress));
    if (progress < 1) requestAnimationFrame(tick);
    else if (onDone) onDone();
  };
  requestAnimationFrame(tick);
};

// Generic count-up for the integer (Transactions) and duration (Avg Serving)
// dash numbers, mirroring _animateSalesTotal. `fmt` formats each interpolated
// value; `onDone` fires once when the number settles at toVal.
const _animateNumber = function (el, fromVal, toVal, fmt, onDone) {
  const duration = 700;
  const start = performance.now();
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const tick = (now) => {
    const progress = Math.min((now - start) / duration, 1);
    el.textContent = fmt(fromVal + (toVal - fromVal) * easeOut(progress));
    if (progress < 1) requestAnimationFrame(tick);
    else if (onDone) onDone();
  };
  requestAnimationFrame(tick);
};

// ── Dashboard card reveals: serialized count-up + odometer roll ──────────────
// The three home dash cards (Sales -> Transactions -> Avg Serving) reveal ONE AT
// A TIME so only a single thing ever moves: each card counts its number up, then
// rolls its "vs yesterday" % into place like a mechanical counter, and only then
// does the next card start. _enqueueDashAnim chains the steps; each resolves when
// its card has fully animated.
let _dashAnimChain = Promise.resolve();
const _enqueueDashAnim = (step) => {
  const run = _dashAnimChain.then(step, step);
  _dashAnimChain = run.then(() => {}, () => {});
  return run;
};

// On a new sale the dashboard sits behind the success overlay - hold each card's
// animation until that overlay is gone so the count-up/roll is actually seen.
const _waitForSuccessOverlayGone = () => new Promise((resolve) => {
  if (!document.querySelector('.success-overlay')) { resolve(); return; }
  const obs = new MutationObserver(() => {
    if (!document.querySelector('.success-overlay')) { obs.disconnect(); resolve(); }
  });
  obs.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => { obs.disconnect(); resolve(); }, 10_000);
});

// Sets a trend badge's final markup but holds it hidden (opacity 0) so it does
// not flash in place while the number above it is still counting up. Empty
// badges (no baseline) render nothing.
const _armBadge = (el, badge) => {
  if (!el) return;
  if (!badge || badge.empty) {
    if (badge?.className) el.className = badge.className;
    el.innerHTML = '';
    el.classList.remove('svs--pending');
    return;
  }
  el.className = badge.className;
  el.innerHTML = badge.html;
  el.classList.add('svs--pending');
};

// Reveals an armed badge by rolling each of its digits up into place (odometer).
// Non-digit characters (arrow, %, "m"/"s", "$", spaces) stay put. Resolves when
// the roll finishes; honors prefers-reduced-motion by just showing the value.
const _DIGITS = '0123456789';
const _rollBadgeIn = (el) => new Promise((resolve) => {
  if (!el) { resolve(); return; }
  el.classList.remove('svs--pending');
  if (!el.innerHTML.trim()) { resolve(); return; }
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { resolve(); return; }

  const reels = [];
  const textNodes = [];
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  for (const node of textNodes) {
    if (!/\d/.test(node.nodeValue)) continue;
    const frag = document.createDocumentFragment();
    for (const ch of node.nodeValue) {
      if (ch >= '0' && ch <= '9') {
        const reel = document.createElement('span');
        reel.className = 'odo-reel';
        const col = document.createElement('span');
        col.className = 'odo-col';
        for (const d of _DIGITS) {
          const dg = document.createElement('span');
          dg.className = 'odo-d';
          dg.textContent = d;
          col.appendChild(dg);
        }
        reel.appendChild(col);
        frag.appendChild(reel);
        reels.push({ col, target: Number(ch) });
      } else {
        frag.appendChild(document.createTextNode(ch));
      }
    }
    node.parentNode.replaceChild(frag, node);
  }
  if (reels.length === 0) { resolve(); return; }

  void el.offsetWidth; // reflow so reels paint at digit 0 before rolling
  const stagger = 55;
  reels.forEach(({ col, target }, i) => {
    col.style.transitionDelay = `${i * stagger}ms`;
    col.style.transform = `translateY(-${target}em)`;
  });
  setTimeout(resolve, (reels.length - 1) * stagger + 600);
});

const _countUpNumber = (el, fromVal, toVal, fmt) => new Promise((resolve) => {
  if (!el || fromVal === toVal) { if (el) el.textContent = fmt(toVal); resolve(); return; }
  _animateNumber(el, fromVal, toVal, fmt, resolve);
});

const _countUpMoney = (el, fromVal, toVal) => new Promise((resolve) => {
  const fmt = (n) => formatMoney(n);
  if (!el || fromVal === toVal) { if (el) el.textContent = fmt(toVal); resolve(); return; }
  _animateSalesTotal(el, fromVal, toVal, resolve);
});

let _lastServingMins = null;

const _cashierDisplayName = (c) => {
  if (!c) return null;
  return `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || null;
};

const _updateCashierDisplay = function () {
  const el = document.getElementById('shiftStr');
  if (!el) return;
  const name = _cashierDisplayName(model.state.currentCashier) || model.state.username || '—';
  el.textContent = name.split(' ')[0];
  _updateCashierShiftStatus();
};

const controlSwitchCashier = async function () {
  const existing = document.getElementById('cashierPickerOverlay');
  if (existing) { existing.remove(); return; }

  // Open the modal IMMEDIATELY with a spinner - the staff + on-shift fetches
  // used to run before anything appeared, which read as a dead button for a
  // few seconds on slow connections.
  const el = document.createElement('div');
  el.id = 'cashierPickerOverlay';
  el.className = 'cashier-picker-overlay';
  const card = document.createElement('div');
  card.className = 'cashier-picker-card';
  card.innerHTML = `
    <div class="cashier-picker-header">
      <div>
        <h3 class="cashier-picker-title">Switch Cashier</h3>
        <p class="cashier-picker-subtitle">Who's at the register right now?</p>
      </div>
      <button class="modal-close-btn" id="cashierPickerCloseBtn" type="button">&times;</button>
    </div>
    <div class="cashier-picker-loading" role="status" aria-label="Loading staff"><span class="cashier-picker-spinner"></span></div>
  `;
  el.appendChild(card);
  document.body.appendChild(el);
  el.addEventListener('click', e => { if (e.target === el) el.remove(); });
  card.querySelector('#cashierPickerCloseBtn')?.addEventListener('click', () => el.remove());

  // Everyone active is LISTED, but only staff who are currently clocked in can be
  // switched to (plus the owner, who runs the POS without clocking in - prevents a
  // no-cashier lockout). Off-shift staff are greyed out with a "Not clocked in"
  // badge. If the on-shift lookup fails (e.g. offline), treat everyone as on shift
  // rather than block. Fetches run in PARALLEL (they used to be sequential).
  let onShiftIds = null;
  await Promise.all([
    model.loadStaff().catch(() => {}),
    model.getOnShiftStaff()
      .then(list => { onShiftIds = new Set(list.map(s => s.staffId)); })
      .catch(() => {}),
  ]);
  if (!document.body.contains(el)) return; // closed while loading

  const isOffShift = s => onShiftIds !== null && !s.isOwner && !onShiftIds.has(s.id);
  const activeStaff = model.state.staff
    .filter(s => s.isActive)
    .sort((a, b) => isOffShift(a) - isOffShift(b));
  const AVATAR_COLORS = ['#22c55e','#3b82f6','#f59e0b','#ec4899','#8b5cf6','#06b6d4'];

  const staffMeta = (s, i) => {
    const fullName    = _cashierDisplayName(s);
    const displayName = fullName || (s.email ? s.email.split('@')[0] : '?');
    const initials    = fullName
      ? fullName.split(' ').map(w => w[0]).slice(0,2).join('').toUpperCase()
      : displayName.slice(0,2).toUpperCase();
    return { displayName, initials, color: AVATAR_COLORS[i % AVATAR_COLORS.length] };
  };

  const renderStaffList = () => {
    card.innerHTML = `
      <div class="cashier-picker-header">
        <div>
          <h3 class="cashier-picker-title">Switch Cashier</h3>
          <p class="cashier-picker-subtitle">Who's at the register right now?</p>
        </div>
        <button class="modal-close-btn" id="cashierPickerCloseBtn" type="button">&times;</button>
      </div>
      <ul class="cashier-picker-list">
        ${activeStaff.length ? activeStaff.map((s, i) => {
          const { displayName, initials, color } = staffMeta(s, i);
          const isActive = model.state.currentCashier?.id === s.id;
          const offShift = isOffShift(s);
          return `
            <li class="cashier-picker-item${isActive ? ' cashier-picker-item--active' : ''}${(!s.hasPin || offShift) ? ' cashier-picker-item--disabled' : ''}"
                data-id="${s.id}" data-idx="${i}" role="button" tabindex="0">
              <div class="cashier-picker-avatar" style="background:${color}">${esc(initials)}</div>
              <div class="cashier-picker-info">
                <span class="cashier-picker-name">${esc(displayName)}</span>
                <span class="cashier-picker-role">${esc(s.role)}${s.hasPin ? '' : ' &middot; <em class="cashier-no-pin">PIN not set</em>'}</span>
              </div>
              ${offShift ? '<span class="cashier-offshift-badge">Not clocked in</span>' : ''}
              ${isActive ? `<div class="cashier-picker-check"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg></div>` : ''}
            </li>`;
        }).join('') : '<li class="cashier-picker-empty">No active staff found.</li>'}
      </ul>
    `;
    card.querySelector('#cashierPickerCloseBtn')?.addEventListener('click', () => el.remove());
    card.querySelector('.cashier-picker-list')?.addEventListener('click', e => {
      const item = e.target.closest('.cashier-picker-item[data-id]');
      if (!item || item.classList.contains('cashier-picker-item--disabled')) return;
      const chosen = activeStaff.find(s => s.id === item.dataset.id);
      if (chosen) renderPinScreen(chosen, parseInt(item.dataset.idx));
    });
  };

  const renderPinScreen = (chosen, idx) => {
    const { displayName, initials, color } = staffMeta(chosen, idx);

    if (!chosen.hasPin) {
      model.state.currentCashier = chosen;
      _updateCashierDisplay();
      showToast(`Switched to ${displayName}. Set a PIN in Staff settings.`, 'success');
      el.remove();
      return;
    }

    card.innerHTML = `
      <div class="cashier-picker-header">
        <div>
          <h3 class="cashier-picker-title">Enter PIN</h3>
          <p class="cashier-picker-subtitle">Switching to ${esc(displayName)}</p>
        </div>
        <button class="modal-close-btn" id="cashierPinCloseBtn" type="button">&times;</button>
      </div>
      <div class="cashier-pin-screen">
        <div class="cashier-picker-avatar cashier-pin-avatar" style="background:${color}">${esc(initials)}</div>
        <p class="cashier-pin-name">${esc(displayName)}</p>
        <p class="cashier-pin-role">${esc(chosen.role)}</p>
        <div class="cashier-pin-dots" id="cashierPinDots">
          <span class="cashier-pin-dot"></span>
          <span class="cashier-pin-dot"></span>
          <span class="cashier-pin-dot"></span>
          <span class="cashier-pin-dot"></span>
          <span class="cashier-pin-dot"></span>
          <span class="cashier-pin-dot"></span>
        </div>
        <p class="cashier-pin-error hidden" id="cashierPinError">Incorrect PIN. Try again.</p>
        <div class="cashier-numpad">
          ${[1,2,3,4,5,6,7,8,9].map(n => `<button class="cashier-numpad-btn" data-key="${n}">${n}</button>`).join('')}
          <button class="cashier-numpad-btn cashier-numpad-nav" data-key="back" aria-label="Back">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            Back
          </button>
          <button class="cashier-numpad-btn" data-key="0">0</button>
          <button class="cashier-numpad-btn cashier-numpad-nav" data-key="del" aria-label="Delete digit">
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"/><line x1="18" y1="9" x2="12" y2="15"/><line x1="12" y1="9" x2="18" y2="15"/></svg>
          </button>
        </div>
        <button class="cashier-pin-forgot" id="cashierPinForgotBtn" type="button">Forgot PIN?</button>
      </div>
    `;

    let enteredPin = '';

    const updateDots = () => {
      card.querySelectorAll('.cashier-pin-dot').forEach((d, i) => {
        d.classList.toggle('cashier-pin-dot--filled', i < enteredPin.length);
      });
    };

    const shakeAndClear = () => {
      const dotsEl = card.querySelector('#cashierPinDots');
      const errEl  = card.querySelector('#cashierPinError');
      dotsEl?.classList.add('cashier-pin-shake');
      errEl?.classList.remove('hidden');
      setTimeout(() => {
        dotsEl?.classList.remove('cashier-pin-shake');
        enteredPin = '';
        updateDots();
      }, 600);
    };

    card.querySelector('#cashierPinCloseBtn')?.addEventListener('click', () => el.remove());
    card.querySelector('#cashierPinForgotBtn')?.addEventListener('click', () => renderForgotPin(chosen, idx));
    card.querySelector('.cashier-numpad')?.addEventListener('click', e => {
      const key = e.target.closest('.cashier-numpad-btn')?.dataset.key;
      if (!key) return;
      if (key === 'back') { renderStaffList(); return; }
      if (key === 'del')  { enteredPin = enteredPin.slice(0, -1); updateDots(); return; }
      if (enteredPin.length >= 6) return;
      enteredPin += key;
      updateDots();
      if (enteredPin.length === 6) {
        if (enteredPin === chosen.pin) {
          model.state.currentCashier = chosen;
          _updateCashierDisplay();
          showToast(`Switched to ${displayName}`, 'success');
          el.remove();
        } else {
          shakeAndClear();
        }
      }
    });
  };

  // Forgot PIN: the staff member proves identity with their OWN account password
  // (verified on an isolated client - the POS session is untouched), then sets a
  // new 6-digit PIN and is switched in immediately.
  const renderForgotPin = (chosen, idx) => {
    const { displayName, initials, color } = staffMeta(chosen, idx);
    card.innerHTML = `
      <div class="cashier-picker-header">
        <div>
          <h3 class="cashier-picker-title">Reset PIN</h3>
          <p class="cashier-picker-subtitle">Verify it's you, ${esc(displayName)}</p>
        </div>
        <button class="modal-close-btn" id="cashierForgotCloseBtn" type="button">&times;</button>
      </div>
      <div class="cashier-forgot-body">
        <div class="cashier-picker-avatar cashier-pin-avatar" style="background:${color}">${esc(initials)}</div>
        <div class="edit-field" style="margin-top:12px">
          <label for="cashierForgotEmail">Your email</label>
          <input type="email" id="cashierForgotEmail" class="settings-input" value="${esc(chosen.email ?? '')}" readonly />
        </div>
        <div class="edit-field" style="margin-top:10px">
          <label for="cashierForgotPassword">Your account password</label>
          <input type="password" id="cashierForgotPassword" class="settings-input" placeholder="••••••••" autocomplete="current-password" />
        </div>
        <div class="edit-field" style="margin-top:10px">
          <label for="cashierForgotNewPin">New 6-digit PIN</label>
          <input type="text" id="cashierForgotNewPin" class="settings-input pin-input" inputmode="numeric" maxlength="6" placeholder="••••••" autocomplete="off" />
        </div>
        <p class="cashier-pin-error hidden" id="cashierForgotError"></p>
        <div class="adj-form-actions" style="margin-top:16px">
          <button type="button" class="btn" id="cashierForgotBackBtn">Back</button>
          <button type="button" class="btn primary" id="cashierForgotSaveBtn">Verify &amp; Save PIN</button>
        </div>
      </div>
    `;

    const errEl = card.querySelector('#cashierForgotError');
    const fail = (msg) => { errEl.textContent = msg; errEl.classList.remove('hidden'); };

    card.querySelector('#cashierForgotCloseBtn')?.addEventListener('click', () => el.remove());
    card.querySelector('#cashierForgotBackBtn')?.addEventListener('click', () => renderPinScreen(chosen, idx));
    card.querySelector('#cashierForgotSaveBtn')?.addEventListener('click', async () => {
      errEl.classList.add('hidden');
      const password = card.querySelector('#cashierForgotPassword')?.value;
      const newPin   = card.querySelector('#cashierForgotNewPin')?.value.trim();
      if (!password) return fail('Enter your account password.');
      if (!/^\d{6}$/.test(newPin)) return fail('The new PIN must be exactly 6 digits.');
      const btn = card.querySelector('#cashierForgotSaveBtn');
      btn.disabled = true; btn.textContent = 'Verifying…';
      try {
        const captchaToken = await getTokenInteractive();
        await model.resetOwnPin({ email: chosen.email, password, staffId: chosen.id, newPin, captchaToken });
        model.state.currentCashier = chosen;
        _updateCashierDisplay();
        showToast(`PIN updated. Switched to ${displayName}.`, 'success');
        el.remove();
      } catch (err) {
        btn.disabled = false; btn.textContent = 'Verify & Save PIN';
        fail(err.message ?? 'Could not reset the PIN.');
      }
    });
    card.querySelector('#cashierForgotPassword')?.focus();
  };

  renderStaffList();
};

// Enqueues the Today's Sales card reveal (count-up then % roll). Used standalone
// on a new sale and on void; the initial dashboard load goes through
// _revealDashboardCards so all three cards stay in a fixed Sales->Txns->Serving
// order. Returns the chain promise so callers can sequence after it if needed.
const refreshTodaySalesDisplay = function () {
  const dataP = Promise.all([
    model.loadYesterdaySalesTotal(),
    model.loadTodaySalesTotal(),
  ]).catch(() => null);
  return _enqueueDashAnim(() => _animateSalesStep(dataP));
};

const initApp = async function (user, { isInviteAcceptance = false } = {}) {
  // Session confirmed: release the pre-boot anti-home-flash guard (a signed-in
  // load never sets it, but a stale/edge-case token check must not leave the
  // app chrome hidden).
  document.documentElement.classList.remove('boot-auth');
  await model.loadBusinessContext(user, { isInviteAcceptance });
  const s = model.state.currentStaff;
  model.state.username =
    (s ? `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() : null) ||
    user.user_metadata?.display_name ||
    user.email;
  const companyNameEl = document.querySelector('.company-name');
  if (companyNameEl) companyNameEl.textContent = model.state.username;
  localStorage.setItem('pointbunny_store_name', model.state.username);
  localStorage.setItem('pointbunny_business_id', model.state.businessId);
  // Stash the business country so every window (POS views + KDS/CFD popups) can
  // localize the order-type label (Takeout vs Takeaway) without extra plumbing.
  localStorage.setItem('pointbunny_business_country', model.state.businessAddressCountry ?? '');
  // Bind the realtime broadcast channel to THIS business so KDS / customer-display
  // sync never crosses tenants. Must happen before any postMessage below.
  channel.init(model.state.businessId);
  // Best-effort silent reconnect to a previously-paired Bluetooth printer so it
  // is ready at checkout without reopening Settings. No-op on iOS/unsupported.
  btPrinter.reconnect().catch(() => {});
  _applyPermissionGates();
  if (sessionStorage.getItem('pointbunny_role_changed')) {
    sessionStorage.removeItem('pointbunny_role_changed');
    setTimeout(() => showToast(`Your role is now ${model.state.role}.`, 'info'), 600);
  }
  if (model.state.currentStaff) model.state.currentCashier = model.state.currentStaff;
  _updateCashierDisplay();
  if (model.hasPermission('manage_business')) model.loadBusinessProfile().catch(() => {});

  // All queries are independent after businessId is set — run in parallel
  // instead of 7 sequential round trips (was ~1.4s, now takes as long as the slowest one)
  await Promise.all([
    model.loadMenuItems(),
    model.loadMenuCategories(),
    model.loadAdjustments(),
    model.loadDiscountCodes(),
    model.loadEmployees(),
    model.loadOrderQueue(),
    // Inventory loads with everything else so the order screen can badge
    // out-of-stock items and checkout can compute consumption with no extra
    // fetch. Tolerant of the un-migrated case - inventory is opt-in.
    model.loadInventory().catch(() => {}),
    // Reward catalog loads eagerly too - checkout needs it (use_loyalty) even
    // for staff who can never open the manage_loyalty-gated Loyalty panel.
    // Tolerant of the un-migrated case, same as inventory.
    model.fetchLoyaltyRewards().catch(() => {}),
  ]);
  _updateMenuSetupBanner(); // also runs _updateGettingStarted()
  _applyHomeStatsVisibility();
  _startOnShiftIndicator();
  _refreshHomeExpensesToday();
  if (modelState.orderQueue.length > 0) {
    KDSView.renderQueue(modelState.orderQueue);
    _ensureKDSTick();
  }
  // Push queue to any KDS/CFD windows already open when the main app finishes loading
  channel.postMessage({
    type: MSG.KDS_QUEUE_SYNC,
    queue: modelState.orderQueue,
    thresholds: {
      yellow: model.state.settings.kdsYellowThreshold,
      red: model.state.settings.kdsRedThreshold,
    },
  });
  initAnalytics(user.id);
  model.watchStaffDeactivation(_handleStaffDeactivated, _handleStaffRoleChanged);
  _revealDashboardCards();
  model.loadTickets().then(() => SupportView.syncUnreadBadge(model.state.tickets)).catch(() => {});
  _refreshNotifications();
};

let _yesterdayTotal = null;
let _todayTransactionCount = null;
let _yesterdayTransactionCount = null;
let _settingsOTPEmail = null;
let _settingsOTPData  = null;

// Shared trend arrows for the dashboard badges (color comes from the svs-- class).
const _ARROW_UP   = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>`;
const _ARROW_DOWN = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/></svg>`;

// "vs yesterday" trend badge. When today has no sales yet, showing a red "100%
// down" against yesterday reads as a decline rather than "the day just hasn't
// started" - so today = 0 always renders as a neutral empty state instead, no
// matter what yesterday was. When yesterday is $0 (and today has sales) it
// reads "from $0 yesterday" (up).
// Compact money for the "vs ... yesterday" caption (whole dollars + commas).
const _fmtMoneyShort = (n) => currencySymbol() + Math.round(Number(n) || 0).toLocaleString('en-US');

// Smaller arrows for the serving badge (matches the original 11px inline icons).
const _SERV_ARROW_UP   = `<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>`;
const _SERV_ARROW_DOWN = `<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/></svg>`;

const _fmtServing = (mins) => {
  const t = Math.round(mins * 60);
  const m = Math.floor(t / 60);
  const s = t % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

// ── Pure badge builders: same data/maths as before, but they only COMPUTE the
// markup ({ className, html, baseText } or { empty:true }). The animation steps
// decide when to paint and roll them. This keeps the data path identical. ──────
const _salesBadge = (todayTotal) => {
  if (_yesterdayTotal === null) return { empty: true, className: 'sales-vs-yesterday', baseText: '' };
  if (todayTotal === 0) return { empty: true, className: 'sales-vs-yesterday svs--flat', baseText: 'No sales yet today' };
  if (_yesterdayTotal === 0) {
    return { className: 'sales-vs-yesterday svs--up', html: `${_ARROW_UP}<span>from ${currencySymbol()}0</span>`, baseText: 'No sales yesterday' };
  }
  const pct  = Math.round(((todayTotal - _yesterdayTotal) / _yesterdayTotal) * 100);
  const isUp = pct >= 0;
  return {
    className: `sales-vs-yesterday ${isUp ? 'svs--up' : 'svs--down'}`,
    html: `${isUp ? _ARROW_UP : _ARROW_DOWN}<span>${Math.abs(pct)}%</span>`,
    baseText: `vs ${_fmtMoneyShort(_yesterdayTotal)} yesterday`,
  };
};

const _txnsBadge = () => {
  if (_yesterdayTransactionCount === null) return { empty: true, className: 'sales-vs-yesterday', baseText: '' };
  if (_todayTransactionCount === 0) return { empty: true, className: 'sales-vs-yesterday svs--flat', baseText: 'No sales yet today' };
  if (_yesterdayTransactionCount === 0) {
    return { className: 'sales-vs-yesterday svs--up', html: `${_ARROW_UP}<span>from 0</span>`, baseText: 'None yesterday' };
  }
  const pct  = Math.round(((_todayTransactionCount - _yesterdayTransactionCount) / _yesterdayTransactionCount) * 100);
  const isUp = pct >= 0;
  const yc   = _yesterdayTransactionCount;
  return {
    className: `sales-vs-yesterday ${isUp ? 'svs--up' : 'svs--down'}`,
    html: `${isUp ? _ARROW_UP : _ARROW_DOWN}<span>${Math.abs(pct)}%</span>`,
    baseText: `vs ${yc} ${yc === 1 ? 'sale' : 'sales'} yesterday`,
  };
};

const _servingBadge = (todayMins, yestMins) => {
  if (yestMins === null || yestMins === 0) return { empty: true, className: 'home-dash-serving-vs', baseText: 'No data yesterday' };
  const diffSecs = Math.round((yestMins - todayMins) * 60); // positive = faster today
  const absSecs  = Math.abs(diffSecs);
  const absFmt   = absSecs >= 60 ? `${Math.floor(absSecs / 60)}m ${absSecs % 60}s` : `${absSecs}s`;
  const baseText = `vs ${_fmtServing(yestMins)} yesterday`;
  if (diffSecs > 0) return { className: 'home-dash-serving-vs svs--up',   html: `${_SERV_ARROW_UP}<span>${absFmt} faster</span>`, baseText };
  if (diffSecs < 0) return { className: 'home-dash-serving-vs svs--down', html: `${_SERV_ARROW_DOWN}<span>${absFmt} slower</span>`, baseText };
  return { className: 'home-dash-serving-vs svs--flat', html: '<span>No change</span>', baseText };
};

const _fetchServingTotals = () => {
  const { start: ts, end: te } = _getBizDayBounds();
  const { start: ys, end: ye } = _getBizDayBounds(new Date(Date.now() - 86400000));
  return Promise.all([
    model.fetchPeriodTotals(ts.toISOString(), te.toISOString()),
    model.fetchPeriodTotals(ys.toISOString(), ye.toISOString()),
  ]).then(([today, yesterday]) => ({ today, yesterday }));
};

// ── Per-card animation steps (run serialized via _enqueueDashAnim). Each waits
// for the success overlay to clear, counts its number up, then rolls its badge. ─
const _animateSalesStep = async (dataP) => {
  const el = document.getElementById('totalStr');
  if (!el) return;
  await _waitForSuccessOverlayGone();
  let todayTotal;
  try { const d = await dataP; if (d) { _yesterdayTotal = d[0]; todayTotal = d[1]; } } catch (_) {}
  if (todayTotal === undefined) { try { todayTotal = await model.loadTodaySalesTotal(); } catch (_) { return; } }
  const currentVal = parseFloat(el.textContent.replace(/[$,]/g, '')) || 0;
  const badge  = _salesBadge(todayTotal);
  const baseEl = document.getElementById('salesBaseStr');
  if (baseEl) baseEl.textContent = badge.baseText ?? '';
  const badgeEl = document.getElementById('salesVsYesterday');
  _armBadge(badgeEl, badge);
  await _countUpMoney(el, currentVal, todayTotal);
  await _rollBadgeIn(badgeEl);
};

const _animateTxnsStep = async (dataP) => {
  const countEl = document.getElementById('basketCountStr');
  if (!countEl) return;
  await _waitForSuccessOverlayGone();
  if (dataP) {
    try { const d = await dataP; if (d) { _todayTransactionCount = d.today; _yesterdayTransactionCount = d.yesterday; } } catch (_) {}
  }
  if (_todayTransactionCount === null) return;
  const parsed  = parseInt(String(countEl.textContent).replace(/[^\d-]/g, ''), 10);
  const fromVal = Number.isFinite(parsed) ? parsed : 0; // "—" on first load -> count up from 0
  const badge   = _txnsBadge();
  const baseEl  = document.getElementById('basketsBaseStr');
  if (baseEl) baseEl.textContent = badge.baseText ?? '';
  const badgeEl = document.getElementById('basketsVsYesterday');
  _armBadge(badgeEl, badge);
  await _countUpNumber(countEl, fromVal, _todayTransactionCount, (n) => String(Math.round(n)));
  await _rollBadgeIn(badgeEl);
};

const _animateServingStep = async (dataP) => {
  const valEl = document.getElementById('homeAvgServing');
  if (!valEl) return;
  await _waitForSuccessOverlayGone();
  let data;
  try { data = await dataP; } catch (_) {}
  if (!data) { try { data = await _fetchServingTotals(); } catch (_) { return; } }
  const baseEl  = document.getElementById('servingBaseStr');
  const badgeEl = document.getElementById('homeServingVs');
  const todayMins = data.today.avgServingMinutes;
  if (todayMins === null) {
    valEl.textContent = '—';
    _armBadge(badgeEl, { empty: true, className: 'home-dash-serving-vs' });
    if (baseEl) baseEl.textContent = '';
    _lastServingMins = null;
    return;
  }
  const fromMins = _lastServingMins ?? 0; // count from the previously shown value (0 on first load)
  _lastServingMins = todayMins;
  const badge = _servingBadge(todayMins, data.yesterday.avgServingMinutes);
  if (baseEl) baseEl.textContent = badge.baseText ?? '';
  _armBadge(badgeEl, badge);
  await _countUpNumber(valEl, fromMins, todayMins, _fmtServing);
  await _rollBadgeIn(badgeEl);
};

// Standalone enqueuers (one card at a time). Transactions uses the in-memory
// counts (already incremented on a sale / decremented on a void); pass them in
// at trigger sites in Sales-before-Transactions order.
const _updateTransactionBadge = function () {
  return _enqueueDashAnim(() => _animateTxnsStep(null));
};

const _loadServingComparison = function () {
  const dataP = _fetchServingTotals().catch(() => null);
  return _enqueueDashAnim(() => _animateServingStep(dataP));
};

// Initial dashboard reveal: fetch all three cards' data in parallel, then queue
// their animations in a fixed Sales -> Transactions -> Avg Serving order so only
// one card moves at a time regardless of which fetch resolves first.
const _revealDashboardCards = function () {
  const salesP   = Promise.all([model.loadYesterdaySalesTotal(), model.loadTodaySalesTotal()]).catch(() => null);
  const txnsP    = model.loadTransactionCounts().catch(() => null);
  const servingP = _fetchServingTotals().catch(() => null);
  _enqueueDashAnim(() => _animateSalesStep(salesP));
  _enqueueDashAnim(() => _animateTxnsStep(txnsP));
  _enqueueDashAnim(() => _animateServingStep(servingP));
};

// Post-launch prompts must be sequenced, not stacked: the PIN setup overlay sits at
// z-index 99999 and would cover the onboarding form. So show onboarding first when it's
// needed and defer the PIN prompt until onboarding is dismissed (controlOnboardingSubmit);
// otherwise show the PIN prompt right away.
const _showLaunchPrompts = function () {
  // If they got in via a recovery code, their 2FA was removed - nudge a re-enroll.
  if (sessionStorage.getItem('pointbunny_2fa_recovered')) {
    sessionStorage.removeItem('pointbunny_2fa_recovered');
    showToast('You signed in with a recovery code, so two-factor was turned off. Re-enable it in Settings to stay protected.', 'info');
  }
  if (model.state.needsOnboarding) {
    OnboardingView.show();
  } else {
    _maybeShowPinSetup();
  }
};

const controlOnboardingSubmit = async function ({ businessName, businessType, industry, phone, street, city, province, zip, country }) {
  OnboardingView.setLoading(true);
  try {
    await model.saveOnboardingInfo({ businessName, businessType, industry, phone, street, city, province, zip, country });
    const companyNameEl = document.querySelector('.company-name');
    if (companyNameEl) companyNameEl.textContent = businessName;
    localStorage.setItem('pointbunny_store_name', businessName);
    localStorage.setItem('pointbunny_business_country', country ?? '');
    // Auto set Dine In / Takeout: ON for food & beverage, OFF otherwise. Set both
    // directions explicitly so a food business doesn't inherit a stale 'false' left
    // in localStorage by a non-food business onboarded earlier in the same browser.
    const orderTypeOn = businessType === 'food_beverage';
    model.setBusinessSetting('orderTypeEnabled', orderTypeOn);
    OnboardingView.hide();
    showToast('Business profile saved!', 'success');
    // If we know a tax rate for this location and no tax adjustment exists yet,
    // offer to auto-add it. Otherwise go straight to the deferred PIN setup.
    if (!_maybeShowTaxSuggestion({ country, province })) {
      _maybeShowPinSetup();
    }
  } catch (err) {
    OnboardingView.showError(err.message ?? 'Something went wrong. Please try again.');
  } finally {
    OnboardingView.setLoading(false);
  }
};

// Holds the suggestion currently shown in the tax prompt so the Add handler can use it.
let _pendingTaxSuggestion = null;

// Shows the tax-suggestion prompt and returns true if it was shown, false otherwise.
// Skips silently when no rate is known for the location or a tax-type fee already exists.
const _maybeShowTaxSuggestion = function ({ country, province }) {
  const suggestion = getTaxSuggestion({ country, province });
  if (!suggestion) return false;
  const existing = (model.state.settings.adjustments ?? []).some(
    (a) => a.type === 'fee' && /tax|vat|gst|iva|sst|consumption/i.test(a.name ?? '')
  );
  if (existing) return false;
  _pendingTaxSuggestion = suggestion;
  OnboardingView.showTaxPrompt(suggestion);
  return true;
};

const controlAddSuggestedTax = async function () {
  if (!_pendingTaxSuggestion) { OnboardingView.hideTaxPrompt(); _maybeShowPinSetup(); return; }
  OnboardingView.setTaxLoading(true);
  try {
    await model.addAdjustment({
      name: _pendingTaxSuggestion.name,
      type: 'fee',
      calculation: 'percentage',
      value: _pendingTaxSuggestion.rate,
    });
    showToast(`${_pendingTaxSuggestion.name} added to receipts.`, 'success');
  } catch (err) {
    showToast(err.message ?? 'Could not add tax. You can add it in Settings.', 'error');
  } finally {
    OnboardingView.setTaxLoading(false);
    OnboardingView.hideTaxPrompt();
    _pendingTaxSuggestion = null;
    _maybeShowPinSetup();
  }
};

const controlSkipSuggestedTax = function () {
  OnboardingView.hideTaxPrompt();
  _pendingTaxSuggestion = null;
  _maybeShowPinSetup();
};

const controlAcceptInvite = async function ({ password, pin }) {
  try {
    AuthView.setInviteLoading(true);
    try {
      await model.updatePassword(password);
    } catch (pwErr) {
      // On a retry after a partial acceptance the password may already be set to this
      // value, so Supabase rejects it as "same as the old password". That is not a real
      // failure on an invite - the account only needs its staff row claimed below. Any
      // other error (weak password, etc.) is still surfaced.
      if (!/different from the old|should be different|same.*password/i.test(pwErr.message ?? '')) {
        throw pwErr;
      }
    }

    const { data: { user } } = await supabase.auth.getUser();
    showLoadingScreen();
    AuthView.hide();

    await initApp(user, { isInviteAcceptance: true });
    hideLoadingScreen();

    if (model.state.currentStaff) {
      try {
        await model.setStaffPin(model.state.currentStaff.id, pin);
        model.state.currentStaff.hasPin = true;
        if (model.state.currentCashier?.id === model.state.currentStaff.id) {
          model.state.currentCashier.hasPin = true;
        }
      } catch (pinErr) {
        console.error('PIN save failed during invite acceptance:', pinErr);
      }
    }

    _wireApp();
    _showLaunchPrompts();
  } catch (err) {
    console.error('invite accept failed:', err);
    hideLoadingScreen();
    AuthView.show();
    AuthView.showInviteError(err.message ?? 'Something went wrong. Please contact your manager.');
    AuthView.setInviteLoading(false);
  }
};

// The signed-in account has a pending invite (surfaced by loadBusinessContext) and chose
// to accept it. Claim the row, then launch the app as that business's staff member.
const controlAcceptPendingInvite = async function (staffId) {
  try {
    AuthView.setJoinLoading(true);
    const user = await model.claimPendingInvite(staffId);
    showLoadingScreen();
    AuthView.hide();
    await initApp(user);
    hideLoadingScreen();
    _wireApp();
    _showLaunchPrompts();
  } catch (err) {
    console.error('accept pending invite failed:', err);
    hideLoadingScreen();
    AuthView.setJoinLoading(false);
    AuthView.show();
    AuthView.showJoinError(err.message ?? 'Could not join the team. Please try again.');
  }
};

// Declining leaves the pending invite in place (the owner can still see it and the person
// can accept later); we just sign out and return to the login screen.
const controlDeclinePendingInvite = async function () {
  model.dismissPendingInvites();
  await supabase.auth.signOut();
  window.location.reload();
};

const _isInAppBrowser = () => {
  const ua = navigator.userAgent || '';
  if (/FBAN|FBAV|Instagram|Twitter|Line|MicroMessenger|Snapchat|TikTok/.test(ua)) return true;
  if (/iphone|ipad|ipod/i.test(ua) && !/safari/i.test(ua)) return true;
  if (/android/i.test(ua) && /wv/.test(ua)) return true;
  return false;
};

const controlGoogleSignIn = async function () {
  if (_isInAppBrowser()) {
    AuthView.showGoogleInAppError(window.location.href);
    return;
  }
  AuthView.setGoogleLoading(true);
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin + window.location.pathname },
  });
  if (error) {
    AuthView.setGoogleLoading(false);
    AuthView.showError(error.message);
  }
  // On success, Supabase redirects the browser — no further code runs here.
};

// ── Two-Factor Authentication ─────────────────────────────────────────────────

let _enrollFactorId = null;

// Login gate. Returns true if it's OK to proceed (no 2FA on this account, or the
// code was verified), false if the user cancelled the prompt. Fails OPEN on an
// unexpected MFA-API error so a transient hiccup can't lock everyone out (2FA is
// opt-in - the security floor here is the password, which already passed).
const _enforceMFA = async function () {
  let aal;
  try { aal = await model.getAAL(); }
  catch { return true; }
  // Only challenge when the session is password-only but a verified factor exists.
  if (!(aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2')) return true;
  let factorId;
  try { ({ factorId } = await model.getMFAStatus()); }
  catch { return true; }
  if (!factorId) return true;

  let error = null;
  let mode = 'totp';
  while (true) {
    const res = await AuthView.promptMFACode({ error, mode });
    if (res === null) return false; // cancelled
    mode = res.type; // a failed attempt re-prompts in the SAME mode (recovery stays recovery)
    AuthView.setMFALoading(true); // verify/redeem runs in the background - show it's working
    try {
      if (res.type === 'recovery') {
        // Burns the code + removes the lost factor server-side; the session is then
        // effectively 2FA-off, so the gate passes. Flag it so we can nudge a re-enroll.
        await model.redeemRecoveryCode(res.code);
        sessionStorage.setItem('pointbunny_2fa_recovered', '1');
      } else {
        await model.verifyMFALogin(factorId, res.code);
      }
      AuthView.hideMFAPrompt();
      return true;
    } catch (err) {
      error = res.type === 'recovery'
        ? (err?.message ?? "That recovery code didn't work. Check it and try again.")
        : 'Incorrect or expired code. Try again.';
    }
  }
};

const _refresh2FA = async function () {
  try {
    const { enabled } = await model.getMFAStatus();
    SettingsView.render2FA(enabled);
  } catch {
    SettingsView.render2FA(false);
  }
};

const controlEnable2FA = async function () {
  try {
    const { factorId, qr, secret } = await model.enrollMFA();
    _enrollFactorId = factorId;
    SettingsView.show2FAEnroll({ qr, secret });
  } catch (err) {
    showToast(err.message ?? 'Could not start two-factor setup.', 'error');
  }
};

const controlConfirm2FAEnroll = async function (code) {
  try {
    await model.verifyMFAEnrollment(_enrollFactorId, code);
    _enrollFactorId = null;
    await _refresh2FA();
    // Recovery codes need a server round-trip. Keep the enroll modal up with a
    // spinner until they're ready so the user knows setup isn't finished and can't
    // wander off before the codes modal appears. If generation fails, 2FA is still
    // on - the user can regenerate by toggling off/on; surface the error either way.
    SettingsView.set2FAEnrollBusy('Generating recovery codes…');
    try {
      const codes = await model.generateRecoveryCodes();
      SettingsView.hide2FAEnroll();
      SettingsView.showRecoveryCodes(codes);
    } catch {
      SettingsView.hide2FAEnroll();
      showToast('Two-factor authentication enabled, but we could not generate recovery codes. Turn it off and on again to retry.', 'error');
    }
  } catch {
    SettingsView.set2FAEnrollError('Incorrect or expired code. Try again.');
  }
};

const controlDisable2FA = async function () {
  if (!confirm('Turn off two-factor authentication? Your account will no longer ask for a code at sign-in.')) return;
  try {
    const { factorId } = await model.getMFAStatus();
    if (factorId) await model.unenrollMFA(factorId);
    await model.deleteRecoveryCodes();
    showToast('Two-factor authentication turned off.', 'success');
    await _refresh2FA();
  } catch (err) {
    showToast(err.message ?? 'Could not turn off two-factor authentication.', 'error');
  }
};

const controlSignIn = async function (email, password, captchaToken) {
  AuthView.setLoading(true);
  const { data, error } = await supabase.auth.signInWithPassword({ email, password, options: { captchaToken } });
  if (error) {
    AuthView.setLoading(false);
    AuthView.showError(error.message);
    return;
  }
  // 2FA gate: if this account has two-factor on, require a valid code before we
  // load the app. Cancelling signs back out.
  const mfaOk = await _enforceMFA();
  if (!mfaOk) {
    await supabase.auth.signOut();
    AuthView.setLoading(false);
    AuthView.showError('Two-factor verification was cancelled.');
    return;
  }
  await AuthView.playSignInSuccess();
  showLoadingScreen();
  try {
    await initApp(data.user);
  } catch (err) {
    if (err.code === 'PENDING_INVITE_CONSENT') {
      hideLoadingScreen();
      AuthView.show();
      AuthView.showJoinTeam(model.state.pendingInvites);
      return;
    }
    console.error('initApp failed:', err);
    hideLoadingScreen();
    await supabase.auth.signOut();
    AuthView.show();
    AuthView.showError(['STAFF_DEACTIVATED', 'ADMIN_ACCOUNT'].includes(err.code) ? err.message : 'Something went wrong loading the app. Please try again.');
    return;
  }
  hideLoadingScreen();
  _wireApp();
  _showLaunchPrompts();
};

const controlSignUp = async function ({ firstName, lastName, email, password, captchaToken }) {
  AuthView.setSignUpLoading(true);
  // Carried in auth user_metadata (not read back until this user's first login, which may
  // happen on a different device once they confirm their email) - see sourceTracking.js.
  const storedSource = sourceTracking.getStoredSource();
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      captchaToken,
      // Without this, Supabase falls back to the dashboard Site URL (app.pointbunny.com)
      // for the confirmation link regardless of where signup happened - which sent local
      // testing straight to production and skipped whatever code is only running locally.
      // Origin-relative, same pattern as resetClient.js's redirectTo and Google OAuth.
      emailRedirectTo: window.location.origin,
      data: {
        first_name: firstName,
        last_name: lastName,
        display_name: `${firstName} ${lastName}`,
        role: 'owner',
        source: storedSource?.source ?? null,
      },
    },
  });
  AuthView.setSignUpLoading(false);
  if (error) {
    const msg = /character of each|abcdefghijk/i.test(error.message)
      ? 'Password must include uppercase, lowercase, a number, and a special character.'
      : error.message;
    AuthView.showSignUpError(msg);
    return;
  }
  // Supabase returns identities: [] (empty) when the email is already registered.
  // No error is thrown and no confirmation email is sent, leaving the user confused.
  if (data.user?.identities?.length === 0) {
    AuthView.showSignUpError('An account with this email already exists. Please sign in instead.');
    return;
  }
  AuthView.showCheckEmail(email);
};

// Source-tracking funnel: fired once, the first time the visitor types into the sign-up
// form. A no-op when there's no tracked source (organic signup - nothing to attribute).
const controlSourceFormStarted = function () {
  const stored = sourceTracking.getStoredSource();
  if (!stored || sourceTracking.hasFiredEvent('form_started')) return;
  sourceTracking.markEventFired('form_started');
  model.logRegistrationEvent('form_started', {
    sourceId: stored.source,
    sessionId: sourceTracking.getOrCreateVisitorId(),
  });
};

const _handleStaffDeactivated = async function () {
  // Flag survives the reload so the login screen can explain why they were kicked out
  sessionStorage.setItem('pointbunny_deactivated', '1');
  await supabase.auth.signOut();
  window.location.reload();
};

// An owner changed this logged-in user's role. We keep them signed in but force a
// clean reload so initApp re-reads the new role and re-applies permission gating
// (hot-swapping role in already-rendered UI is error-prone). The flag lets us
// confirm with a toast after the reload.
let _roleChangeReloadPending = false;
const _handleStaffRoleChanged = function () {
  if (_roleChangeReloadPending) return;
  _roleChangeReloadPending = true;
  sessionStorage.setItem('pointbunny_role_changed', '1');
  showToast('Your access level was updated. Reloading…', 'info');
  setTimeout(() => window.location.reload(), 1400);
};

const controlSignOut = async function () {
  const sweepEl = document.createElement('div');
  sweepEl.className = 'app-exit-sweep';
  sweepEl.innerHTML = '<span class="app-exit-label">Signing out…</span>';
  document.body.appendChild(sweepEl);
  await destroyAnalytics();
  await supabase.auth.signOut();
  setTimeout(() => window.location.reload(), 500);
};

const controlForgotPassword = async function (email, captchaToken) {
  try {
    AuthView.setForgotLoading(true);
    await model.sendPasswordResetEmail(email, captchaToken);
    AuthView.showForgotSent(email);
  } catch (err) {
    AuthView.showForgotError(err.message ?? 'Something went wrong. Please try again.');
    AuthView.setForgotLoading(false);
  }
};

// Password reset is handled entirely on the standalone reset.html page (PKCE flow);
// the main app no longer has an in-SPA reset form or controller. See reset.js.

// ── Cashflow ──────────────────────────────────────────────────────────────────

const _getCashflowRange = function (period, from, to) {
  const now = new Date();
  const ymd = (d) => d.toISOString().slice(0, 10);
  const fmt = (d) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const fmtShort = (d) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  if (period === "today") {
    const { start, end } = _getBizDayBounds(now);
    return { startISO: start.toISOString(), endISO: end.toISOString(), label: fmt(now) };
  }

  if (period === "yesterday") {
    const { start, end } = _getBizDayBounds(new Date(Date.now() - 86400000));
    return { startISO: start.toISOString(), endISO: end.toISOString(), label: `Yesterday · ${fmt(start)}` };
  }

  if (period === "week") {
    const day = now.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    const monday = new Date(now); monday.setDate(now.getDate() + diffToMon); monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6); sunday.setHours(23, 59, 59, 999);
    return { startISO: monday.toISOString(), endISO: sunday.toISOString(), label: `${fmtShort(monday)} – ${fmtShort(sunday)}` };
  }

  if (period === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { startISO: start.toISOString(), endISO: end.toISOString(), label: now.toLocaleDateString("en-US", { month: "long", year: "numeric" }) };
  }

  if (period === "year") {
    const start = new Date(now.getFullYear(), 0, 1);
    const end   = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
    return { startISO: start.toISOString(), endISO: end.toISOString(), label: String(now.getFullYear()) };
  }

  // custom — append T00:00:00 (no Z) so the string is parsed as LOCAL midnight,
  // not UTC midnight (which lands on the previous calendar day in UTC- timezones).
  const start = new Date(from + 'T00:00:00');
  const end   = new Date(to   + 'T00:00:00'); end.setHours(23, 59, 59, 999);
  return { startISO: start.toISOString(), endISO: end.toISOString(), label: `${fmtShort(start)} – ${fmtShort(end)}` };
};

const _cashflowSummary = () => {
  const gross    = modelState.cashflowSales.reduce((s, r) => s + Number(r.total_price), 0);
  const expenses = modelState.expenses.reduce((s, e) => s + e.amount, 0);
  return { gross, expenses, net: gross - expenses };
};

const _cashflowCanEdit = () => model.hasPermission('manage_cashflow');

// ── Cashflow period cache ─────────────────────────────────────────────────────
// Stores fetched results per period key so switching periods doesn't re-fetch.
// 'today' is intentionally never cached — it changes throughout the day.
// The entire cache is cleared on any data mutation (new sale, void, expense).
const _cashflowCache = new Map();
const _cfCacheKey = (period, from, to) =>
  period === 'custom' ? `custom:${from}:${to}` : period;
const _clearCashflowCache = () => _cashflowCache.clear();
const _staffCanManage  = () => model.hasPermission('manage_staff');

// Applies role-based gating to the static DOM after login. Two mechanisms:
//   [data-perm="key"]         → hidden entirely when the role lacks the capability
//                               (nav into areas the role can't enter)
//   [data-perm-disable="key"] → kept visible but disabled + a "no access" title
//                               (controls inside a panel the role can partly use)
// This is cosmetic UX only; every open handler re-checks hasPermission and RLS
// enforces at the database. Re-run after injecting gated markup at runtime.
const _ROLE_RANK = { Cashier: 1, Manager: 2, Admin: 3 };
const _applyPermissionGates = function (root = document) {
  root.querySelectorAll('[data-perm]').forEach((el) => {
    el.classList.toggle('perm-hidden', !model.hasPermission(el.dataset.perm));
  });
  // Legacy tier gate for the few settings/display toggles that don't map to a named
  // capability (e.g. the customer-display ad image, manager+). Hidden when the
  // current role ranks below the element's minimum role.
  root.querySelectorAll('[data-min-role]').forEach((el) => {
    const need = _ROLE_RANK[el.dataset.minRole.charAt(0).toUpperCase() + el.dataset.minRole.slice(1)] ?? 99;
    const have = _ROLE_RANK[model.state.role] ?? 0;
    el.classList.toggle('perm-hidden', have < need);
  });
  root.querySelectorAll('[data-perm-disable]').forEach((el) => {
    const denied = !model.hasPermission(el.dataset.permDisable);
    el.classList.toggle('perm-disabled', denied);
    el.setAttribute('aria-disabled', String(denied));
    if (el.matches('button, input, select, textarea, fieldset')) el.disabled = denied;
    if (denied && el.dataset.permMsg) el.title = el.dataset.permMsg;
  });
  // Elements that stay visible but lose their click-through action without the
  // capability (e.g. dashboard stat cards that shortcut into Reports). We strip the
  // interactive affordance so they no longer look clickable; the bound listener is
  // already a no-op without the permission.
  root.querySelectorAll('[data-perm-action]').forEach((el) => {
    const denied = !model.hasPermission(el.dataset.permAction);
    el.classList.toggle('perm-inert', denied);
    if (denied) {
      el.removeAttribute('role');
      el.removeAttribute('tabindex');
      el.removeAttribute('title');
    } else {
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
    }
  });
};

let _currentReportsPeriod = { period: 'today' };
let _currentPrevTotals = null;
let _selectedOverviewMetrics = new Set();
let _overviewTimeSeries = null;
const METRIC_COLORS = { revenue: '#22c55e', expenses: '#ef4444', net: '#3b82f6', transactions: '#8b5cf6', avgOrder: '#f59e0b' };
const METRIC_LABELS = { revenue: 'Gross Income', expenses: 'Expenses', net: 'Net Income', transactions: 'Transactions', avgOrder: 'Avg. Order' };
const _reportsExpenseSummary = () => {
  const gross    = (modelState.cashflowSales ?? []).reduce((s, r) => s + Number(r.total_price), 0);
  const expenses = (modelState.expenses      ?? []).reduce((s, e) => s + e.amount, 0);
  return { gross, expenses, net: gross - expenses };
};

let _currentCashflowPeriod = { period: "today" };

const _renderCashflowLists = () => {
  const canEdit = _cashflowCanEdit();
  CashflowView.renderSalesList(modelState.cashflowSales, canEdit);
  CashflowView.renderExpensesList(modelState.expenses, canEdit);
  CashflowView.renderVoidedList(modelState.voidedSales, canEdit);
};

const controlOpenCashflow = async function () {
  if (!model.hasPermission('manage_cashflow')) { showToast("You don't have access to activity.", 'error'); return; }
  CashflowView.open(_cashflowCanEdit());
  CashflowView.renderLoading();
  CashflowView.setLoading(true);
  try {
    _currentCashflowPeriod = { period: "today" };
    const { startISO, endISO, label } = _getCashflowRange("today");
    await model.fetchCashflowData(startISO, endISO);
    CashflowView.setPeriodLabel(label);
    CashflowView.renderSummary(_cashflowSummary());
    _renderCashflowLists();
  } catch (err) {
    showToast(err.message ?? err);
  } finally {
    CashflowView.setLoading(false);
  }
};

const controlCloseCashflow = function () {
  CashflowView.close();
};

const controlChangePeriod = async function ({ period, from, to }) {
  _currentCashflowPeriod = { period, from, to };
  const { startISO, endISO, label } = _getCashflowRange(period, from, to);

  // Cache hit — restore instantly from memory, no network round-trip
  // 'today' is always re-fetched (live data changes throughout the day)
  const key = _cfCacheKey(period, from, to);
  const cached = period !== 'today' ? _cashflowCache.get(key) : null;
  if (cached) {
    modelState.cashflowSales = cached.sales;
    modelState.expenses      = cached.expenses;
    modelState.voidedSales   = cached.voidedSales;
    CashflowView.setPeriodLabel(label);
    CashflowView.renderSummary(_cashflowSummary());
    _renderCashflowLists();
    return;
  }

  // Cache miss — fetch from Supabase
  const isHeavy = period === 'year';
  CashflowView.renderLoading();
  CashflowView.setLoading(true);
  if (isHeavy) CashflowView.setHeavyLoadNotice(true);
  try {
    await model.fetchCashflowData(startISO, endISO);
    // Store in cache (never cache 'today' — it changes with every new sale)
    if (period !== 'today') {
      _cashflowCache.set(key, {
        sales:       modelState.cashflowSales,
        expenses:    modelState.expenses,
        voidedSales: modelState.voidedSales,
      });
    }
    CashflowView.setPeriodLabel(label);
    CashflowView.renderSummary(_cashflowSummary());
    _renderCashflowLists();
  } catch (err) {
    showToast(err.message ?? err);
  } finally {
    CashflowView.setLoading(false);
    if (isHeavy) CashflowView.setHeavyLoadNotice(false);
  }
};

const _refreshReportsExpenses = () => {
  if (document.getElementById("reportsPanel")?.classList.contains("hidden")) return;
  ReportsView.renderExpenseKpis(_reportsExpenseSummary());
};

const controlAddExpense = async function (data) {
  try {
    CashflowView.setSubmitting(true);
    await model.addExpense(data);
    _clearCashflowCache();
    CashflowView.hideExpenseModal();
    CashflowView.renderSummary(_cashflowSummary());
    CashflowView.renderExpensesList(modelState.expenses, _cashflowCanEdit());
    CashflowView.scrollExpensesToTop();
    _refreshReportsExpenses();
    _refreshHomeExpensesToday();
    _requestActivityRefresh();
    showToast("Expense added.", "success");
  } catch (err) {
    showToast(err.message ?? err);
  } finally {
    CashflowView.setSubmitting(false);
  }
};

// ── Timezone-aware date helpers ───────────────────────────────────────────────
// Returns { start: Date, end: Date } for the calendar day that contains `date`
// in the business's configured IANA timezone (falls back to browser timezone).
const _dayBoundsInTz = function (date, tz) {
  // Format the date as "YYYY-MM-DD HH:mm:ss" in the target timezone (sv-SE gives ISO-style output)
  const dtStr = new Intl.DateTimeFormat('sv-SE', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).format(date).replace(' ', 'T');
  // Compute UTC offset by comparing tz-local time (treated as UTC) vs actual UTC ms
  const offsetMs = date.getTime() - new Date(dtStr + 'Z').getTime();
  const dayStr = dtStr.slice(0, 10); // "YYYY-MM-DD"
  const startMs = new Date(dayStr + 'T00:00:00Z').getTime() + offsetMs;
  return { start: new Date(startMs), end: new Date(startMs + 86400000 - 1) };
};

const _getBizDayBounds = function (date = new Date()) {
  const tz = model.state.businessTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  return _dayBoundsInTz(date, tz);
};

const _isToday = (isoStr) => {
  const d = new Date(isoStr);
  const { start, end } = _getBizDayBounds();
  return d >= start && d <= end;
};

const _executeVoid = async function (id, voidedBy) {
  const { wasInQueue } = await model.voidSale(id, voidedBy);
  // Restore the stock this sale consumed by replaying its own adjustment log
  // rows (exact even if the recipe changed since; no-op for pre-inventory
  // sales). Fire-and-forget - the void itself is already committed.
  model.reverseInventoryConsumption(id, voidedBy)
    .then(() => model.loadInventory())
    .then(() => {
      NewOrderView.updateStockBadges(model.productStockStatuses());
      channel.postMessage({ type: MSG.INVENTORY_SYNC });
    })
    .catch(() => {});
  // Return the sale's loyalty points (earned taken back, redeemed refunded) by
  // replaying its own ledger rows. Server-side no-op for sales without loyalty
  // rows, so this is safe to call unconditionally. Fire-and-forget.
  model.reverseLoyaltyForSale(id).catch(() => {});
  _clearCashflowCache();
  KDSView.renderQueue(modelState.orderQueue);
  channel.postMessage({ type: MSG.KDS_ORDER_VOIDED, id });
  CashflowView.renderSummary(_cashflowSummary());
  _renderCashflowLists();
  const voided = modelState.voidedSales.find(s => s.id === id);
  if (voided && _isToday(voided.sale_date)) {
    refreshTodaySalesDisplay();
    if (wasInQueue && _todayTransactionCount !== null) { _todayTransactionCount = Math.max(0, _todayTransactionCount - 1); _updateTransactionBadge(); }
  }
  _requestActivityRefresh();
  showToast('Transaction voided.', 'success');
};

const controlVoidTransaction = function (id) {
  const doVoid = async (voidedBy) => {
    try { await _executeVoid(id, voidedBy); }
    catch (err) { showToast(err.message ?? err); }
  };

  if (model.hasPermission('void_sale')) {
    CashflowView.showConfirmModal({
      message: 'Void this transaction? It will be removed from reports and the active queue. This cannot be undone.',
      confirmLabel: 'Void',
      cancelLabel: 'Cancel',
      onConfirm: () => doVoid([modelState.currentStaff?.firstName, modelState.currentStaff?.lastName].filter(Boolean).join(' ') || 'Admin'),
    });
  } else {
    CashflowView.showOverrideModal(async (email, password) => {
      try {
        const captchaToken = await getTokenInteractive();
        const staffName = await model.verifyOverrideCredentials(email, password, 'void_sale', captchaToken);
        CashflowView.hideOverrideModal();
        await _executeVoid(id, staffName);
      } catch (err) {
        CashflowView.setOverrideError(err.message ?? 'Authorization failed.');
      }
    });
  }
};


const _generateAndDownloadCSV = function () {
  const periodLabel = document.querySelector("#cashflowPeriodLabel")?.textContent ?? "cashflow";
  const safeLabel = periodLabel.replace(/[^a-z0-9]/gi, "-").toLowerCase();

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  // A cell starting with = + - @ (or a tab/CR) is a live formula to Excel/Sheets
  // even inside quotes - prefix with ' so a malicious item/customer name (e.g.
  // from CSV import) can't execute as a formula when this export is reopened.
  const escape = (val) => {
    let s = String(val ?? "");
    // A real signed amount ("+123.45"/"-25.00") must stay a plain number so
    // Excel/Sheets can still sum the column - only guard non-numeric formula shapes.
    if (/^[=+\-@\t\r]/.test(s) && !Number.isFinite(Number(s))) s = `'${s}`;
    return `"${s.replace(/"/g, '""')}"`;
  };

  const salesRows = (modelState.cashflowSales ?? []).map((s) => ({
    date: s.sale_date,
    row: [
      fmtDate(s.sale_date), fmtTime(s.sale_date), "Sale",
      (s.items ?? []).map((i) => `${i.itemName} x${i.quantity}`).join("; "),
      "", `+${Number(s.total_price).toFixed(2)}`,
      s.is_manual ? "Yes" : "No",
      s.added_by ?? "",
    ],
  }));

  const expenseRows = (modelState.expenses ?? []).map((e) => ({
    date: e.expenseDate,
    row: [fmtDate(e.expenseDate), fmtTime(e.expenseDate), "Expense", e.description, e.category ?? "", `-${e.amount.toFixed(2)}`, "No", e.createdBy],
  }));

  const allRows = [...salesRows, ...expenseRows]
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map((r) => r.row);

  const header = ["Date", "Time", "Type", "Description", "Category", "Amount", "Manual Entry", "Added By"];
  const csv = [header, ...allRows].map((row) => row.map(escape).join(",")).join("\n");

  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cashflow-${safeLabel}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const controlExportCSV = function () {
  const sales = modelState.cashflowSales ?? [];
  const expenses = modelState.expenses ?? [];
  const periodLabel = document.querySelector("#cashflowPeriodLabel")?.textContent ?? "this period";

  if (!sales.length && !expenses.length) {
    showToast("No data to export for this period.", "info");
    return;
  }

  const salesLine = sales.length ? `${sales.length} sale${sales.length !== 1 ? "s" : ""}` : "";
  const expenseLine = expenses.length ? `${expenses.length} expense${expenses.length !== 1 ? "s" : ""}` : "";
  const summary = [salesLine, expenseLine].filter(Boolean).join(" and ");

  CashflowView.showConfirmModal({
    message: `Export ${summary} for "${periodLabel}" as a CSV file?`,
    confirmLabel: "Download CSV",
    cancelLabel: "Cancel",
    onConfirm: _generateAndDownloadCSV,
  });
};

const controlReprintSale = async function (sale) {
  const mapped = {
    date: sale.sale_date,
    receiptNumber: sale.receipt_number ?? null,
    showInvoiceNumber: modelState.settings.showInvoiceNumber !== false,
    ticketNumber: sale.ticket_number ?? null,
    showTicketNumber: modelState.settings.showTicketNumber !== false,
    taxInclusiveRate: sale.tax_inclusive_rate ?? null,
    orderType: sale.order_type ?? null,
    items: sale.items ?? [],
    adjustments: (sale.adjustments ?? [])
      .filter((a) => !a.removed)
      .map((a) => ({ ...a, computedAmount: a.computedAmount ?? a.computed_amount })),
    totalPrice: sale.total_price,
    customerPayment: sale.customer_payment,
    customerChange: sale.customer_change,
    paymentMethod: sale.payment_method ?? 'cash',
    subtotal: sale.subtotal,
    storeName:       modelState.businessName ?? modelState.username,
    businessPhone:   modelState.businessPhone   ?? null,
    addressStreet:   modelState.businessAddressStreet   ?? null,
    addressCity:     modelState.businessAddressCity     ?? null,
    addressProvince: modelState.businessAddressProvince ?? null,
    addressZip:      modelState.businessAddressZip      ?? null,
    addressCountry:  modelState.businessAddressCountry  ?? null,
    cashierName: sale.added_by,
    showCashier: modelState.settings.showCashierName !== false,
    showRemovedAdjustments: false,
    removedAdjustments: [],
  };
  if (btPrinter.isConnected()) {
    _printToBluetooth(mapped);
    return;
  }
  const promise = ReceiptView.print(mapped, { width: btPrinter.paperWidth() });
  if (!promise) showToast('Popup was blocked. Allow popups for this site and try again.', 'error');
};

const controlOpenSaleReceipt = function (id) {
  const sale = modelState.cashflowSales.find((s) => s.id === id)
    ?? modelState.voidedSales.find((s) => s.id === id);
  if (sale) CashflowView.showSaleReceipt(sale, () => controlReprintSale(sale));
};

const controlDeleteExpense = async function (id) {
  try {
    await model.deleteExpense(id);
    _clearCashflowCache();
    CashflowView.renderSummary(_cashflowSummary());
    CashflowView.renderExpensesList(modelState.expenses, _cashflowCanEdit());
    _refreshReportsExpenses();
    _refreshHomeExpensesToday();
    _requestActivityRefresh();
    showToast("Expense deleted.", "success");
  } catch (err) {
    showToast(err.message ?? err);
  }
};

// ── Discounts ─────────────────────────────────────────────────────────────────

const controlOpenDiscounts = function () {
  if (!model.hasPermission('manage_adjustments')) { showToast("You don't have access to receipt adjustments.", 'error'); return; }
  DiscountView.open();
  DiscountView.renderAdjustments(model.state.settings.adjustments);
  DiscountView.syncTaxMode(model.state.settings.taxMode, model.state.settings.taxInclusiveRate, model.state.settings.taxInclusiveLabel, model.state.settings.taxExemptLabel);
  DiscountView.renderExemptProducts(model.state.menuItems, model.state.menuCategories);
  // "Set up taxes & fees" (Getting Started) checks off when the owner
  // actually opens this panel - write-once.
  if (model.hasPermission('manage_business') && !model.state.settings.taxSetupSeen) {
    model.setBusinessSetting('taxSetupSeen', true);
    _updateGettingStarted();
  }
};

// Warn before closing with an unsaved Tax Settings draft so the back arrow
// can't silently throw away a half-finished mode/rate/name change - mirrors
// the Settings modal's own unsaved-changes exit guard.
const controlCloseDiscounts = function () {
  if (!_taxSettingsDraftOriginal) { DiscountView.close(); return; }
  DiscountView.showConfirmModal({
    message: 'You have unsaved tax settings.',
    note: 'If you leave now, your changes will be discarded.',
    confirmLabel: 'Discard & Close',
    cancelLabel: 'Keep Editing',
    onConfirm: () => {
      controlDiscardTaxSettingsDraft();
      DiscountView.close();
    },
  });
};

// Deep link from the Edit Item modal's read-only Tax section - exemption is
// now selected centrally here instead of per-item.
const controlManageItemTax = function (itemId) {
  controlOpenDiscounts();
  DiscountView.focusExemptProduct(itemId);
};

// Diffs the picker's checked set against current tax_class so Save only
// writes products that actually changed.
const controlSaveExemptProducts = async function (exemptIds) {
  if (!model.hasPermission('manage_adjustments')) { showToast("You don't have access to receipt adjustments.", 'error'); return; }
  const exemptSet = new Set(exemptIds);
  const toExempt = model.state.menuItems.filter((i) => exemptSet.has(i._id) && i.taxClass !== 'exempt').map((i) => i._id);
  const toStandard = model.state.menuItems.filter((i) => !exemptSet.has(i._id) && i.taxClass === 'exempt').map((i) => i._id);
  if (!toExempt.length && !toStandard.length) { showToast('No changes to save.', 'info'); return; }
  try {
    await Promise.all([
      toExempt.length ? model.bulkUpdateItemTaxClass(toExempt, 'exempt') : Promise.resolve(),
      toStandard.length ? model.bulkUpdateItemTaxClass(toStandard, 'standard') : Promise.resolve(),
    ]);
    DiscountView.renderExemptProducts(model.state.menuItems, model.state.menuCategories);
    const n = toExempt.length + toStandard.length;
    showToast(`Updated ${n} product${n === 1 ? '' : 's'}.`, 'success');
  } catch (err) {
    showToast(err.message ?? err);
  }
};

// ── Promos panel ──────────────────────────────────────────────────────────────

const controlOpenPromos = function () {
  if (!model.hasPermission('manage_discounts')) { showToast("You don't have access to promo codes.", 'error'); return; }
  PromoView.open();
  PromoView.render(model.state.discountCodes);
};

const controlClosePromos = function () {
  PromoView.close();
};

// ── Loyalty panel ─────────────────────────────────────────────────────────────

// Cached per panel open: the customer list and each customer's streak result
// (computed from their non-voided sale dates in the business's timezone).
let _loyaltyCustomers = [];
let _loyaltyStreaks = new Map();

const _loyaltyTz = () =>
  model.state.businessTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

const _computeLoyaltyStreaks = function (customers, saleDatesByCustomer) {
  const tz = _loyaltyTz();
  const map = new Map();
  for (const c of customers) {
    map.set(c.id, computeAllStreaks(saleDatesByCustomer.get(c.id) ?? [], tz));
  }
  return map;
};

const _renderLoyaltyPanel = function () {
  LoyaltyView.renderStats(_loyaltyCustomers, _loyaltyStreaks);
  LoyaltyView.renderCustomers(_loyaltyCustomers, _loyaltyStreaks);
};

const _reloadLoyaltyCustomers = async function () {
  const [customers, saleDates] = await Promise.all([
    model.fetchLoyaltyCustomers(),
    model.fetchLoyaltySaleDates(),
  ]);
  _loyaltyCustomers = customers;
  _loyaltyStreaks = _computeLoyaltyStreaks(customers, saleDates);
  _renderLoyaltyPanel();
};

// Rewards are loaded eagerly at bootstrap (checkout needs them regardless of
// manage_loyalty), so opening the panel just re-renders from state - no fetch.
const _renderLoyaltyRewards = function () {
  const menuItemsById = new Map(model.state.menuItems.map((i) => [i._id, i]));
  LoyaltyView.renderRewards(model.state.loyaltyRewards, menuItemsById);
};

const controlOpenLoyalty = async function () {
  if (!model.hasPermission('manage_loyalty')) { showToast("You don't have access to the loyalty program.", 'error'); return; }
  try {
    LoyaltyView.open();
    LoyaltyView.syncSettings(model.state.settings);
    _renderLoyaltyRewards();
    await _reloadLoyaltyCustomers();
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

const controlCloseLoyalty = function () {
  LoyaltyView.close();
};

const controlSaveLoyaltySettings = async function (values) {
  try {
    for (const [key, value] of Object.entries(values)) {
      model.stageBusinessSetting(key, value);
    }
    await model.persistBusinessSettings();
    LoyaltyView.syncSettings(model.state.settings);
    await _reloadLoyaltyCustomers();
    showToast('Loyalty settings saved.', 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

const controlDiscardLoyaltySettings = function () {
  LoyaltyView.syncSettings(model.state.settings);
};

const controlLoyaltySearch = function (query) {
  const q = query.toLowerCase();
  const digits = query.replace(/\D/g, "");
  const filtered = !q
    ? _loyaltyCustomers
    : _loyaltyCustomers.filter((c) =>
        (c.name ?? "").toLowerCase().includes(q) ||
        (digits && c.phone.includes(digits)),
      );
  LoyaltyView.renderCustomers(filtered, _loyaltyStreaks);
};

const controlOpenLoyaltyCustomer = async function (id) {
  try {
    const customer = _loyaltyCustomers.find((c) => c.id === id);
    if (!customer) return;
    const ledger = await model.fetchLoyaltyLedger(id);
    // All-time cumulative points = every 'earn' row, excluding sales that were
    // later voided (their earn was taken back by a void_reversal).
    const voidedSales = new Set(
      ledger.filter((t) => t.kind === 'void_reversal').map((t) => t.sale_id),
    );
    const allTimePoints = ledger
      .filter((t) => t.kind === 'earn' && !voidedSales.has(t.sale_id))
      .reduce((sum, t) => sum + t.points, 0);
    LoyaltyView.showDetail(customer, ledger, _loyaltyStreaks.get(id), allTimePoints);
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

const controlLoyaltyShowAddForm = function () {
  LoyaltyView.showAddForm();
};

const controlLoyaltyEditCustomer = function (id) {
  const customer = _loyaltyCustomers.find((c) => c.id === id);
  if (customer) LoyaltyView.showAddForm(customer);
};

// Shared save for the Add/Edit Customer form. data.id present = edit
// (profile fields only - phone is the identity key and stays fixed).
const controlLoyaltyAddCustomer = async function (data) {
  try {
    const extras = {
      email: data.email,
      birthdayMonth: data.birthdayMonth,
      birthdayDay: data.birthdayDay,
      note: data.note,
    };
    if (data.id) {
      await model.updateCustomer(data.id, { name: data.name, ...extras });
    } else {
      await model.enrollCustomer(data.phone, data.name, extras, { strict: true });
      // Panel enrollment must not leave a customer linked to the NEXT checkout.
      model.clearLoyaltyCustomer();
    }
    LoyaltyView.closeAddForm();
    await _reloadLoyaltyCustomers();
    if (!data.id) _requestActivityRefresh(); // new-member feed event
    showToast(data.id ? 'Customer updated.' : 'Customer added to the loyalty program.', 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

// ── Rewards card (panel) ──────────────────────────────────────────────────────

const controlLoyaltyRewardSearch = function (query) {
  const excludedItemIds = new Set(model.state.loyaltyRewards.map((r) => r.itemId));
  LoyaltyView._renderRewardSearchResults(query, model.state.menuItems, excludedItemIds);
};

const controlLoyaltyRewardPick = function (itemId, itemName) {
  LoyaltyView.showRewardPicked(itemId, itemName);
};

const controlLoyaltyRewardCancelPick = function () {
  LoyaltyView.hideRewardPicked();
};

const controlAddLoyaltyReward = async function (itemId, pointsCost) {
  try {
    await model.addLoyaltyReward(itemId, pointsCost);
    LoyaltyView.hideRewardPicked();
    _renderLoyaltyRewards();
    showToast('Reward added.', 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

const controlToggleLoyaltyReward = async function (id) {
  try {
    await model.toggleLoyaltyReward(id);
    _renderLoyaltyRewards();
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

const controlDeleteLoyaltyReward = async function (id) {
  try {
    await model.deleteLoyaltyReward(id);
    _renderLoyaltyRewards();
    showToast('Reward removed.', 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

const controlNewDiscountCode = function () {
  if (!model.hasPermission('manage_discounts')) { showToast("You don't have access to discount codes.", 'error'); return; }
  PromoView.showForm();
};

const controlSaveDiscountCode = async function (data) {
  if (!model.hasPermission('manage_discounts')) { showToast("You don't have access to discount codes.", 'error'); return; }
  try {
    if (data.id) {
      await model.updateDiscountCode(data.id, data);
    } else {
      await model.createDiscountCode(data);
    }
    PromoView.closeForm();
    PromoView.render(model.state.discountCodes);
    showToast(data.id ? "Code updated." : "Code created.", "success");
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlEditDiscountCode = function (id) {
  const dc = model.state.discountCodes.find((d) => d.id === id);
  if (!dc) return;
  PromoView.showForm(dc);
};

const controlDeleteDiscountCode = async function (id) {
  if (!model.hasPermission('manage_discounts')) { showToast("You don't have access to discount codes.", 'error'); return; }
  if (!confirm("Delete this promo code? This cannot be undone.")) return;
  try {
    await model.deleteDiscountCode(id);
    PromoView.render(model.state.discountCodes);
    showToast("Code deleted.", "success");
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlToggleDiscountStatus = async function (id) {
  if (!model.hasPermission('manage_discounts')) { showToast("You don't have access to discount codes.", 'error'); return; }
  try {
    await model.toggleDiscountCodeStatus(id);
    PromoView.render(model.state.discountCodes);
  } catch (err) {
    showToast(err.message ?? err);
  }
};

// ── Promo code at checkout ────────────────────────────────────────────────────

const controlApplyPromoCode = function (code) {
  try {
    if (model.state.currentPromoCode) model.removePromoCodeFromReceipt();
    const dc = model.validateDiscountCode(code);
    model.applyPromoCodeToReceipt(dc);
    _refreshCheckoutAdj();
  } catch (err) {
    showToast(err.message ?? err, "error");
  }
};

const controlRemovePromoCode = function () {
  model.removePromoCodeFromReceipt();
  _refreshCheckoutAdj();
};

// ── Loyalty at checkout ───────────────────────────────────────────────────────

const _denyLoyalty = () => showToast("You don't have access to loyalty.", "error");

const controlLoyaltyLookup = async function (phone) {
  if (!model.hasPermission('use_loyalty')) { _denyLoyalty(); return; }
  try {
    const customer = await model.lookupCustomer(phone);
    if (customer) {
      _refreshCheckoutAdj();
    } else {
      // Unknown phone: offer inline enrollment (keeps the normalized digits).
      OrderCheckOutView.showLoyaltyEnroll(phone);
    }
  } catch (err) {
    showToast(err.message ?? err, "error");
  }
};

const controlLoyaltyEnroll = async function (phone, name) {
  if (!model.hasPermission('use_loyalty')) { _denyLoyalty(); return; }
  try {
    await model.enrollCustomer(phone, name);
    _refreshCheckoutAdj();
    _requestActivityRefresh(); // "X joined the loyalty program" feed event
    showToast("Customer enrolled in the loyalty program.", "success");
  } catch (err) {
    showToast(err.message ?? err, "error");
  }
};

const controlLoyaltyEnrollCancel = function () {
  _refreshCheckoutAdj(); // re-renders the idle loyalty section
};

// Everything the Redeem modal needs in one place: customer, streak, points
// balance/reserved, the FULL reward catalog (not just cart-matching items -
// 2026-07-27, Kelly's ask: show every configured reward, greyed out if
// unaffordable, since redeeming now adds the item rather than requiring it
// already be in the cart), and the totals the points-off stepper caps against.
const _buildRedeemModalPayload = function () {
  const ctx = _loyaltyCartCtx();
  const settings = model.state.settings;
  const tz = model.state.businessTimezone ?? undefined;
  const adjResult = model.calculateAdjustments(
    OrderCheckOutView._subtotal,
    model.state.currentReceiptAdjustments,
  );
  const rewards = model.state.loyaltyRewards
    .filter((r) => r.active)
    .map((r) => ({
      id: r.id,
      pointsCost: r.pointsCost,
      itemName: model.state.menuItems.find((mi) => mi._id === r.itemId)?.itemName ?? '(item removed)',
    }));
  return {
    customer: ctx.customer,
    streak: computeStreak(ctx.customer?.saleDates ?? [], 'day', tz),
    rewards,
    redemptionByRewardId: ctx.redemptionByRewardId,
    reservedPoints: ctx.reservedPoints,
    redeemPoints: settings.loyaltyRedeemPoints || 100,
    redeemValue: settings.loyaltyRedeemValue || 5,
    redemption: model.state.currentLoyaltyRedemption,
    finalTotal: adjResult.finalTotal,
    tz,
  };
};

// Redeem/remove actions can happen while the modal is open (that's the whole
// point) - re-render it in place so its own points/eligible-item list stays
// live, on top of the checkout section refresh every mutating action already does.
const _refreshRedeemModalIfOpen = function () {
  if (!OrderCheckOutView.isRedeemModalOpen()) return;
  OrderCheckOutView.showRedeemModal(_buildRedeemModalPayload());
};

const controlOpenLoyaltyRedeemModal = function () {
  if (!model.hasPermission('use_loyalty')) { _denyLoyalty(); return; }
  if (!model.state.currentLoyaltyCustomer) return;
  OrderCheckOutView.showRedeemModal(_buildRedeemModalPayload());
};

const controlLoyaltyApplyRedemption = function (increments) {
  if (!model.hasPermission('use_loyalty')) { _denyLoyalty(); return; }
  try {
    model.applyLoyaltyRedemption(increments);
    _refreshCheckoutAdj();
    _refreshRedeemModalIfOpen();
  } catch (err) {
    showToast(err.message ?? err, "error");
  }
};

const controlLoyaltyRemoveRedemption = function () {
  model.removeLoyaltyRedemptionFromReceipt();
  _refreshCheckoutAdj();
  _refreshRedeemModalIfOpen();
};

const controlLoyaltyUnlink = function () {
  model.clearLoyaltyCustomer();
  OrderCheckOutView.closeRedeemModal();
  OrderCheckOutView._subtotal = model.state.cart.reduce((acc, item) => acc + item.totalPrice, 0);
  _broadcastCart();
  _refreshCheckoutAdj();
};

// rewardId, not a cart line - redeeming now mints its OWN cart line (see
// model.applyLoyaltyRewardRedemption), so the subtotal genuinely changes and
// the cart needs to be re-broadcast to the customer display too.
const controlApplyLoyaltyRewardRedemption = function (rewardId) {
  if (!model.hasPermission('use_loyalty')) { _denyLoyalty(); return; }
  try {
    model.applyLoyaltyRewardRedemption(rewardId);
    OrderCheckOutView._subtotal = model.state.cart.reduce((acc, item) => acc + item.totalPrice, 0);
    _broadcastCart();
    _refreshCheckoutAdj();
    _refreshRedeemModalIfOpen();
  } catch (err) {
    showToast(err.message ?? err, "error");
  }
};

const controlRemoveLoyaltyRewardRedemption = function (cartItemLineId) {
  model.removeLoyaltyRewardRedemption(cartItemLineId);
  OrderCheckOutView._subtotal = model.state.cart.reduce((acc, item) => acc + item.totalPrice, 0);
  _broadcastCart();
  _refreshCheckoutAdj();
  _refreshRedeemModalIfOpen();
};

// ── Staff ─────────────────────────────────────────────────────────────────────

const controlOpenStaff = async function () {
  if (!model.hasPermission('manage_staff')) { showToast("You don't have access to staff.", 'error'); return; }
  try {
    StaffView.open(_staffCanManage());
    // "Set up your team" (Getting Started) checks off when the owner opens
    // this panel - write-once, and deliberately not tied to an invite
    // actually happening (invites may become a paid-tier feature).
    if (model.hasPermission('manage_business') && !model.state.settings.teamSetupSeen) {
      model.setBusinessSetting('teamSetupSeen', true);
      _updateGettingStarted();
    }
    await Promise.all([model.loadStaff(), model.loadRoles()]);
    StaffView.render(model.state.staff, _staffCanManage(), model.state.roles);
    _renderRolesTab();
  } catch (err) {
    showToast(err.message ?? err);
  }
};

// Re-renders the Roles tab with the current user's management context.
const _renderRolesTab = function () {
  const counts = {};
  for (const s of model.state.staff) {
    if (s.roleId && s.isActive !== false) counts[s.roleId] = (counts[s.roleId] ?? 0) + 1;
  }
  StaffView.renderRoles(model.state.roles, {
    canManage: model.hasPermission('manage_roles'),
    ownRoleId: model.state.currentStaff?.roleId ?? null,
    labels:    model.PERMISSION_LABELS,
    totalCaps: model.ALL_PERMISSIONS.length,
    counts,
  });
};

// Create / edit / delete a role from the Roles tab editor. Guardrails: requires
// manage_roles; the Admin role is locked; you can't edit the role you're assigned
// to; and the editor only lets you grant capabilities you hold yourself (enforced
// in the view by disabling non-grantable boxes, and again in the model).
const controlRoleAction = async function (action, roleId) {
  if (!model.hasPermission('manage_roles')) {
    showToast("You don't have access to manage roles.", 'error');
    return;
  }
  const grantableCaps = model.ALL_PERMISSIONS.filter((k) => model.hasPermission(k));
  const editorCtx = {
    allPermissions: model.ALL_PERMISSIONS,
    labels: model.PERMISSION_LABELS,
    descriptions: model.PERMISSION_DESCRIPTIONS,
    groups: model.PERMISSION_GROUPS,
    grantableCaps,
  };

  if (action === 'create') {
    const { onSave } = StaffView.showRoleEditor({ role: null, isNew: true, ...editorCtx });
    onSave(async (name, caps, close, meta) => {
      if (!name) { showToast('Enter a role name.', 'error'); return; }
      try {
        await model.addRole(name, caps, meta?.description, meta?.icon);
        close();
        _renderRolesTab();
        showToast('Role created.', 'success');
      } catch (err) { showToast(err.message ?? 'Could not create role.'); }
    });
    return;
  }

  const role = model.state.roles.find((r) => r.id === roleId);
  if (!role) return;

  if (action === 'edit') {
    if (role.locked) { showToast('The Admin role always has full access and cannot be edited.', 'error'); return; }
    if (roleId === model.state.currentStaff?.roleId) { showToast("You can't edit the role you're currently assigned to.", 'error'); return; }
    const members = model.state.staff.filter(s => s.roleId === roleId && s.isActive !== false);
    const { onSave } = StaffView.showRoleEditor({ role, isNew: false, members, ...editorCtx });
    onSave(async (name, caps, close, meta) => {
      if (!name) { showToast('Enter a role name.', 'error'); return; }
      try {
        if (name !== role.name) await model.renameRole(roleId, name);
        await model.updateRoleMeta(roleId, meta ?? {});
        await model.updateRolePermissions(roleId, caps);
        close();
        _renderRolesTab();
        showToast('Role updated.', 'success');
      } catch (err) { showToast(err.message ?? 'Could not update role.'); }
    });
    return;
  }

  if (action === 'delete') {
    if (role.locked) { showToast('The Admin role cannot be deleted.', 'error'); return; }
    if (!confirm(`Delete the "${role.name}" role? Staff assigned to it must be reassigned first.`)) return;
    try {
      await model.deleteRole(roleId);
      _renderRolesTab();
      showToast('Role deleted.', 'success');
    } catch (err) { showToast(err.message ?? 'Could not delete role.'); }
  }
};

const controlCloseStaff = function () {
  StaffView.close();
};

// ── Timesheets (top-level panel; formerly the Staff panel's Payroll tab) ──────

const controlOpenTimesheets = async function () {
  // manage_pay holders run payroll; manage_staff holders kept their access from
  // when this lived inside the Staff panel. Everyone else is view-blocked (staff
  // see their own hours on the time clock's My Timesheet instead).
  if (!model.hasPermission('manage_pay') && !model.hasPermission('manage_staff')) {
    showToast("You don't have access to timesheets.", 'error');
    return;
  }
  try {
    TimesheetView.open();
    try { await model.loadStaff(); } catch (_) { /* names fall back to shift rows */ }
    const d = new Date();
    await controlFetchTimesheets('week', `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlCloseTimesheets = function () {
  TimesheetView.close();
};

// ── Inventory ─────────────────────────────────────────────────────────────────

let _inventoryHistoryFilter = null;

// One snapshot shape shared by every Inventory render/refresh path.
const _inventorySnapshot = function () {
  return {
    items: modelState.inventoryItems,
    links: modelState.inventoryLinks,
    adjustments: modelState.inventoryAdjustments,
    menuItems: modelState.menuItems,
    suppliers: modelState.suppliers,
    itemSuppliers: modelState.itemSuppliers,
    purchaseOrders: modelState.purchaseOrders,
    inventoryEnabled: model.state.settings.inventoryEnabled !== false,
    historyFilter: _inventoryHistoryFilter,
    invoiceLabelFor: (adj) => formatInvoiceNumber(adj.receiptNumber, adj.saleDate),
  };
};

const controlOpenInventory = async function () {
  if (!model.hasPermission('manage_inventory')) {
    showToast("You don't have access to inventory.", 'error');
    return;
  }
  try {
    InventoryView.open();
    // "Set up inventory" (Getting Started) checks off when the owner opens
    // this panel - write-once, view-based like the taxes/receipt/team steps.
    if (model.hasPermission('manage_business') && !model.state.settings.inventorySetupSeen) {
      model.setBusinessSetting('inventorySetupSeen', true);
      _updateGettingStarted();
    }
    _inventoryHistoryFilter = null;
    await Promise.all([
      model.loadInventory(),
      model.loadInventoryAdjustments().catch(() => []),
      model.loadSuppliers().catch(() => []),
      model.loadPurchaseOrders().catch(() => []),
    ]);
    InventoryView.render(_inventorySnapshot());
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlCloseInventory = function () {
  InventoryView.close();
};

const controlAdjustStock = async function (itemId, delta, note) {
  try {
    const item = await model.adjustInventory(itemId, delta, note);
    if (item) InventoryView.patchItemRow(item);
    // History (and other terminals) refresh from the DB, not from guesswork.
    model.loadInventoryAdjustments(_inventoryHistoryFilter).then(() => {
      InventoryView.refreshIfOpen(_inventorySnapshot());
    }).catch(() => {});
    channel.postMessage({ type: MSG.INVENTORY_SYNC });
    _requestActivityRefresh();
  } catch (err) {
    showToast(err.message ?? err);
  }
};

// onSaved: optional callback with the created item - the PO form's quick-add
// uses it to select the new item in place (after the panel re-render, so the
// fresh item list is already in the view's snapshot).
const controlCreateInventoryItem = async function (data, onSaved) {
  try {
    const item = await model.createInventoryItem(data);
    InventoryView.render(_inventorySnapshot());
    channel.postMessage({ type: MSG.INVENTORY_SYNC });
    showToast('Inventory item added.', 'success');
    onSaved?.(item);
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlUpdateInventoryItem = async function (id, data) {
  try {
    await model.updateInventoryItem(id, data);
    InventoryView.render(_inventorySnapshot());
    channel.postMessage({ type: MSG.INVENTORY_SYNC });
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlDeleteInventoryItem = async function (id) {
  try {
    await model.deleteInventoryItem(id);
    // Removing an item can change which products count as out of stock.
    model.loadInventoryAdjustments(_inventoryHistoryFilter).catch(() => {});
    InventoryView.render(_inventorySnapshot());
    channel.postMessage({ type: MSG.INVENTORY_SYNC });
    showToast('Inventory item deleted.', 'success');
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlInventoryHistoryFilter = async function (itemId) {
  _inventoryHistoryFilter = itemId;
  try {
    await model.loadInventoryAdjustments(itemId);
    InventoryView.render(_inventorySnapshot());
  } catch (err) {
    showToast(err.message ?? err);
  }
};

// ── Inventory: master on/off toggle ───────────────────────────────────────────
// Business-level (server-authoritative). Off = sales stop deducting stock and
// every low/out-of-stock signal is muted (order chips, heads-up modal,
// activity feed). Data and history are untouched. Persists instantly - an
// explicit switch, not a passive settings draft.
const controlToggleInventoryEnabled = function (enabled) {
  model.setBusinessSetting('inventoryEnabled', enabled);
  InventoryView.refreshIfOpen(_inventorySnapshot());
  NewOrderView.updateStockBadges(model.productStockStatuses());
  channel.postMessage({ type: MSG.INVENTORY_SYNC });
  showToast(enabled ? 'Inventory tracking is on.' : 'Inventory tracking is off. Sales will not lower stock.', 'success');
};

// ── Inventory: product links from the item drawer ("Used by") ────────────────

const _afterLinkChange = function () {
  InventoryView.refreshIfOpen(_inventorySnapshot());
  NewOrderView.updateStockBadges(model.productStockStatuses());
  channel.postMessage({ type: MSG.INVENTORY_SYNC });
};

const controlAddProductLink = async function (menuItemId, inventoryItemId, qty, overrides = []) {
  try {
    await model.addInventoryLink(menuItemId, inventoryItemId, qty, overrides);
    _afterLinkChange();
    showToast(overrides.length ? 'Product connected with per-variant amounts.' : 'Product connected.', 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

const controlUpdateProductLink = async function (linkId, qty) {
  try {
    await model.updateInventoryLinkQty(linkId, qty);
    _afterLinkChange();
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

const controlRemoveProductLink = async function (linkId) {
  try {
    await model.removeInventoryLink(linkId);
    _afterLinkChange();
    showToast('Product unlinked.', 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

// Variant overrides are edited in the product editor - deep-link there
// (catalogue opens over the inventory panel; closing it returns here).
const controlEditLinkedProduct = function (menuItemId) {
  controlMenuList();
  controlShowEditMenu(menuItemId);
};

// ── Item <-> supplier price list (edited from BOTH the item drawer and the
//    supplier drawer - one table underneath, always in sync) ─────────────────

const _afterItemSupplierChange = function () {
  InventoryView.refreshIfOpen(_inventorySnapshot());
  channel.postMessage({ type: MSG.INVENTORY_SYNC });
};

const controlAddItemSupplier = async function (itemId, supplierId, unitCost) {
  try {
    await model.addItemSupplier(itemId, supplierId, unitCost);
    _afterItemSupplierChange();
    showToast('Supplier linked.', 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

const controlSetPreferredSupplier = async function (rowId) {
  try {
    await model.setPreferredItemSupplier(rowId);
    _afterItemSupplierChange();
    showToast('Preferred supplier updated.', 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

const controlUpdateSupplierCost = async function (rowId, unitCost) {
  try {
    await model.updateItemSupplierCost(rowId, unitCost);
    _afterItemSupplierChange();
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

const controlRemoveItemSupplier = async function (rowId) {
  try {
    await model.removeItemSupplier(rowId);
    _afterItemSupplierChange();
    showToast('Supplier unlinked.', 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

// ── Suppliers ─────────────────────────────────────────────────────────────────

const controlSaveSupplier = async function (id, data) {
  try {
    const supplier = id ? await model.updateSupplier(id, data) : await model.createSupplier(data);
    // Reconcile the form's "items they supply" rows against the shared
    // item<->supplier price list: remove unlisted, update changed prices,
    // add new (preferred/promote logic lives in the model).
    if (Array.isArray(data.supplyItems)) {
      const desired = new Map(data.supplyItems.map((r) => [r.inventoryItemId, r.unitCost]));
      const existing = [...model.state.itemSuppliers.filter((r) => r.supplierId === supplier.id)];
      for (const row of existing) {
        if (!desired.has(row.inventoryItemId)) {
          await model.removeItemSupplier(row.id);
        } else {
          const raw = desired.get(row.inventoryItemId);
          const parsed = raw === '' || raw == null || isNaN(Number(raw)) ? null : Number(raw);
          if (parsed !== row.unitCost) await model.updateItemSupplierCost(row.id, raw);
          desired.delete(row.inventoryItemId);
        }
      }
      for (const [itemId, cost] of desired) {
        await model.addItemSupplier(itemId, supplier.id, cost);
      }
    }
    InventoryView.refreshIfOpen(_inventorySnapshot());
    channel.postMessage({ type: MSG.INVENTORY_SYNC });
    showToast(id ? 'Supplier updated.' : 'Supplier added.', 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

const controlDeleteSupplier = async function (id) {
  try {
    await model.deleteSupplier(id);
    InventoryView.refreshIfOpen(_inventorySnapshot());
    channel.postMessage({ type: MSG.INVENTORY_SYNC });
    showToast('Supplier deleted. Items keep their history.', 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

// ── Purchase orders ───────────────────────────────────────────────────────────

// Optional receipt photo: upload first (5 MB guarded pipeline) so the PO row
// is written with its URL. An upload failure aborts with a clear toast - the
// user can retry or remove the file.
const _resolvePoReceipt = async function (data) {
  if (!data.receiptFile) return data;
  const receiptUrl = await model.uploadPoReceipt(data.receiptFile);
  return { ...data, receiptUrl };
};

const controlCreatePurchaseOrder = async function (data) {
  try {
    data = await _resolvePoReceipt(data);
    const po = await model.createPurchaseOrder(data);
    const label = `PO-${String(po.poNumber).padStart(3, '0')}`;
    // Where the order already is: draft (default), sent (awaiting delivery),
    // or received (log-after-the-fact - stock goes up right away).
    if (data.initialStatus === 'sent') {
      await model.setPurchaseOrderStatus(po.id, 'sent');
      InventoryView.refreshIfOpen(_inventorySnapshot());
      channel.postMessage({ type: MSG.INVENTORY_SYNC });
      showToast(`${label} created - waiting for delivery.`, 'success');
    } else if (data.initialStatus === 'received') {
      await controlReceivePurchaseOrder(po.id); // handles reloads, badges, toast
    } else {
      InventoryView.refreshIfOpen(_inventorySnapshot());
      channel.postMessage({ type: MSG.INVENTORY_SYNC });
      showToast(`${label} saved - for approval.`, 'success');
    }
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

const controlUpdatePurchaseOrder = async function (id, data) {
  try {
    data = await _resolvePoReceipt(data);
    await model.updatePurchaseOrder(id, data);
    InventoryView.refreshIfOpen(_inventorySnapshot());
    channel.postMessage({ type: MSG.INVENTORY_SYNC });
    showToast('Purchase order updated.', 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

const controlSetPoStatus = async function (id, status) {
  try {
    await model.setPurchaseOrderStatus(id, status);
    InventoryView.refreshIfOpen(_inventorySnapshot());
    channel.postMessage({ type: MSG.INVENTORY_SYNC });
    if (status === 'sent') showToast('Order approved - waiting for delivery. Order it with your supplier however you normally do.', 'success');
    if (status === 'cancelled') showToast('Purchase order cancelled.', 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

// Whole-PO receive: RPC adds every line to stock atomically, then everything
// that shows stock refreshes (panel, order-screen badges, other terminals,
// activity feed).
const controlReceivePurchaseOrder = async function (id) {
  try {
    await model.receivePurchaseOrder(id);
    await Promise.all([
      model.loadInventory(),
      model.loadPurchaseOrders(),
      model.loadInventoryAdjustments(_inventoryHistoryFilter).catch(() => []),
    ]);
    InventoryView.refreshIfOpen(_inventorySnapshot());
    NewOrderView.updateStockBadges(model.productStockStatuses());
    channel.postMessage({ type: MSG.INVENTORY_SYNC });
    _requestActivityRefresh();
    const po = model.state.purchaseOrders.find((p) => p.id === id);
    showToast(`PO-${String(po?.poNumber ?? 0).padStart(3, '0')} received. Stock updated.`, 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

// ── Stock counts ──────────────────────────────────────────────────────────────

const controlApplyStockCount = async function (lines) {
  try {
    await model.applyStockCount(lines);
    await Promise.all([
      model.loadInventory(),
      model.loadInventoryAdjustments(_inventoryHistoryFilter).catch(() => []),
    ]);
    InventoryView.refreshIfOpen(_inventorySnapshot());
    NewOrderView.updateStockBadges(model.productStockStatuses());
    channel.postMessage({ type: MSG.INVENTORY_SYNC });
    _requestActivityRefresh();
    showToast(`Stock count applied. ${lines.length} item${lines.length === 1 ? '' : 's'} updated.`, 'success');
  } catch (err) {
    showToast(err.message ?? err, 'error');
  }
};

// Home sidebar "Time Clock" shortcut: managers land on the Timesheets panel's
// Time Clock section (device setup, kiosk link, how-it-works, who's timed in).
// Staff without panel access just get the kiosk page itself.
const controlOpenTimeclockHub = function () {
  if (model.hasPermission('manage_pay') || model.hasPermission('manage_staff')) {
    TimesheetView.setSection('timeclock');
    controlOpenTimesheets();
  } else {
    // Clean /timeclock URL via the Netlify rewrite; the dev server has no
    // rewrites, so localhost opens the .html directly.
    window.open(location.hostname === 'localhost' ? 'timeclock.html' : '/timeclock', '_blank');
  }
};

const _maybeShowPinSetup = function () {
  if (!model.state.currentStaff || model.state.currentStaff.hasPin) return;

  const name = model.state.currentStaff.firstName || 'there';
  const el = document.createElement('div');
  el.className = 'cashier-picker-overlay';
  el.style.zIndex = '99999';
  const card = document.createElement('div');
  card.className = 'cashier-picker-card';
  card.style.maxWidth = '340px';
  el.appendChild(card);
  document.body.appendChild(el);

  const renderCreate = () => {
    card.innerHTML = `
      <div class="cashier-picker-header" style="border-bottom:none;padding-bottom:0">
        <div>
          <h3 class="cashier-picker-title">Set Your PIN</h3>
          <p class="cashier-picker-subtitle">Hi ${esc(name)}! Create a 6-digit PIN to use at the register.</p>
        </div>
      </div>
      <div class="cashier-pin-screen">
        <div class="cashier-pin-dots" id="pinSetupDots">
          ${'<span class="cashier-pin-dot"></span>'.repeat(6)}
        </div>
        <p class="cashier-pin-error hidden" id="pinSetupError">PINs don't match. Try again.</p>
        <div class="cashier-numpad">
          ${[1,2,3,4,5,6,7,8,9].map(n => `<button class="cashier-numpad-btn" data-key="${n}">${n}</button>`).join('')}
          <div></div>
          <button class="cashier-numpad-btn" data-key="0">0</button>
          <button class="cashier-numpad-btn cashier-numpad-nav" data-key="del" aria-label="Delete digit">
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"/><line x1="18" y1="9" x2="12" y2="15"/><line x1="12" y1="9" x2="18" y2="15"/></svg>
          </button>
        </div>
      </div>
    `;
    let entered = '';
    const updateDots = () => card.querySelectorAll('.cashier-pin-dot').forEach((d, i) => d.classList.toggle('cashier-pin-dot--filled', i < entered.length));
    card.querySelector('.cashier-numpad')?.addEventListener('click', e => {
      const key = e.target.closest('.cashier-numpad-btn')?.dataset.key;
      if (!key) return;
      if (key === 'del') { entered = entered.slice(0, -1); updateDots(); return; }
      if (entered.length >= 6) return;
      entered += key;
      updateDots();
      if (entered.length === 6) renderConfirm(entered);
    });
  };

  const renderConfirm = (firstPin) => {
    card.innerHTML = `
      <div class="cashier-picker-header" style="border-bottom:none;padding-bottom:0">
        <div>
          <h3 class="cashier-picker-title">Confirm PIN</h3>
          <p class="cashier-picker-subtitle">Enter your PIN one more time.</p>
        </div>
      </div>
      <div class="cashier-pin-screen">
        <div class="cashier-pin-dots" id="pinSetupDots">
          ${'<span class="cashier-pin-dot"></span>'.repeat(6)}
        </div>
        <p class="cashier-pin-error hidden" id="pinSetupError">PINs don't match. Try again.</p>
        <div class="cashier-numpad">
          ${[1,2,3,4,5,6,7,8,9].map(n => `<button class="cashier-numpad-btn" data-key="${n}">${n}</button>`).join('')}
          <button class="cashier-numpad-btn cashier-numpad-nav" data-key="back" aria-label="Back">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            Back
          </button>
          <button class="cashier-numpad-btn" data-key="0">0</button>
          <button class="cashier-numpad-btn cashier-numpad-nav" data-key="del" aria-label="Delete digit">
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"/><line x1="18" y1="9" x2="12" y2="15"/><line x1="12" y1="9" x2="18" y2="15"/></svg>
          </button>
        </div>
      </div>
    `;
    let entered = '';
    const updateDots = () => card.querySelectorAll('.cashier-pin-dot').forEach((d, i) => d.classList.toggle('cashier-pin-dot--filled', i < entered.length));
    const shakeAndRetry = () => {
      const dotsEl = card.querySelector('#pinSetupDots');
      const errEl  = card.querySelector('#pinSetupError');
      dotsEl?.classList.add('cashier-pin-shake');
      errEl?.classList.remove('hidden');
      setTimeout(() => { dotsEl?.classList.remove('cashier-pin-shake'); renderCreate(); }, 800);
    };
    card.querySelector('.cashier-numpad')?.addEventListener('click', async e => {
      const key = e.target.closest('.cashier-numpad-btn')?.dataset.key;
      if (!key) return;
      if (key === 'back') { renderCreate(); return; }
      if (key === 'del')  { entered = entered.slice(0, -1); updateDots(); return; }
      if (entered.length >= 6) return;
      entered += key;
      updateDots();
      if (entered.length === 6) {
        if (entered === firstPin) {
          try {
            await model.setStaffPin(model.state.currentStaff.id, entered);
            model.state.currentCashier = model.state.currentStaff;
            el.remove();
            showToast('PIN set! You\'re all set.', 'success');
          } catch (err) {
            showToast(err.message ?? 'Could not save PIN. Try again.');
            el.remove();
          }
        } else {
          shakeAndRetry();
        }
      }
    });
  };

  renderCreate();
};

const controlEditStaffRole = function (staffId) {
  const staff = model.state.staff.find(s => s.id === staffId);
  if (!staff) return;
  const { onSave } = StaffView.showEditRoleModal(staff, model.state.roles);
  onSave(async (roleId) => {
    try {
      await model.updateStaffRole(staffId, roleId);
      StaffView.render(model.state.staff, _staffCanManage(), model.state.roles);
      showToast('Staff updated.', 'success');
    } catch (err) {
      showToast(err.message ?? 'Could not update staff.');
    }
  });
};

const controlSetStaffPin = async function (staffId, pin) {
  try {
    await model.setStaffPin(staffId, pin);
    StaffView.render(model.state.staff, _staffCanManage(), model.state.roles);
    showToast('PIN updated.', 'success');
  } catch (err) {
    showToast(err.message ?? 'Could not update PIN.');
  }
};

const controlShowInviteForm = function () {
  StaffView.showInviteForm(model.state.roles);
};

const controlInviteStaff = async function (data) {
  const email = data.email.toLowerCase();

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    showToast('Please enter a valid email address.', 'error');
    return;
  }
  if (email === model.state.currentStaff?.email?.toLowerCase()) {
    showToast("You can't invite yourself.", 'error');
    return;
  }
  const alreadyOnTeam = model.state.staff.some(s => s.email.toLowerCase() === email);
  if (alreadyOnTeam) {
    showToast('This email is already on your staff list.', 'error');
    return;
  }

  try {
    const { reactivated, existingAccount } = await model.inviteStaff(data);
    StaffView.closeForm();
    StaffView.render(model.state.staff, _staffCanManage(), model.state.roles);
    showToast(
      reactivated
        ? 'Staff member reactivated. They can sign in with their existing account.'
        : existingAccount
          ? 'This email already has a Pointbunny account. They will be asked to join your team the next time they sign in.'
          : 'Invite sent!',
      'success'
    );
  } catch (err) {
    // Server-side guard (staff_pending_invite_unique): a duplicate pending invite
    // is rejected by the DB with Postgres code 23505 (unique_violation).
    if (err?.code === '23505' || /duplicate key|unique constraint/i.test(err?.message ?? '')) {
      showToast('That person already has a pending invite.', 'error');
    } else {
      showToast(err.message ?? err);
    }
  }
};

const controlRemoveStaff = async function (id) {
  if (!confirm('Remove this staff member? They will lose access to the system.')) return;
  try {
    await model.removeStaff(id);
    StaffView.render(model.state.staff, _staffCanManage(), model.state.roles);
    showToast('Staff member removed.', 'success');
  } catch (err) {
    showToast(err.message ?? err);
  }
};

// Last resort for a staff member locked out of both their authenticator AND their
// recovery codes - manage_staff can strip the factor here instead of needing
// Supabase Dashboard access. The owner's own 2FA is out of scope (support-only).
const controlRemoveStaffMFA = async function (id) {
  const member = model.state.staff.find((s) => s.id === id);
  const name = member ? `${member.firstName} ${member.lastName}`.trim() : 'this staff member';
  if (!confirm(`Remove two-factor authentication for ${name}? They'll be able to sign in with just their password until they set it up again.`)) return;
  StaffView.setRemoveMFALoading(id, true);
  try {
    const { hadFactor } = await model.adminRemoveStaffMFA(id);
    showToast(hadFactor ? `Two-factor authentication removed for ${name}.` : `${name} didn't have two-factor authentication enabled.`, 'success');
  } catch (err) {
    showToast(err.message ?? err);
  } finally {
    StaffView.setRemoveMFALoading(id, false);
  }
};

// ── Payroll / Timesheets ──────────────────────────────────────────────────────

let _tsType  = 'week';
let _tsValue = null;

const controlFetchTimesheets = async function (type, value) {
  try {
    let startISO, endISO, label, resolvedValue;
    if (type === 'custom') {
      const start = new Date(value.from + 'T00:00:00'); start.setHours(0, 0, 0, 0);
      const end   = new Date(value.to   + 'T00:00:00'); end.setHours(23, 59, 59, 999);
      startISO = start.toISOString();
      endISO   = end.toISOString();
      const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      label = `${fmt(start)} – ${fmt(end)}`;
      resolvedValue = value;
    } else {
      const range = _getRangeFromValue(type, value);
      ({ startISO, endISO, label } = range);
      if (type === 'week') {
        const monday = new Date(startISO);
        resolvedValue = `${monday.getFullYear()}-${String(monday.getMonth()+1).padStart(2,'0')}-${String(monday.getDate()).padStart(2,'0')}`;
      } else {
        resolvedValue = value;
      }
    }
    _tsType  = type;
    _tsValue = resolvedValue;
    const canEdit = model.hasPermission('manage_pay');
    // Reviewers also load the pending correction requests, the deleted-shifts log,
    // and the registered time-clock devices. The purge (hard-delete of soft-deleted
    // shifts past their 7-day restore window) runs lazily here, before the log is
    // fetched, so expired entries never appear; best-effort - a failure (e.g. the
    // RPC not migrated yet) must not break the view.
    const [, , deletedShifts, devices, leaveRequests, onShift] = await Promise.all([
      model.fetchShifts(startISO, endISO),
      canEdit ? model.fetchShiftEditRequests() : Promise.resolve(),
      canEdit
        ? model.purgeDeletedShifts().catch(() => {}).then(() => model.fetchDeletedShifts())
        : Promise.resolve([]),
      canEdit ? model.fetchTimeclockDevices().catch(() => []) : Promise.resolve([]),
      // .catch keeps the panel alive if the leave_requests migration hasn't run yet.
      model.fetchLeaveRequests().catch(() => []),
      // "Currently timed in" list in the Time Clock section; best-effort.
      model.getOnShiftStaff().catch(() => []),
    ]);
    TimesheetView.renderTimesheets(model.state.shifts, model.state.staff, canEdit, { type, value: resolvedValue, label }, canEdit ? model.state.shiftEditRequests : [], deletedShifts ?? [], devices ?? [], leaveRequests ?? [], onShift ?? []);
  } catch (err) {
    showToast(err.message ?? 'Failed to load timesheets');
  }
};

// Soft-delete shifts (a single day entry or a staff member's whole visible week).
// Week view only - month/year are too coarse for bulk deletion by design.
const controlDeleteShifts = async function (ids, describe) {
  if (!model.hasPermission('manage_pay')) { showToast("You don't have access to delete shifts.", 'error'); return; }
  if (!ids?.length) return;
  if (!window.confirm(`Delete ${describe}? It will move to the Deleted shifts list below, where you can restore it.`)) return;
  try {
    await model.setShiftsDeleted(ids, true);
    showToast(ids.length === 1 ? 'Shift deleted.' : `${ids.length} shifts deleted.`, 'success');
    await controlFetchTimesheets(_tsType, _tsValue ?? null);
  } catch (err) {
    showToast(err.message ?? 'Could not delete the shift.', 'error');
  }
};

const controlRestoreShift = async function (id) {
  if (!model.hasPermission('manage_pay')) return;
  try {
    await model.setShiftsDeleted([id], false);
    showToast('Shift restored.', 'success');
    await controlFetchTimesheets(_tsType, _tsValue ?? null);
  } catch (err) {
    showToast(err.message ?? 'Could not restore the shift.', 'error');
  }
};

// ── On-shift indicator (home page floating circle) ────────────────────────────
// Polls who's currently clocked in (60s - shift state changes slowly) and renders
// the lower-right bubble. Errors are swallowed: the indicator is best-effort and
// must never break the app (e.g. before the get_on_shift_staff migration runs).
let _onShiftTimer = null;
// Cashier card status line ("On shift since X" / "On break" / "Not clocked in").
// Fed by the same on-shift list as the bubble; cached so a cashier switch can
// restamp the line without refetching.
let _lastOnShiftList = null;
const _updateCashierShiftStatus = function (list = _lastOnShiftList) {
  const el = document.getElementById('cashierShiftStatus');
  if (!el) return;
  const cashier = model.state.currentCashier;
  if (!cashier || list === null) { el.innerHTML = ''; return; }
  if (cashier.isOwner) {
    el.innerHTML = '<span class="home-action-dot home-action-dot--on"></span>Owner';
    return;
  }
  const entry = list.find(s => s.staffId === cashier.id);
  if (!entry) {
    el.innerHTML = '<span class="home-action-dot"></span>Not clocked in';
  } else if (entry.onBreak) {
    el.innerHTML = '<span class="home-action-dot home-action-dot--break"></span>On break';
  } else {
    const since = new Date(entry.clockedInAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    el.innerHTML = `On shift since ${since}`;
  }
};

// Today's expenses total on the Sales & Expenses card - the one money number
// the dash cards don't already show. Best-effort; refreshed after add/delete.
const _refreshHomeExpensesToday = async function () {
  const el = document.getElementById('homeExpensesToday');
  if (!el) return;
  // Don't bother fetching when the figures are hidden from this user anyway
  if (document.body.classList.contains('hide-home-stats')) return;
  try {
    const total = await model.loadTodayExpensesTotal();
    el.textContent = total > 0 ? `${formatMoney(total)} in expenses today` : 'No expenses today';
  } catch (_) { /* leave as-is */ }
};

const _refreshOnShift = async function () {
  try {
    const list = await model.getOnShiftStaff();
    _lastOnShiftList = list;
    OnShiftView.render(list);
    _updateCashierShiftStatus(list);
  } catch (_) { /* best-effort */ }
};
const _startOnShiftIndicator = function () {
  clearInterval(_onShiftTimer);
  _refreshOnShift();
  // Fallback poll behind the SHIFT_SYNC live broadcasts. Skipped while the tab
  // is hidden (a background POS tab doesn't need fresh on-shift data); one
  // catch-up refresh fires when the tab becomes visible again.
  _onShiftTimer = setInterval(() => { if (!document.hidden) _refreshOnShift(); }, 60000);
  if (!_onShiftVisibilityHooked) {
    _onShiftVisibilityHooked = true;
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && _onShiftTimer) _refreshOnShift();
    });
  }
};
let _onShiftVisibilityHooked = false;

// Revoke a registered time-clock device. The device shows the activation screen
// again on its next load.
const controlRevokeDevice = async function (id, label) {
  if (!model.hasPermission('manage_pay')) return;
  if (!window.confirm(`Remove "${label}"? That device will need a new activation code to be used again.`)) return;
  try {
    await model.deleteTimeclockDevice(id);
    showToast('Device removed.', 'success');
    await controlFetchTimesheets(_tsType, _tsValue ?? null);
  } catch (err) {
    showToast(err.message ?? 'Could not remove the device.', 'error');
  }
};

// Approve/reject a shift-correction request from the Payroll inbox (manage_pay only).
const controlReviewRequest = async function (id, decision) {
  if (!model.hasPermission('manage_pay')) { showToast("You don't have access to review time corrections.", 'error'); return; }
  const req = model.state.shiftEditRequests.find(r => r.id === id);
  if (!req) return;
  try {
    if (decision === 'approved') {
      await model.reviewShiftEditRequest(id, 'approved', {
        clockIn: req.proposedIn,
        clockOut: req.proposedOut,
        breaks: req.proposedBreaks,
      });
      showToast('Correction applied.', 'success');
    } else {
      const note = window.prompt('Reason for rejecting this correction? (optional)') ?? '';
      await model.reviewShiftEditRequest(id, 'rejected', { note: note.trim() || null });
      showToast('Correction rejected.');
    }
    // Refresh the current timesheet view (shows the applied change + drops the request).
    await controlFetchTimesheets(_tsType, _tsValue ?? null);
  } catch (err) {
    showToast(err.message ?? 'Could not update the request.', 'error');
  }
};

// Approve/reject a leave request from the Timesheets Leave section (manage_pay only).
const controlReviewLeave = async function (id, decision) {
  if (!model.hasPermission('manage_pay')) { showToast("You don't have access to review leave requests.", 'error'); return; }
  try {
    if (decision === 'approved') {
      await model.reviewLeaveRequest(id, 'approved');
      showToast('Leave approved.', 'success');
    } else {
      const note = window.prompt('Reason for rejecting this leave request? (optional)') ?? '';
      await model.reviewLeaveRequest(id, 'rejected', note.trim() || null);
      showToast('Leave request rejected.');
    }
    await controlFetchTimesheets(_tsType, _tsValue ?? null);
  } catch (err) {
    showToast(err.message ?? 'Could not update the leave request.', 'error');
  }
};

const controlSaveShift = async function (data) {
  if (!model.hasPermission('manage_pay')) { showToast('Only admins can edit shifts.', 'error'); return; }
  try {
    if (data.id) {
      await model.updateShift(data);
    } else {
      await model.addShift(data);
    }
    showToast('Shift saved.', 'success');
    await controlFetchTimesheets(_tsType, _tsValue);
  } catch (err) {
    showToast(err.message ?? 'Failed to save shift');
  }
};

// ── Reports ───────────────────────────────────────────────────────────────────

const _computeReportsSummary = function () {
  const sales = modelState.reportsSales;
  const revenue = sales.reduce((s, r) => s + Number(r.total_price), 0);
  const transactions = sales.length;
  const avgOrder = transactions > 0 ? revenue / transactions : 0;
  const avgServingMinutes = _computeServingTimeStats()?.avgMinutes ?? null;
  return { revenue, transactions, avgOrder, avgServingMinutes };
};

let _topItemsSortKey = "quantity";

// A soft-deleted item still appears in historical sales; tag it "(deleted)" so
// it's clear in Reports it's no longer in the live catalogue. Only flag names
// that belong to a deleted item AND aren't a current live item (so a rename
// doesn't get mislabeled).
const _markDeletedName = function (name) {
  const deleted = new Set((model.state.deletedItems ?? []).map((d) => d.name));
  const live = new Set(model.state.menuItems.map((i) => i.itemName));
  return deleted.has(name) && !live.has(name) ? `${name} (deleted)` : name;
};

const _computeTopItems = function (limit = 10, sortKey = _topItemsSortKey) {
  const map = new Map();
  for (const sale of modelState.reportsSales) {
    for (const item of (sale.items ?? [])) {
      const key = item.itemName;
      const existing = map.get(key) ?? { name: key, quantity: 0, revenue: 0 };
      existing.quantity += Number(item.quantity ?? 1);
      existing.revenue  += Number(item.totalPrice ?? 0);
      map.set(key, existing);
    }
  }
  return [...map.values()]
    .sort((a, b) => b[sortKey] - a[sortKey])
    .slice(0, limit)
    .map((it) => ({ ...it, name: _markDeletedName(it.name) }));
};

const controlSortTopItems = function (key) {
  _topItemsSortKey = key;
  ReportsView.setTopItemsSort(key);
  ReportsView.renderTopItems(_computeTopItems(), key);
};

const _computeCategoryMix = function () {
  const itemCatMap = new Map(model.state.menuItems.map(i => [i.itemName, i.category]));
  const map = new Map();
  for (const sale of modelState.reportsSales) {
    for (const item of (sale.items ?? [])) {
      const cat = itemCatMap.get(item.itemName) ?? "Other";
      map.set(cat, (map.get(cat) ?? 0) + Number(item.totalPrice ?? 0));
    }
  }
  return [...map.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label, value]) => ({ label, value }));
};

const _computeRevenueOverTime = function (period, startISO, endISO) {
  const sales = modelState.reportsSales;
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const fmtHour = (i) => i === 0 ? "12am" : i < 12 ? `${i}am` : i === 12 ? "12pm" : `${i - 12}pm`;

  if (period === "today") {
    const hours = Array(24).fill(0);
    for (const s of sales) hours[new Date(s.sale_date).getHours()] += Number(s.total_price);
    return { labels: Array.from({ length: 24 }, (_, i) => fmtHour(i)), data: hours };
  }

  if (period === "year") {
    const year = new Date(startISO).getFullYear();
    const mmap = new Map();
    for (const s of sales) {
      const m = s.sale_date.slice(0, 7);
      mmap.set(m, (mmap.get(m) ?? 0) + Number(s.total_price));
    }
    return {
      labels: MONTHS,
      data: MONTHS.map((_, i) => mmap.get(`${year}-${String(i + 1).padStart(2, "0")}`) ?? 0),
    };
  }

  // week / month / custom → daily
  const localYMD = d =>
    `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const dmap = new Map();
  for (const s of sales) {
    const d = localYMD(new Date(s.sale_date));   // local date of sale
    dmap.set(d, (dmap.get(d) ?? 0) + Number(s.total_price));
  }
  const start = new Date(startISO);
  const end   = new Date(endISO);
  const labels = [], data = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    labels.push(period === "week" ? DAYS[d.getDay()] : `${d.getMonth() + 1}/${d.getDate()}`);
    data.push(dmap.get(localYMD(d)) ?? 0);       // local date key, not UTC
  }
  return { labels, data };
};

const _computeAllTimeSeries = function(period, startISO, endISO) {
  const sales    = modelState.reportsSales;
  const expenses = modelState.expenses ?? [];
  const MONTHS   = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const DAYS     = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const fmtHour  = i => i === 0 ? "12am" : i < 12 ? `${i}am` : i === 12 ? "12pm" : `${i - 12}pm`;

  let labels, n, saleKeyFn, expKeyFn;

  if (period === "today") {
    labels = Array.from({ length: 24 }, (_, i) => fmtHour(i));
    n = 24;
    saleKeyFn = s => new Date(s.sale_date).getHours();
    expKeyFn  = e => e.expenseDate ? new Date(e.expenseDate).getHours() : -1;
  } else if (period === "year") {
    const year = new Date(startISO).getFullYear();
    labels = MONTHS;
    n = 12;
    const mKey = str => {
      const yr = parseInt(str.slice(0, 4));
      const mo = parseInt(str.slice(5, 7)) - 1;
      return yr === year ? mo : -1;
    };
    saleKeyFn = s => mKey(s.sale_date);
    expKeyFn  = e => e.expenseDate ? mKey(e.expenseDate) : -1;
  } else {
    const start = new Date(startISO);
    const end   = new Date(endISO);
    labels = [];
    const dateStrs = [];
    // Use LOCAL calendar date so keys match regardless of timezone.
    // d.toISOString() gives the UTC date which can be a day behind local midnight
    // in UTC+ timezones, causing sales to map to the wrong bucket.
    const localYMD = d =>
      `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      labels.push(period === "week"
        ? [DAYS[d.getDay()], `${mm}/${dd}`]          // two-line: "Mon" + "05/12"
        : `${d.getMonth() + 1}/${d.getDate()}`);
      dateStrs.push(localYMD(d));                    // LOCAL date key, not UTC
    }
    n = labels.length;
    const dmap = new Map(dateStrs.map((s, i) => [s, i]));
    // Map each sale to its LOCAL calendar date so it lands in the right bucket
    saleKeyFn = s => dmap.get(localYMD(new Date(s.sale_date))) ?? -1;
    expKeyFn  = e => e.expenseDate ? (dmap.get(e.expenseDate.slice(0, 10)) ?? -1) : -1;
  }

  const revArr = Array(n).fill(0);
  const expArr = Array(n).fill(0);
  const txArr  = Array(n).fill(0);
  const svcSum = Array(n).fill(0);
  const svcCnt = Array(n).fill(0);

  for (const s of sales) {
    const i = saleKeyFn(s);
    if (i < 0 || i >= n) continue;
    revArr[i] += Number(s.total_price);
    txArr[i]++;
    if (s.prepared_at) {
      const mins = (new Date(s.prepared_at) - new Date(s.sale_date)) / 60000;
      if (mins >= 0 && mins <= 120) { svcSum[i] += mins; svcCnt[i]++; }
    }
  }
  for (const e of expenses) {
    const i = expKeyFn(e);
    if (i < 0 || i >= n) continue;
    expArr[i] += e.amount;
  }

  return {
    labels,
    series: {
      revenue:      revArr,
      expenses:     expArr,
      net:          revArr.map((r, i) => r - expArr[i]),
      transactions: txArr,
      avgOrder:     revArr.map((r, i) => txArr[i] > 0 ? r / txArr[i] : 0),
      avgServing:   svcSum.map((s, i) => svcCnt[i] > 0 ? +(s / svcCnt[i]).toFixed(1) : 0),
    },
  };
};

const _buildOverviewDatasets = function() {
  if (!_overviewTimeSeries) return { labels: [], datasets: [] };
  const { labels, series } = _overviewTimeSeries;
  const datasets = [..._selectedOverviewMetrics].map(metric => {
    const color = METRIC_COLORS[metric];
    return {
      label: METRIC_LABELS[metric],
      data: series[metric] ?? [],
      borderColor: color,
      backgroundColor: color + '22',
      fill: 'origin',
      tension: 0.4,
      pointRadius: 3,
      pointHoverRadius: 7,
      borderWidth: 2.5,
      _metric: metric,
    };
  });
  return { labels, datasets };
};

const _renderOverviewChart = async function() {
  const { labels, datasets } = _buildOverviewDatasets();
  await ReportsView.renderOverviewChart({ labels, datasets });
  ReportsView.setSelectedMetrics(_selectedOverviewMetrics);
};

const controlToggleMetric = function(metric) {
  if (!METRIC_COLORS[metric]) return;
  if (_selectedOverviewMetrics.has(metric)) {
    _selectedOverviewMetrics.delete(metric);
  } else {
    _selectedOverviewMetrics.add(metric);
  }
  _renderOverviewChart();
};

const _computeTrafficPeaks = function(period, startISO, endISO) {
  const sales = modelState.reportsSales;
  const fmtHour = i => i === 0 ? '12am' : i < 12 ? `${i}am` : i === 12 ? '12pm' : `${i - 12}pm`;
  const FULLDAY = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const hourCounts = Array(24).fill(0);
  for (const s of sales) hourCounts[new Date(s.sale_date).getHours()]++;
  const maxHour = Math.max(...hourCounts);
  const peakHourIdx = maxHour > 0 ? hourCounts.indexOf(maxHour) : -1;

  const dayCounts = Array(7).fill(0);
  for (const s of sales) {
    const d = new Date(s.sale_date).getDay();
    dayCounts[d === 0 ? 6 : d - 1]++;
  }
  const maxDay = Math.max(...dayCounts);
  const peakDayIdx = maxDay > 0 ? dayCounts.indexOf(maxDay) : -1;

  const days = Math.max(1, Math.round((new Date(endISO) - new Date(startISO)) / 86400000));
  const avgDaily = sales.length > 0 ? sales.length / days : 0;

  return {
    peakHour:  peakHourIdx >= 0 ? { label: fmtHour(peakHourIdx), count: maxHour } : null,
    peakDay:   period !== 'today' && peakDayIdx >= 0 ? { label: FULLDAY[peakDayIdx], count: maxDay } : null,
    total:     sales.length,
    avgDaily,
  };
};

const _computeHourlyBreakdown = function () {
  const hours = Array(24).fill(0);
  for (const s of modelState.reportsSales)
    hours[new Date(s.sale_date).getHours()]++;
  const fmtHour = (i) => i === 0 ? "12am" : i < 12 ? `${i}am` : i === 12 ? "12pm" : `${i - 12}pm`;
  return { labels: Array.from({ length: 24 }, (_, i) => fmtHour(i)), data: hours };
};

const _computeDayOfWeek = function (period) {
  if (period === "today") return { labels: [], data: [], isEmpty: true };
  const MON = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const totals = Array(7).fill(0);
  for (const s of modelState.reportsSales) {
    const d = new Date(s.sale_date).getDay(); // 0=Sun
    const idx = d === 0 ? 6 : d - 1;         // shift to Mon=0
    totals[idx]++;
  }
  return { labels: MON, data: totals, isEmpty: false };
};

// Shared binning logic for any "minutes between two timestamps on a sale" metric -
// overall serving time, kitchen-only, and pickup-wait all bucket the same way, just
// with a different pair of timestamps (see _computeServingTimeStats below).
const _bucketizeServingTimes = function (sales, minsFn) {
  const fmtHour = (i) => i === 0 ? "12am" : i < 12 ? `${i}am` : i === 12 ? "12pm" : `${i - 12}pm`;
  const MON = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const hourCounts = Array(24).fill(0), hourTotals = Array(24).fill(0);
  const dayCounts  = Array(7).fill(0),  dayTotals  = Array(7).fill(0);
  let totalMins = 0, totalCount = 0;
  for (const s of sales) {
    const mins = minsFn(s);
    if (mins == null || mins < 0 || mins > 120) continue;
    const h = new Date(s.sale_date).getHours();
    hourCounts[h]++; hourTotals[h] += mins;
    const d = new Date(s.sale_date).getDay();
    const idx = d === 0 ? 6 : d - 1;
    dayCounts[idx]++; dayTotals[idx] += mins;
    totalMins += mins; totalCount++;
  }
  if (!totalCount) return null;
  return {
    avgMinutes: totalMins / totalCount,
    byHour: { labels: Array.from({ length: 24 }, (_, i) => fmtHour(i)), data: hourCounts.map((c, i) => c > 0 ? hourTotals[i] / c : 0) },
    byDay:  { labels: MON, data: dayCounts.map((c, i) => c > 0 ? dayTotals[i] / c : 0) },
  };
};

// Three cohorts, all derived from the same sales rows (2026-08-27):
//   overall (unchanged) - order placed -> Done, every completed order
//   kitchen  - order placed -> Ready for Pickup
//   pickup   - Ready for Pickup -> Done
// kitchen/pickup only cover orders that were actually marked Ready (that step is
// optional - see CLAUDE.md "Active Queue Section"), so they're computed over a
// narrower subset than the overall figure; readyShare tells the view what fraction
// of orders that subset represents.
const _computeServingTimeStats = function () {
  const sales = modelState.reportsSales.filter(s => s.prepared_at);
  if (!sales.length) return null;
  const overall = _bucketizeServingTimes(sales, s => (new Date(s.prepared_at) - new Date(s.sale_date)) / 60000);
  if (!overall) return null;

  const readySales = sales.filter(s => s.ready_at);
  const kitchen = readySales.length
    ? _bucketizeServingTimes(readySales, s => (new Date(s.ready_at) - new Date(s.sale_date)) / 60000)
    : null;
  const pickup = readySales.length
    ? _bucketizeServingTimes(readySales, s => (new Date(s.prepared_at) - new Date(s.ready_at)) / 60000)
    : null;

  return {
    ...overall,
    preparedCount: sales.length,
    readyCount: readySales.length,
    readyShare: sales.length ? readySales.length / sales.length : 0,
    kitchen,
    pickup,
  };
};

// Order Type (Dine In / Takeout) revenue + count + avg order + avg serving time
// (Placed -> Done), for the Reports "Channels" tab. Orders recorded while
// orderTypeEnabled was off carry order_type = null and are grouped as
// 'unspecified' - the view filters those out when the setting is off.
const _computeChannelMix = function () {
  const sales = modelState.reportsSales;
  if (!sales.length) return [];
  const map = new Map();
  for (const s of sales) {
    const key = s.order_type ?? 'unspecified';
    const e = map.get(key) ?? { type: key, revenue: 0, count: 0, servTotal: 0, servCount: 0 };
    e.revenue += Number(s.total_price);
    e.count++;
    if (s.prepared_at) {
      const mins = (new Date(s.prepared_at) - new Date(s.sale_date)) / 60000;
      if (mins >= 0 && mins <= 120) { e.servTotal += mins; e.servCount++; }
    }
    map.set(key, e);
  }
  const totalRevenue = sales.reduce((sum, s) => sum + Number(s.total_price), 0);
  return [...map.values()]
    .map(e => ({
      type: e.type,
      label: e.type === 'unspecified' ? 'Unspecified' : orderTypeLabel(e.type),
      revenue: e.revenue,
      count: e.count,
      avgOrder: e.count ? e.revenue / e.count : 0,
      share: totalRevenue > 0 ? e.revenue / totalRevenue : 0,
      countShare: sales.length > 0 ? e.count / sales.length : 0,
      avgServingMinutes: e.servCount ? e.servTotal / e.servCount : null,
    }))
    .sort((a, b) => b.revenue - a.revenue);
};

// Payment method (Cash / Card / QR) revenue + count, for the Reports "Channels" tab.
const _computePaymentMethodMix = function () {
  const sales = modelState.reportsSales;
  if (!sales.length) return [];
  const LABELS = { cash: 'Cash', card: 'Card', qr: 'QR' };
  const map = new Map();
  for (const s of sales) {
    const key = s.payment_method ?? 'cash';
    const e = map.get(key) ?? { method: key, revenue: 0, count: 0 };
    e.revenue += Number(s.total_price);
    e.count++;
    map.set(key, e);
  }
  const totalRevenue = sales.reduce((sum, s) => sum + Number(s.total_price), 0);
  return [...map.values()]
    .map(e => ({
      method: e.method,
      label: LABELS[e.method] ?? e.method,
      revenue: e.revenue,
      count: e.count,
      share: totalRevenue > 0 ? e.revenue / totalRevenue : 0,
      countShare: sales.length > 0 ? e.count / sales.length : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue);
};

const _getPreviousPeriodRange = function (period, startISO, endISO, from, to) {
  const start = new Date(startISO);
  const end   = new Date(endISO);

  if (period === "today") {
    const s = new Date(start); s.setDate(s.getDate() - 1); s.setHours(0,0,0,0);
    const e = new Date(s); e.setHours(23,59,59,999);
    return { prevStart: s.toISOString(), prevEnd: e.toISOString(), vsLabel: "yesterday" };
  }
  if (period === "week") {
    const s = new Date(start); s.setDate(s.getDate() - 7);
    const e = new Date(end);   e.setDate(e.getDate() - 7);
    return { prevStart: s.toISOString(), prevEnd: e.toISOString(), vsLabel: "last week" };
  }
  if (period === "month") {
    const s = new Date(start.getFullYear(), start.getMonth() - 1, 1);
    const e = new Date(start.getFullYear(), start.getMonth(), 0, 23, 59, 59, 999);
    return { prevStart: s.toISOString(), prevEnd: e.toISOString(), vsLabel: "last month" };
  }
  if (period === "year") {
    const s = new Date(start.getFullYear() - 1, 0, 1);
    const e = new Date(start.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
    return { prevStart: s.toISOString(), prevEnd: e.toISOString(), vsLabel: "last year" };
  }
  // custom — same duration shifted back
  const ms = end - start;
  const e  = new Date(start.getTime() - 1);
  const s  = new Date(e.getTime() - ms);
  return { prevStart: s.toISOString(), prevEnd: e.toISOString(), vsLabel: "previous period" };
};

// ── Compare helpers ───────────────────────────────────────────────────────────

const _computeSummaryFromSales = function (sales) {
  const revenue = sales.reduce((s, r) => s + Number(r.total_price), 0);
  const transactions = sales.length;
  const avgOrder = transactions > 0 ? revenue / transactions : 0;
  let servTotal = 0, servCount = 0;
  for (const s of sales) {
    if (!s.prepared_at) continue;
    const mins = (new Date(s.prepared_at) - new Date(s.sale_date)) / 60000;
    if (mins < 0 || mins > 120) continue;
    servTotal += mins; servCount++;
  }
  return { revenue, transactions, avgOrder, avgServingMinutes: servCount > 0 ? servTotal / servCount : null };
};

const _computeTopItemsFromSales = function (sales, limit = 5, sortKey = "quantity") {
  const map = new Map();
  for (const sale of sales) {
    for (const item of (sale.items ?? [])) {
      const key = item.itemName;
      const existing = map.get(key) ?? { name: key, quantity: 0, revenue: 0 };
      existing.quantity += Number(item.quantity ?? 1);
      existing.revenue  += Number(item.totalPrice ?? 0);
      map.set(key, existing);
    }
  }
  return [...map.values()]
    .sort((a, b) => b[sortKey] - a[sortKey])
    .slice(0, limit)
    .map((it) => ({ ...it, name: _markDeletedName(it.name) }));
};

const _computeCompareRevenue = function (sales, type, startISO, endISO) {
  const MONTHS  = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const DAYS    = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const fmtHour = (i) => i === 0 ? "12am" : i < 12 ? `${i}am` : i === 12 ? "12pm" : `${i - 12}pm`;

  if (type === "today" || type === "yesterday" || type === "day") {
    const hours = Array(24).fill(0);
    for (const s of sales) hours[new Date(s.sale_date).getHours()] += Number(s.total_price);
    return { labels: Array.from({ length: 24 }, (_, i) => fmtHour(i)), data: hours };
  }
  if (type === "year") {
    const year = new Date(startISO).getFullYear();
    const mmap = new Map();
    for (const s of sales) { const m = s.sale_date.slice(0, 7); mmap.set(m, (mmap.get(m) ?? 0) + Number(s.total_price)); }
    return { labels: MONTHS, data: MONTHS.map((_, i) => mmap.get(`${year}-${String(i + 1).padStart(2, "0")}`) ?? 0) };
  }
  // week / month / custom → daily
  const localYMD = d =>
    `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const dmap = new Map();
  for (const s of sales) {
    const d = localYMD(new Date(s.sale_date));   // local date of sale
    dmap.set(d, (dmap.get(d) ?? 0) + Number(s.total_price));
  }
  const start = new Date(startISO);
  const end   = new Date(endISO);
  const labels = [], data = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    labels.push(type === "week" ? DAYS[d.getDay()] : `${d.getMonth() + 1}/${d.getDate()}`);
    data.push(dmap.get(localYMD(d)) ?? 0);       // local date key, not UTC
  }
  return { labels, data };
};

// ── Per-sales-array helpers (used by compare mode for both periods) ────────────

const _computeCategoryMixFromSales = function (sales) {
  const itemCatMap = new Map(model.state.menuItems.map(i => [i.itemName, i.category]));
  const map = new Map();
  for (const sale of sales)
    for (const item of (sale.items ?? [])) {
      const cat = itemCatMap.get(item.itemName) ?? "Other";
      map.set(cat, (map.get(cat) ?? 0) + Number(item.totalPrice ?? 0));
    }
  return [...map.entries()].sort((a, b) => b[1] - a[1]).map(([label, value]) => ({ label, value }));
};

const _computeStaffFromSales = function (sales) {
  const map = new Map();
  for (const sale of sales) {
    const name = sale.added_by || "Unknown";
    const e = map.get(name) ?? { name, transactions: 0, revenue: 0 };
    e.transactions++; e.revenue += Number(sale.total_price);
    map.set(name, e);
  }
  return [...map.values()].sort((a, b) => b.revenue - a.revenue);
};

const _computeHourlyFromSales = function (sales) {
  const fmtHour = (i) => i === 0 ? "12am" : i < 12 ? `${i}am` : i === 12 ? "12pm" : `${i - 12}pm`;
  const hours = Array(24).fill(0);
  for (const s of sales) hours[new Date(s.sale_date).getHours()]++;
  return { labels: Array.from({ length: 24 }, (_, i) => fmtHour(i)), data: hours };
};

const _computeDowFromSales = function (sales) {
  const MON = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const totals = Array(7).fill(0);
  for (const s of sales) {
    const d = new Date(s.sale_date).getDay();
    totals[d === 0 ? 6 : d - 1]++;
  }
  return { labels: MON, data: totals };
};

const _computeServingFromSales = function (sales) {
  const fmtHour = (i) => i === 0 ? "12am" : i < 12 ? `${i}am` : i === 12 ? "12pm" : `${i - 12}pm`;
  const MON = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const filtered = sales.filter(s => s.prepared_at);
  if (!filtered.length) return null;
  const hourC = Array(24).fill(0), hourT = Array(24).fill(0);
  const dayC  = Array(7).fill(0),  dayT  = Array(7).fill(0);
  let totalMins = 0, totalCount = 0;
  for (const s of filtered) {
    const mins = (new Date(s.prepared_at) - new Date(s.sale_date)) / 60000;
    if (mins < 0 || mins > 120) continue;
    const h = new Date(s.sale_date).getHours();
    hourC[h]++; hourT[h] += mins;
    const d = new Date(s.sale_date).getDay();
    const idx = d === 0 ? 6 : d - 1;
    dayC[idx]++; dayT[idx] += mins;
    totalMins += mins; totalCount++;
  }
  if (!totalCount) return null;
  return {
    avgMinutes: totalMins / totalCount,
    byHour: { labels: Array.from({ length: 24 }, (_, i) => fmtHour(i)), data: hourC.map((c, i) => c > 0 ? hourT[i] / c : 0) },
    byDay:  { labels: MON, data: dayC.map((c, i) => c > 0 ? dayT[i] / c : 0) },
  };
};

const _computeTrafficPeaksFromSales = function (sales) {
  const fmtHour = i => i === 0 ? "12am" : i < 12 ? `${i}am` : i === 12 ? "12pm" : `${i - 12}pm`;
  const FULLDAY = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
  const hourC = Array(24).fill(0);
  for (const s of sales) hourC[new Date(s.sale_date).getHours()]++;
  const maxH = Math.max(...hourC);
  const dayC = Array(7).fill(0);
  for (const s of sales) { const d = new Date(s.sale_date).getDay(); dayC[d === 0 ? 6 : d - 1]++; }
  const maxD = Math.max(...dayC);
  return {
    peakHour:      maxH > 0 ? fmtHour(hourC.indexOf(maxH))  : "—",
    peakHourCount: maxH,
    peakDay:       maxD > 0 ? FULLDAY[dayC.indexOf(maxD)]   : "—",
    peakDayCount:  maxD,
  };
};

// ─────────────────────────────────────────────────────────────────────────────

let _compareModeActive = false;
let _cmpSalesA = null, _cmpSalesB = null, _cmpRangeA = null, _cmpRangeB = null;
let _cmpTopItemsSortKey = "quantity";

const _getRangeFromValue = function (type, value) {
  const fmtShort = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  if (type === "day") {
    const start = new Date(value + "T00:00:00"); start.setHours(0, 0, 0, 0);
    const end   = new Date(value + "T00:00:00"); end.setHours(23, 59, 59, 999);
    return { startISO: start.toISOString(), endISO: end.toISOString(),
      label: start.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) };
  }
  if (type === "week") {
    const d = new Date(value + "T00:00:00");
    const day = d.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    const monday = new Date(d); monday.setDate(d.getDate() + diffToMon); monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6); sunday.setHours(23, 59, 59, 999);
    return { startISO: monday.toISOString(), endISO: sunday.toISOString(),
      label: `${fmtShort(monday)} – ${fmtShort(sunday)}` };
  }
  if (type === "month") {
    const [year, month] = value.split("-").map(Number);
    const start = new Date(year, month - 1, 1);
    const end   = new Date(year, month, 0, 23, 59, 59, 999);
    return { startISO: start.toISOString(), endISO: end.toISOString(),
      label: start.toLocaleDateString("en-US", { month: "long", year: "numeric" }) };
  }
  if (type === "quarter") {
    const [year, q] = value.split("-Q").map(Number);
    const startMonth = (q - 1) * 3;
    const start = new Date(year, startMonth, 1);
    const end   = new Date(year, startMonth + 3, 0, 23, 59, 59, 999);
    return { startISO: start.toISOString(), endISO: end.toISOString(), label: `Q${q} ${year}` };
  }
  if (type === "year") {
    const year = parseInt(value);
    const start = new Date(year, 0, 1);
    const end   = new Date(year, 11, 31, 23, 59, 59, 999);
    return { startISO: start.toISOString(), endISO: end.toISOString(), label: String(year) };
  }
  return null;
};

const controlToggleCompare = function () {
  _compareModeActive = !_compareModeActive;
  if (_compareModeActive) ReportsView.enterCompareMode();
  else ReportsView.exitCompareMode();
};

const controlCmpSortTopItems = function (key) {
  _cmpTopItemsSortKey = key;
  ReportsView.setCmpTopItemsSort(key);
  if (_cmpSalesA && _cmpSalesB) {
    ReportsView.renderCmpTopItems(
      _computeTopItemsFromSales(_cmpSalesA, 5, key),
      _computeTopItemsFromSales(_cmpSalesB, 5, key),
    );
  }
};

const controlRunComparison = async function ({ type, aValue, bValue, fromA, toA, fromB, toB }) {
  try {
    _cmpRangeA = type === "custom" ? _getCashflowRange("custom", fromA, toA) : _getRangeFromValue(type, aValue);
    _cmpRangeB = type === "custom" ? _getCashflowRange("custom", fromB, toB) : _getRangeFromValue(type, bValue);
    _cmpTopItemsSortKey = "quantity";
    ReportsView.setCmpTopItemsSort("quantity");

    const [salesA, salesB] = await Promise.all([
      fetchReportsSalesRaw(_cmpRangeA.startISO, _cmpRangeA.endISO),
      fetchReportsSalesRaw(_cmpRangeB.startISO, _cmpRangeB.endISO),
    ]);
    _cmpSalesA = salesA;
    _cmpSalesB = salesB;

    const labelA = _cmpRangeA.label;
    const labelB = _cmpRangeB.label;

    // Revenue over time (existing)
    const revA = _computeCompareRevenue(salesA, type, _cmpRangeA.startISO, _cmpRangeA.endISO);
    const revB = _computeCompareRevenue(salesB, type, _cmpRangeB.startISO, _cmpRangeB.endISO);
    const maxLen = Math.max(revA.labels.length, revB.labels.length);
    const revLabels = revA.labels.length >= revB.labels.length ? revA.labels : revB.labels;
    const dataA = revA.data.concat(Array(maxLen - revA.data.length).fill(0));
    const dataB = revB.data.concat(Array(maxLen - revB.data.length).fill(0));

    // All additional metrics
    const categoryA  = _computeCategoryMixFromSales(salesA);
    const categoryB  = _computeCategoryMixFromSales(salesB);
    const itemMixA   = _computeTopItemsFromSales(salesA, 8, "quantity");
    const itemMixB   = _computeTopItemsFromSales(salesB, 8, "quantity");
    const staffA     = _computeStaffFromSales(salesA);
    const staffB     = _computeStaffFromSales(salesB);
    const hourlyA    = _computeHourlyFromSales(salesA);
    const hourlyB    = _computeHourlyFromSales(salesB);
    const dowA       = _computeDowFromSales(salesA);
    const dowB       = _computeDowFromSales(salesB);
    const servingA   = _computeServingFromSales(salesA);
    const servingB   = _computeServingFromSales(salesB);
    const tPeaksA    = _computeTrafficPeaksFromSales(salesA);
    const tPeaksB    = _computeTrafficPeaksFromSales(salesB);
    const topItemsA1 = _computeTopItemsFromSales(salesA, 1, "quantity")[0];
    const topItemsB1 = _computeTopItemsFromSales(salesB, 1, "quantity")[0];

    const fmt = (v) => formatMoney(v);

    const salesPeaksA = {
      bestSeller: topItemsA1?.name ?? "—",
      bestSellerSub: topItemsA1 ? `${topItemsA1.quantity} sold` : "",
      topCategory: categoryA[0]?.label ?? "—",
      topCategorySub: categoryA[0] ? fmt(categoryA[0].value) : "",
      topStaff: staffA[0]?.name ?? "—",
      topStaffSub: staffA[0] ? fmt(staffA[0].revenue) : "",
    };
    const salesPeaksB = {
      bestSeller: topItemsB1?.name ?? "—",
      bestSellerSub: topItemsB1 ? `${topItemsB1.quantity} sold` : "",
      topCategory: categoryB[0]?.label ?? "—",
      topCategorySub: categoryB[0] ? fmt(categoryB[0].value) : "",
      topStaff: staffB[0]?.name ?? "—",
      topStaffSub: staffB[0] ? fmt(staffB[0].revenue) : "",
    };

    // Render all
    ReportsView.renderCompareResults({
      labelA, labelB,
      summaryA: _computeSummaryFromSales(salesA),
      summaryB: _computeSummaryFromSales(salesB),
      topItemsA: _computeTopItemsFromSales(salesA, 5, "quantity"),
      topItemsB: _computeTopItemsFromSales(salesB, 5, "quantity"),
    });

    ReportsView.renderCmpPeakStats({ salesPeaksA, salesPeaksB, tPeaksA, tPeaksB });

    await Promise.all([
      ReportsView.renderCompareChart({ labelA, labelB, labels: revLabels, dataA, dataB }),
      ReportsView.renderCmpSalesCharts({ categoryA, categoryB, itemMixA, itemMixB, labelA, labelB }),
      ReportsView.renderCmpStaff({ staffA, staffB }),
      ReportsView.renderCmpTrafficCharts({ hourlyA, hourlyB, dowA, dowB, labelA, labelB }),
      ReportsView.renderCmpKitchenCharts({ servingA, servingB, labelA, labelB }),
    ]);
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const _getAvgDivisor = function (period, startISO, endISO) {
  const now = new Date();
  const end = new Date(endISO);
  const isCurrentPeriod = end >= now;

  if (period === 'today') {
    if (isCurrentPeriod) return Math.max(1, now.getHours() + now.getMinutes() / 60);
    return 24;
  }
  if (period === 'week') {
    if (isCurrentPeriod) {
      const start = new Date(startISO);
      return Math.max(1, Math.min(7, Math.ceil((now - start) / 86400000)));
    }
    return 7;
  }
  if (period === 'month') {
    if (isCurrentPeriod) return Math.max(1, now.getDate() / 7);
    const start = new Date(startISO);
    return new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate() / 7;
  }
  if (period === 'year') {
    if (isCurrentPeriod) return Math.max(1, now.getMonth() + 1);
    return 12;
  }
  // custom: days in range
  const start = new Date(startISO);
  return Math.max(1, Math.round((end - start) / 86400000));
};

const _getAvgUnitSuffix = function (period) {
  if (period === 'today')  return '/ Hr';
  if (period === 'week')   return '/ Day';
  if (period === 'month')  return '/ Wk';
  if (period === 'year')   return '/ Mo';
  return '/ Day';
};

const _getAvgTooltip = function (period, divisor) {
  const d = parseFloat(divisor.toFixed(1));
  if (period === 'today')  return `Divided by hours elapsed today (not always 24, currently ${d} hrs)`;
  if (period === 'week')   return `Divided by days elapsed this week (not always 7, currently ${d} days)`;
  if (period === 'month')  return `Divided by weeks elapsed this month (currently ${d} wks)`;
  if (period === 'year')   return `Divided by months elapsed this year (currently ${d} months)`;
  return `Divided by days in this range (${d} days)`;
};

let _staffSortKey = "revenue";

const _computeStaffPerformance = function (sortKey = _staffSortKey) {
  const map = new Map();
  for (const sale of modelState.reportsSales) {
    const name = sale.added_by || "Unknown";
    const existing = map.get(name) ?? { name, transactions: 0, revenue: 0 };
    existing.transactions++;
    existing.revenue += Number(sale.total_price);
    map.set(name, existing);
  }
  const sorter = sortKey === "quantity"
    ? (a, b) => b.transactions - a.transactions
    : (a, b) => b.revenue - a.revenue;
  return [...map.values()].sort(sorter);
};

const controlSortStaff = function (key) {
  _staffSortKey = key;
  ReportsView.setStaffSort(key);
  const staff = _computeStaffPerformance(key);
  ReportsView.renderStaff(staff, key);
};

let _reportSnapshot = null;

const controlExportReports = function () {
  if (!_reportSnapshot) {
    showToast("No data to export for this period.", "info");
    return;
  }
  const { sales, staff, periodLabel } = _reportSnapshot;
  if (!sales.length) {
    showToast("No data to export for this period.", "info");
    return;
  }

  const safeLabel = periodLabel.replace(/[^a-z0-9]/gi, "-").toLowerCase();
  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  // See _generateAndDownloadCSV's escape() for why the leading-character check exists.
  const escape = (val) => {
    let s = String(val ?? "");
    // A real signed amount ("+123.45"/"-25.00") must stay a plain number so
    // Excel/Sheets can still sum the column - only guard non-numeric formula shapes.
    if (/^[=+\-@\t\r]/.test(s) && !Number.isFinite(Number(s))) s = `'${s}`;
    return `"${s.replace(/"/g, '""')}"`;
  };

  const salesRows = sales.map((s) => [
    fmtDate(s.sale_date),
    fmtTime(s.sale_date),
    (s.items ?? []).map((i) => `${i.itemName} x${i.quantity}`).join("; "),
    `+${Number(s.total_price).toFixed(2)}`,
    s.order_type ?? "",
    s.added_by ?? "",
  ]);

  const staffRows = staff.map((p) => [
    "", "", `[Staff] ${p.name}`,
    p.revenue.toFixed(2),
    "",
    `${p.transactions} transaction${p.transactions !== 1 ? "s" : ""}`,
  ]);

  const header = ["Date", "Time", "Description", "Amount", "Order Type", "Staff / Notes"];
  const sep = [[""], ["--- Staff Summary ---"], ...staffRows.map((r) => r)];
  const csv = [header, ...salesRows, [], ...sep]
    .map((row) => (Array.isArray(row) ? row.map(escape).join(",") : ""))
    .join("\n");

  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `reports-${safeLabel}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  showToast("CSV downloaded.", "success");
};

const controlExportReportsPDF = function () {
  if (!_reportSnapshot) { showToast("No data to export for this period.", "info"); return; }
  const { sales, staff, periodLabel } = _reportSnapshot;
  if (!sales.length) { showToast("No data to export for this period.", "info"); return; }

  const summary = _computeReportsSummary();
  const topItems = _computeTopItems(10);

  const fmt = (n) => formatMoney(n);
  const fmtServ = (v) => {
    if (v == null) return "—";
    const m = Math.floor(v), s = Math.round((v - m) * 60);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  };
  const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  const captureChart = (wrapId, canvasId) => {
    const wrap = document.querySelector(`#${wrapId}`);
    if (!wrap || wrap.classList.contains("hidden")) return null;
    const canvas = document.querySelector(`#${canvasId}`);
    if (!canvas || canvas.style.display === "none") return null;
    try { return canvas.toDataURL("image/png"); } catch { return null; }
  };

  const charts = {
    revenue:     captureChart("rpRevenueWrap",      "rpRevenueCanvas"),
    category:    captureChart("rpCategoryWrap",     "rpCategoryCanvas"),
    hourly:      captureChart("rpHourlyWrap",       "rpHourlyCanvas"),
    dow:         captureChart("rpDowWrap",          "rpDowCanvas"),
    servHour:    captureChart("rpServingHourWrap",  "rpServingHourCanvas"),
    servDay:     captureChart("rpServingDayWrap",   "rpServingDayCanvas"),
  };

  // ── Compare mode PDF ──────────────────────────────────────────────────
  if (_compareModeActive && _cmpSalesA && _cmpSalesB) {
    const sA = _computeSummaryFromSales(_cmpSalesA);
    const sB = _computeSummaryFromSales(_cmpSalesB);
    const labelA = _cmpRangeA?.label ?? "Period A";
    const labelB = _cmpRangeB?.label ?? "Period B";
    const topA = _computeTopItemsFromSales(_cmpSalesA, 8, "revenue");
    const topB = _computeTopItemsFromSales(_cmpSalesB, 8, "revenue");
    const cmpChart = captureChart("rpCompareResults", "rpCmpRevenueCanvas");
    const now2 = new Date();
    const eDate = now2.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    const eTime = now2.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

    const deltaCell = (a, b, invert = false) => {
      if (a == null || b == null || b === 0) return `<td class="td-num td-muted">—</td>`;
      const pct = Math.round(((a - b) / b) * 100);
      const good = invert ? pct <= 0 : pct >= 0;
      const arrow = pct >= 0 ? "↑" : "↓";
      const col = good ? "#16a34a" : "#dc2626";
      return `<td class="td-num" style="color:${col};font-weight:600">${arrow} ${Math.abs(pct)}%</td>`;
    };

    const kpiCard = (label, rawA, rawB, fmtA, fmtB, invert = false) => {
      let aWins = false, bWins = false;
      if (rawA != null && rawB != null) {
        aWins = invert ? rawA < rawB : rawA > rawB;
        bWins = invert ? rawB < rawA : rawB > rawA;
      }
      let badge = "";
      if (rawA != null && rawB != null && rawB !== 0) {
        const pct = Math.round(((rawA - rawB) / rawB) * 100);
        const good = invert ? pct <= 0 : pct >= 0;
        const arrow = pct >= 0 ? "↑" : "↓";
        badge = `<span class="delta-pill ${good ? "delta-up" : "delta-dn"}">${arrow} ${Math.abs(pct)}%</span>`;
      }
      const bCls = bWins ? "kv-b kv-b--win" : (rawA != null && rawB != null && rawA !== rawB ? "kv-b kv-b--lose" : "kv-b kv-b--tie");
      return `<div class="kpi-card">
        <div class="kpi-lbl">${label}</div>
        <div class="kpi-row"><span class="dot-a"></span><span class="kv-a">${fmtA}</span>${badge}</div>
        <div class="kpi-row" style="margin-top:5px"><span class="dot-b"></span><span class="${bCls}">${fmtB}</span></div>
      </div>`;
    };

    const cmpHtml = `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"/>
<title>Pointbunny Comparison: ${esc(labelA)} vs ${esc(labelB)}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#fff;color:#111827;font-size:10.5pt;line-height:1.5;-webkit-print-color-adjust:exact;print-color-adjust:exact}
@page{size:A4;margin:13mm 16mm}
.pdf-header{background:#22c55e;border-radius:14px;padding:20px 24px;color:#fff;display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:18px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.pdf-logo{font-size:20pt;font-weight:800;letter-spacing:-0.02em}
.pdf-period{font-size:10pt;opacity:.85;margin-top:3px}
.pdf-meta{text-align:right;font-size:8.5pt;opacity:.8;line-height:1.7}
.section-heading{font-size:7.5pt;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#22c55e;margin:0 0 10px;padding-bottom:6px;border-bottom:2px solid #dcfce7}
.table-wrap{border:1.5px solid #e5e7eb;border-radius:12px;overflow:hidden;margin-bottom:18px;break-inside:avoid}
table{width:100%;border-collapse:collapse;font-size:8.5pt}
th{background:#f9fafb;padding:8px 10px;text-align:left;font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#6b7280;border-bottom:1.5px solid #e5e7eb}
td{padding:8px 10px;border-bottom:1px solid #f3f4f6;color:#374151}
tr:last-child td{border-bottom:none}
.td-num{text-align:right;font-variant-numeric:tabular-nums}
.td-muted{color:#9ca3af}
.td-metric{font-weight:600;color:#111827;width:110px}
.dot-a{display:inline-block;width:9px;height:9px;border-radius:50%;background:#22c55e;flex-shrink:0}
.dot-b{display:inline-block;width:9px;height:9px;border-radius:50%;background:#3b82f6;flex-shrink:0}
.kpi-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:18px}
.kpi-card{border:1.5px solid #e5e7eb;border-radius:12px;padding:14px 16px;break-inside:avoid}
.kpi-lbl{font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6b7280;margin-bottom:10px}
.kpi-row{display:flex;align-items:center;gap:8px}
.kv-a{font-size:14pt;font-weight:700;line-height:1;color:#16a34a;font-variant-numeric:tabular-nums}
.kv-b{font-size:13pt;font-weight:700;line-height:1;font-variant-numeric:tabular-nums}
.kv-b--win{color:#16a34a}.kv-b--lose{color:#dc2626}.kv-b--tie{color:#6b7280}
.delta-pill{display:inline-flex;align-items:center;gap:2px;font-size:7.5pt;font-weight:700;padding:2px 7px;border-radius:6px;line-height:1.5}
.delta-up{background:#dcfce7;color:#16a34a}
.delta-dn{background:#fee2e2;color:#dc2626}
.chart-wrap{border:1.5px solid #e5e7eb;border-radius:12px;overflow:hidden;margin-bottom:18px}
.chart-label{padding:9px 13px;border-bottom:1px solid #f3f4f6;font-size:8pt;font-weight:600;color:#374151;background:#f9fafb}
.chart-wrap img{width:100%;display:block}
.top-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:18px}
.top-card{border:1.5px solid #e5e7eb;border-radius:12px;overflow:hidden}
.top-card th,.top-card td{padding:7px 10px}
.pdf-footer{margin-top:14px;padding-top:10px;border-top:1px solid #e5e7eb;font-size:7.5pt;color:#9ca3af;text-align:center}
.period-legend{display:flex;align-items:center;gap:10px;margin-bottom:18px;padding:9px 14px;border:1.5px solid #e5e7eb;border-radius:10px;font-size:8.5pt;color:#374151;background:#f9fafb}
.period-lbl{font-weight:600;color:#374151}
.period-sep{color:#9ca3af;margin:0 4px}
.period-tag{font-size:6.5pt;font-weight:700;text-transform:uppercase;letter-spacing:.04em;padding:1px 6px;border-radius:4px;line-height:1.6}
.period-tag-a{background:#dcfce7;color:#16a34a}
.period-tag-b{background:#dbeafe;color:#2563eb}
</style></head><body>

<div class="pdf-header">
  <div>
    <div class="pdf-logo">Pointbunny</div>
    <div class="pdf-period">Comparison Report</div>
  </div>
  <div class="pdf-meta"><div>${eDate}</div><div>${eTime}</div></div>
</div>

<div class="period-legend">
  <span class="dot-a"></span><span class="period-tag period-tag-a">A</span><span class="period-lbl">${esc(labelA)}</span>
  <span class="period-sep">vs</span>
  <span class="dot-b"></span><span class="period-tag period-tag-b">B</span><span class="period-lbl">${esc(labelB)}</span>
</div>

<div class="section-heading">KPI Summary</div>
<div class="kpi-grid">
  ${kpiCard("Gross Income",  sA.revenue,           sB.revenue,           fmt(sA.revenue),               fmt(sB.revenue))}
  ${kpiCard("Transactions",  sA.transactions,      sB.transactions,      String(sA.transactions),       String(sB.transactions))}
  ${kpiCard("Avg. Order",    sA.avgOrder,          sB.avgOrder,          fmt(sA.avgOrder),              fmt(sB.avgOrder))}
  ${kpiCard("Avg. Serving",  sA.avgServingMinutes, sB.avgServingMinutes, fmtServ(sA.avgServingMinutes), fmtServ(sB.avgServingMinutes), true)}
</div>

${cmpChart ? `<div class="section-heading">Gross Income Over Time</div>
<div class="chart-wrap"><div class="chart-label">${esc(labelA)} vs ${esc(labelB)}</div><img src="${cmpChart}" alt="Compare chart"/></div>` : ""}

<div class="section-heading">Top Items by Gross Income</div>
<div class="top-grid">
  <div class="top-card"><table>
    <thead><tr><th><span class="dot-a"></span>${esc(labelA)}</th><th class="td-num">Gross Income</th></tr></thead>
    <tbody>${topA.map(i => `<tr><td>${esc(i.name)}</td><td class="td-num">${fmt(i.revenue)}</td></tr>`).join("") || '<tr><td colspan="2" class="td-muted" style="text-align:center;padding:14px">No data</td></tr>'}</tbody>
  </table></div>
  <div class="top-card"><table>
    <thead><tr><th><span class="dot-b"></span>${esc(labelB)}</th><th class="td-num">Gross Income</th></tr></thead>
    <tbody>${topB.map(i => `<tr><td>${esc(i.name)}</td><td class="td-num">${fmt(i.revenue)}</td></tr>`).join("") || '<tr><td colspan="2" class="td-muted" style="text-align:center;padding:14px">No data</td></tr>'}</tbody>
  </table></div>
</div>

<div class="pdf-footer">Pointbunny POS &nbsp;·&nbsp; ${esc(labelA)} vs ${esc(labelB)} &nbsp;·&nbsp; Exported ${eDate} at ${eTime}</div>
</body></html>`;

    const pop = window.open("", "pointbunny-pdf-report", "width=960,height=720");
    if (!pop) { showToast("Pop-up blocked. Allow pop-ups for this site to export PDF.", "error"); return; }
    pop.document.write(cmpHtml);
    pop.document.close();
    pop.focus();
    setTimeout(() => pop.print(), 700);
    return;
  }

  // ── Regular mode PDF ──────────────────────────────────────────────────
  const chartBlock = (img, label, full = false) => !img ? "" : `
    <div class="chart-block${full ? " chart-block--full" : ""}">
      <div class="chart-label">${label}</div>
      <img src="${img}" alt="${label}"/>
    </div>`;

  const topItemsRows = topItems.map((item, i) => `
    <tr>
      <td class="td-rank">${i + 1}</td>
      <td>${esc(item.name)}</td>
      <td class="td-num">${item.quantity}</td>
      <td class="td-num">${fmt(item.revenue)}</td>
    </tr>`).join("");

  const staffRows = staff.map(p => `
    <tr>
      <td>${esc(p.name)}</td>
      <td class="td-num">${p.transactions}</td>
      <td class="td-num">${fmt(p.revenue)}</td>
      <td class="td-num">${fmt(p.transactions > 0 ? p.revenue / p.transactions : 0)}</td>
    </tr>`).join("");

  const salesRows = sales.slice(0, 50).map(s => `
    <tr>
      <td>${esc(fmtDate(s.sale_date))}</td>
      <td>${esc(fmtTime(s.sale_date))}</td>
      <td>${esc((s.items ?? []).map(i => `${i.itemName} ×${i.quantity}`).join(", "))}</td>
      <td class="td-num">${fmt(s.total_price)}</td>
      <td>${esc(s.order_type ?? "—")}</td>
      <td>${esc(s.added_by ?? "—")}</td>
    </tr>`).join("");

  const now = new Date();
  const exportDate = now.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const exportTime = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<title>Pointbunny Report: ${esc(periodLabel)}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#fff;color:#111827;font-size:10.5pt;line-height:1.5;-webkit-print-color-adjust:exact;print-color-adjust:exact}
@page{size:A4;margin:13mm 16mm}
.pdf-header{background:#22c55e;border-radius:14px;padding:20px 24px;color:#fff;display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:18px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.pdf-logo{font-size:20pt;font-weight:800;letter-spacing:-0.02em}
.pdf-period{font-size:10pt;opacity:.85;margin-top:3px}
.pdf-meta{text-align:right;font-size:8.5pt;opacity:.8;line-height:1.7}
.kpi-strip{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:18px}
.kpi-card{border:1.5px solid #e5e7eb;border-radius:12px;padding:13px 15px}
.kpi-label{font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6b7280;margin-bottom:6px}
.kpi-val{font-size:15pt;font-weight:700;color:#111827;line-height:1;font-variant-numeric:tabular-nums}
.section-heading{font-size:7.5pt;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#22c55e;margin:0 0 10px;padding-bottom:6px;border-bottom:2px solid #dcfce7}
.charts-area{margin-bottom:18px}
.charts-row{display:grid;gap:10px;margin-bottom:10px}
.charts-row--2{grid-template-columns:1fr 1fr}
.charts-row--1{grid-template-columns:1fr}
.chart-block{border:1.5px solid #e5e7eb;border-radius:12px;overflow:hidden;break-inside:avoid}
.chart-label{padding:8px 13px;border-bottom:1px solid #f3f4f6;font-size:8pt;font-weight:600;color:#374151;background:#f9fafb}
.chart-block img{width:100%;display:block}
.table-wrap{border:1.5px solid #e5e7eb;border-radius:12px;overflow:hidden;margin-bottom:18px;break-inside:avoid}
table{width:100%;border-collapse:collapse;font-size:8.5pt}
th{background:#f9fafb;padding:8px 10px;text-align:left;font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#6b7280;border-bottom:1.5px solid #e5e7eb}
td{padding:7px 10px;border-bottom:1px solid #f3f4f6;color:#374151}
tr:last-child td{border-bottom:none}
.td-num{text-align:right;font-variant-numeric:tabular-nums}
.td-rank{color:#9ca3af;font-weight:600;width:28px}
.pdf-footer{margin-top:14px;padding-top:10px;border-top:1px solid #e5e7eb;font-size:7.5pt;color:#9ca3af;text-align:center}
</style>
</head>
<body>

<div class="pdf-header">
  <div>
    <div class="pdf-logo">Pointbunny</div>
    <div class="pdf-period">${esc(periodLabel)}</div>
  </div>
  <div class="pdf-meta">
    <div>${exportDate}</div>
    <div>${exportTime}</div>
  </div>
</div>

<div class="kpi-strip">
  <div class="kpi-card"><div class="kpi-label">Gross Income</div><div class="kpi-val">${fmt(summary.revenue)}</div></div>
  <div class="kpi-card"><div class="kpi-label">Transactions</div><div class="kpi-val">${summary.transactions}</div></div>
  <div class="kpi-card"><div class="kpi-label">Avg. Order</div><div class="kpi-val">${fmt(summary.avgOrder)}</div></div>
  <div class="kpi-card"><div class="kpi-label">Avg. Serving</div><div class="kpi-val">${fmtServ(summary.avgServingMinutes)}</div></div>
</div>

${charts.revenue ? `<div class="section-heading">Charts</div><div class="charts-area">
  <div class="charts-row charts-row--1">${chartBlock(charts.revenue, "Gross Income Over Time")}</div>
  <div class="charts-row charts-row--2">${chartBlock(charts.category, "Category Mix")}${chartBlock(charts.hourly, "Hourly Breakdown")}</div>
  ${(charts.dow || charts.servHour || charts.servDay) ? `<div class="charts-row charts-row--2">${chartBlock(charts.dow, "Day of Week")}${chartBlock(charts.servHour, "Avg. Serving by Hour")}</div>` : ""}
  ${charts.servDay ? `<div class="charts-row charts-row--1">${chartBlock(charts.servDay, "Avg. Serving by Day")}</div>` : ""}
</div>` : ""}

<div class="section-heading">Top Items</div>
<div class="table-wrap">
  <table>
    <thead><tr><th>#</th><th>Item</th><th class="td-num">Qty Sold</th><th class="td-num">Gross Income</th></tr></thead>
    <tbody>${topItemsRows || '<tr><td colspan="4" style="color:#9ca3af;text-align:center;padding:16px">No item data</td></tr>'}</tbody>
  </table>
</div>

${staff.length ? `<div class="section-heading">Staff Performance</div>
<div class="table-wrap">
  <table>
    <thead><tr><th>Cashier</th><th class="td-num">Transactions</th><th class="td-num">Gross Income</th><th class="td-num">Avg. Order</th></tr></thead>
    <tbody>${staffRows}</tbody>
  </table>
</div>` : ""}

<div class="section-heading">Transactions ${sales.length > 50 ? "(showing first 50)" : ""}</div>
<div class="table-wrap">
  <table>
    <thead><tr><th>Date</th><th>Time</th><th>Items</th><th class="td-num">Total</th><th>Type</th><th>Cashier</th></tr></thead>
    <tbody>${salesRows}</tbody>
  </table>
</div>

<div class="pdf-footer">Pointbunny POS &nbsp;·&nbsp; ${esc(periodLabel)} &nbsp;·&nbsp; Exported ${exportDate} at ${exportTime}</div>

</body>
</html>`;

  const popup = window.open("", "pointbunny-pdf-report", "width=960,height=720");
  if (!popup) { showToast("Pop-up blocked. Allow pop-ups for this site to export PDF.", "error"); return; }
  popup.document.write(html);
  popup.document.close();
  popup.focus();
  setTimeout(() => popup.print(), 700);
};

const _renderReportsData = async function (period, startISO, endISO, label, prevTotals, vsLabel) {
  ReportsView.setPeriodLabel(label);
  const expSummary = _reportsExpenseSummary();
  const summary = { ..._computeReportsSummary(), net: expSummary.net, expenses: expSummary.expenses };
  ReportsView.renderSummary(summary);
  if (prevTotals) ReportsView.renderComparison(summary, prevTotals, vsLabel);
  const topItems   = _computeTopItems();
  const categoryMix = _computeCategoryMix();
  const staff      = _computeStaffPerformance();
  ReportsView.renderTopItems(topItems, _topItemsSortKey);
  ReportsView.renderStaff(staff, _staffSortKey);
  ReportsView.renderSalesKpis({
    bestSeller:  topItems[0]   ?? null,
    topCategory: categoryMix[0] ?? null,
    topStaff:    staff[0]      ?? null,
  });
  const avgDivisor = _getAvgDivisor(period, startISO, endISO);
  const avgSuffix  = _getAvgUnitSuffix(period);
  const avgTooltip = _getAvgTooltip(period, avgDivisor);
  ReportsView.renderExpenseKpis({
    ...expSummary,
    avgGross: expSummary.gross / avgDivisor,
    avgNet:   expSummary.net   / avgDivisor,
    avgSuffix,
    avgTooltip,
  });
  _reportSnapshot = { sales: modelState.reportsSales, staff, periodLabel: label };
  _selectedOverviewMetrics = new Set();
  _overviewTimeSeries = _computeAllTimeSeries(period, startISO, endISO);
  ReportsView.renderTrafficKpis(_computeTrafficPeaks(period, startISO, endISO));
  const servingTime = _computeServingTimeStats();
  ReportsView.renderKitchenBreakdown(servingTime);
  await ReportsView.renderChannels({
    channelMix:      _computeChannelMix(),
    paymentMix:      _computePaymentMethodMix(),
    orderTypeEnabled: !!modelState.settings?.orderTypeEnabled,
  });
  await ReportsView.renderCharts({
    categoryMix,
    itemMix:         topItems,
    hourlyBreakdown: _computeHourlyBreakdown(),
    dayOfWeek:       _computeDayOfWeek(period),
    servingTime,
  });
  await _renderOverviewChart();
};

const controlOpenReports = async function (section = "overview") {
  if (!model.hasPermission('view_reports')) { showToast("You don't have access to reports.", 'error'); return; }
  ReportsView.open();
  ReportsView.preloadChart(); // eagerly start compiling the chart.js chunk while data fetches
  ReportsView.renderLoading();
  _currentReportsPeriod = { period: "today" };
  try {
    const { startISO, endISO, label } = _getCashflowRange("today");
    const { prevStart, prevEnd, vsLabel } = _getPreviousPeriodRange("today", startISO, endISO);
    const [, , prevExpenses] = await Promise.all([
      model.fetchReportsSales(startISO, endISO),
      model.fetchPeriodTotals(prevStart, prevEnd).then(pt => { _currentPrevTotals = pt; }),
      model.fetchExpensesTotal(prevStart, prevEnd),
      model.fetchCashflowData(startISO, endISO),
    ]);
    _currentPrevTotals = { ..._currentPrevTotals, expenses: prevExpenses, net: _currentPrevTotals.revenue - prevExpenses };
    await _renderReportsData("today", startISO, endISO, label, _currentPrevTotals, vsLabel);
    if (section !== "overview") ReportsView._switchSection(section);
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const controlCloseReports = function () {
  if (_compareModeActive) { _compareModeActive = false; ReportsView.exitCompareMode(); }
  ReportsView.close();
};

const controlReportsPeriodChange = async function ({ period, from, to }) {
  ReportsView.renderLoading();
  _currentReportsPeriod = { period, from, to };
  try {
    const { startISO, endISO, label } = _getCashflowRange(period, from, to);
    const { prevStart, prevEnd, vsLabel } = _getPreviousPeriodRange(period, startISO, endISO, from, to);
    const [, , prevExpenses] = await Promise.all([
      model.fetchReportsSales(startISO, endISO),
      model.fetchPeriodTotals(prevStart, prevEnd).then(pt => { _currentPrevTotals = pt; }),
      model.fetchExpensesTotal(prevStart, prevEnd),
      model.fetchCashflowData(startISO, endISO),
    ]);
    _currentPrevTotals = { ..._currentPrevTotals, expenses: prevExpenses, net: _currentPrevTotals.revenue - prevExpenses };
    await _renderReportsData(period, startISO, endISO, label, _currentPrevTotals, vsLabel);
    ReportsView.replayActiveSection(); // re-run the entrance so charts/cards animate on every period change
  } catch (err) {
    showToast(err.message ?? err);
  }
};

const _wireApp = function () {
  //MenuList
  MenuListView._addHandlerShowModal(controlMenuList);
  document.getElementById('menuSetupCta')?.addEventListener('click', controlMenuList);
  MenuEditView._showEditMenuForm(controlShowEditMenu);
  MenuEditView._deleteVariant();
  MenuEditView._closeModal();
  MenuEditView._deleteOption();
  MenuEditView._addVariantGroup();
  MenuEditView._addOption();
  MenuEditView._updateImagePreview();
  MenuEditView._addPhotoDropZone();
  MenuEditView._wireVisualPicker();
  MenuEditView._addHandlerHasVariantsToggle();
  MenuEditView._addHandlerInventorySection();
  MenuEditView._addHandlerDeleteItem(controlDeleteMenuItem);
  MenuEditView._addHandlerManageTax(controlManageItemTax);

  //Adding New Menu
  NewMenuItemView._uploadItem(controlUploadItem);
  NewMenuItemView._newMenuCategory();
  NewMenuItemView._addHandlerAddMenuCategory(controlAddNewCategory);
  NewMenuItemView._itemVariantsToggle();
  NewMenuItemView._addVariantOption();

  controlNewMenuButtonToggle();

  // Settings
  SettingsView._addHandlerOpen(controlOpenSettings);
  SettingsView._addHandlerClose();
  SettingsView._addHandlerNavTabs();
  SettingsView._addHandlerMobileNav();
  SettingsView._addHandlerEditProfile(controlEditProfile);
  SettingsView._addHandlerCancelProfile(controlCancelProfile);
  SettingsView._addHandlerSaveProfile(controlSaveProfile);
  SettingsView._addHandlerEditBusiness(controlEditBusiness);
  SettingsView._addHandlerCancelBusiness(controlCancelBusiness);
  SettingsView._addHandlerSaveBusinessInfo(controlSaveBusinessInfo);
  SettingsView._addHandlerVerifyOTP(controlVerifySettingsOTP);
  SettingsView._addHandlerCancelOTP(controlCancelSettingsOTP);
  SettingsView._addHandler2FAEnable(controlEnable2FA);
  SettingsView._addHandler2FADisable(controlDisable2FA);
  SettingsView._addHandler2FAVerifyEnroll(controlConfirm2FAEnroll);
  SettingsView._addHandler2FACancelEnroll();
  SettingsView._addHandler2FACodes(
    () => showToast('Two-factor authentication enabled.', 'success'),
    () => showToast('Please tick the box to confirm you have saved your recovery codes first.', 'error'),
  );
  SettingsView._addHandlerTaxReminderClose();
  MenuListView._addHandlerAddCategory(controlAddCategoryFromSettings);
  MenuListView._addHandlerDeleteCategory(controlDeleteCategory);
  MenuListView._addHandlerToggleCategories();
  MenuListView._addHandlerUploadCsv(controlUploadMenuCsv);
  MenuListView._addHandlerToggleSelectMode();
  MenuListView._addHandlerCardSelect();
  MenuListView._addHandlerSelectBar({ onSetStatus: controlBulkSetStatus, onDelete: controlBulkDelete });
  SettingsView._addHandlerShowRemoved(controlShowRemoved);
  SettingsView._addHandlerTogglePrinting(controlTogglePrinting);
  SettingsView._addHandlerToggleConfirmPrint(controlToggleConfirmPrint);
  SettingsView._addHandlerToggleShowCashier(controlToggleShowCashier);
  SettingsView._addHandlerToggleHideStats(controlToggleHideStats);
  SettingsView._addHandlerToggleOrderType(controlToggleOrderType);
  SettingsView._addHandlerToggleInvoiceNumber(controlToggleShowInvoiceNumber);
  SettingsView._addHandlerToggleTicketNumber(controlToggleShowTicketNumber);
  SettingsView._addHandlerBtDisconnect(controlDisconnectBtPrinter);
  SettingsView._addHandlerBtTest(controlTestBtPrint);
  SettingsView._addHandlerBtPaperWidth(controlStagePaperWidth);
  SettingsView._addHandlerSaveSettingsDraft(controlSaveSettingsDraft);
  SettingsView._addHandlerDiscardSettingsDraft(controlDiscardSettingsDraft);
  SettingsView._addHandlerDiscardSettingsDraftOnClose(controlDiscardSettingsDraft);

  //NewOrder
  NewOrderView._addHandlerShowMenuModal(controlNewOrder);
  NewOrderView._addHandlerEmptyAddItems(controlMenuList);
  NewOrderView._addHandlerCategoryTabs();
  NewOrderView._addHandlerStockChip();
  NewOrderView._addHandlerToggleVisualMode();
  NewOrderItemView._addHandlerShowItemModal(controlDisplayMenuItem);
  controlNewOrderModals();
  NewOrderItemView._pushToCart(controlPushToModelCart);
  NewOrderItemView._adjustQuantity();
  NewOrderView._addHandlerDeleteCartItem(controlDeleteCartItemInOrder);

  // Cashflow
  CashflowView._addHandlerOpen(controlOpenCashflow);
  CashflowView._addHandlerClose(controlCloseCashflow);
  CashflowView._addHandlerPeriodChange(controlChangePeriod);
  CashflowView._addHandlerCustomRange(controlChangePeriod);
  CashflowView._addHandlerOpenAddExpense();
  CashflowView._addHandlerSubmitExpense(controlAddExpense);
  CashflowView._addHandlerDeleteExpense(controlDeleteExpense);
  CashflowView._addHandlerOpenSaleReceipt(controlOpenSaleReceipt);
  CashflowView._addHandlerExport(controlExportCSV);
  CashflowView._addHandlerVoid(controlVoidTransaction);
  CashflowView._addHandlerTabChange();
  CashflowView._addHandlerOverrideModal();

  //New Order Check Out
  OrderCheckOutView._addHandlerShowCheckout(controlOrderCheckout);
  OrderCheckOutView._addHandlerDeleteCartItem(controlDeleteCartItemInCheckout);
  OrderCheckOutView._addHandlerBack(controlGoBackToOrder);
  OrderCheckOutView._subtractChange();
  OrderCheckOutView._wireOrderType();
  OrderCheckOutView._addHandlerPaymentMethod(controlSetPaymentMethod);
  OrderCheckOutView._addHandlerPrintReceipt(controlConcludeTransaction);
  OrderCheckOutView._addHandlerReceiptEdit(controlReceiptEdit);
  OrderCheckOutView._addHandlerReceiptRemove(controlRemoveReceiptAdj);
  OrderCheckOutView._addHandlerReceiptAddManual(controlShowReceiptAddManualForm);
  OrderCheckOutView._addHandlerReceiptSaveOverride(controlSaveReceiptOverride);
  OrderCheckOutView._addHandlerReceiptSaveManual(controlSaveManualReceiptAdj);
  OrderCheckOutView._addHandlerApplyPromo(controlApplyPromoCode);
  OrderCheckOutView._addHandlerRemovePromo(controlRemovePromoCode);
  OrderCheckOutView._addHandlerLoyaltyLookup(controlLoyaltyLookup);
  OrderCheckOutView._addHandlerLoyaltyEnroll(controlLoyaltyEnroll);
  OrderCheckOutView._addHandlerLoyaltyEnrollCancel(controlLoyaltyEnrollCancel);
  OrderCheckOutView._wireLoyaltyStepper();
  OrderCheckOutView._wireLoyaltyInputs();
  OrderCheckOutView._addHandlerLoyaltyRedeem(controlLoyaltyApplyRedemption);
  OrderCheckOutView._addHandlerLoyaltyRemoveRedeem(controlLoyaltyRemoveRedemption);
  OrderCheckOutView._addHandlerLoyaltyUnlink(controlLoyaltyUnlink);
  OrderCheckOutView._addHandlerLoyaltyRewardRedeem(controlApplyLoyaltyRewardRedemption);
  OrderCheckOutView._addHandlerLoyaltyRewardRemove(controlRemoveLoyaltyRewardRedemption);
  OrderCheckOutView._addHandlerLoyaltyRedeemOpen(controlOpenLoyaltyRedeemModal);

  // Order Queue (KDS)
  KDSView._addHandlerViewAll();
  KDSView._addHandlerActions(controlMarkOrderDone, controlMarkOrderReady, controlUnmarkOrderReady);
  KDSView._addHandlerOpenModal(() => modelState.orderQueue);
  KDSView._addHandlerModalClose();
  KDSView._addHandlerModalActions(controlMarkOrderDone, controlMarkOrderReady, controlUnmarkOrderReady);
  KDSView._addHandlerChooserDismiss();
  SettingsView._addHandlerKDSThresholds(controlSaveKDSThresholds);
  SettingsView._addHandlerDisplaySizes(controlSaveKDSWindowSize, controlSaveCFDWindowSize);
  SettingsView._addHandlerCFDAdUpload(controlUploadCFDAd);
  SettingsView._addHandlerCFDAdRemove(controlRemoveCFDAd);
  SettingsView._addHandlerPickupAdUpload(controlUploadPickupAd);
  SettingsView._addHandlerPickupAdRemove(controlRemovePickupAd);
  SettingsView._addHandlerCFDQRUpload(controlUploadCFDQR);
  SettingsView._addHandlerCFDQRRemove(controlRemoveCFDQR);
  // Support
  SupportView._addHandlerOpen(controlOpenSupport);
  SupportView._addHandlerClose();
  SupportView._addHandlerNewTicket(() => SupportView.showNewTicketPanel());
  SupportView._addHandlerCloseNewTicket(() => { SupportView.closeNewTicketModal(); SupportView.resetTicketForm(); });
  SupportView._addHandlerBackToList(() => SupportView.showListPanel());
  SupportView._addHandlerTicketClick(controlOpenTicket);
  SupportView._addHandlerMarkSolved(controlMarkTicketSolved);
  SupportView._addHandlerSubmitTicket(controlSubmitTicket);
  SupportView._addHandlerSendReply(controlSendReply);
  SupportView._addHandlerRating(controlSubmitRating);
  SupportView._addHandlerRatingComment(controlSubmitRatingComment);
  SupportView._addHandlerTicketFiles();
  // Queue/Customer/Pickup screen launchers - collapsed into a single
  // "Screens" dropdown (2026-08-26, Kelly's ask - matches the Terminal
  // picker's toggle+dropdown pattern instead of three separate buttons).
  const displayDropdown     = document.getElementById('displayDropdown');
  const displayDropdownBtn  = document.getElementById('displayDropdownToggle');
  const displayDropdownMenu = document.getElementById('displayDropdownMenu');
  const kdsWindowBtn = document.getElementById('kdsWindowBtn');
  const cfdWindowBtn = document.getElementById('cfdWindowBtn');
  const pickupWindowBtn = document.getElementById('pickupWindowBtn');
  if (displayDropdown && displayDropdownBtn && displayDropdownMenu) {
    displayDropdown.classList.remove('hidden');
    const closeDisplayMenu = () => {
      displayDropdownMenu.classList.add('hidden');
      displayDropdownBtn.classList.remove('nav-terminal-btn--open');
      displayDropdownBtn.setAttribute('aria-expanded', 'false');
    };
    displayDropdownBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const opening = displayDropdownMenu.classList.contains('hidden');
      displayDropdownMenu.classList.toggle('hidden');
      displayDropdownBtn.classList.toggle('nav-terminal-btn--open', opening);
      displayDropdownBtn.setAttribute('aria-expanded', String(opening));
      if (opening) setTimeout(() => document.addEventListener('click', closeDisplayMenu, { once: true }), 0);
    });
    kdsWindowBtn?.addEventListener('click', () => { closeDisplayMenu(); controlOpenKDSWindow(); });
    cfdWindowBtn?.addEventListener('click', () => { closeDisplayMenu(); controlOpenCFDWindow(); });
    pickupWindowBtn?.addEventListener('click', () => { closeDisplayMenu(); controlOpenPickupWindow(); });
  }

  // Terminal number: identifies this POS device and pairs it to its customer
  // display (the CFD only renders cart updates carrying its matching number).
  const terminalControl  = document.getElementById('terminalControl');
  const terminalToggle   = document.getElementById('terminalToggle');
  const terminalDropdown = document.getElementById('terminalDropdown');
  const terminalGrid     = document.getElementById('terminalGrid');
  const terminalValueEl  = document.getElementById('terminalValueLabel');
  if (terminalControl && terminalToggle && terminalDropdown && terminalGrid && terminalValueEl) {
    terminalControl.classList.remove('hidden');
    _populateTerminalOptions(terminalGrid, terminalValueEl); // async; sizes the grid to the team

    const closeTerminalMenu = () => {
      terminalDropdown.classList.add('hidden');
      terminalToggle.classList.remove('nav-terminal-btn--open');
      terminalToggle.setAttribute('aria-expanded', 'false');
    };
    terminalToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      const opening = terminalDropdown.classList.contains('hidden');
      terminalDropdown.classList.toggle('hidden');
      terminalToggle.classList.toggle('nav-terminal-btn--open', opening);
      terminalToggle.setAttribute('aria-expanded', String(opening));
      if (opening) setTimeout(() => document.addEventListener('click', closeTerminalMenu, { once: true }), 0);
    });
    terminalGrid.addEventListener('click', (e) => {
      const chip = e.target.closest('.nav-terminal-chip');
      if (!chip) return;
      e.stopPropagation();
      const value = chip.dataset.value;
      localStorage.setItem('pointbunny_terminal_number', value);
      terminalValueEl.textContent = value;
      terminalGrid.querySelectorAll('.nav-terminal-chip').forEach(c =>
        c.classList.toggle('nav-terminal-chip--active', c.dataset.value === value));
      closeTerminalMenu();
      _broadcastCart(); // re-sync the now-matching customer display with the live cart
    });
  }

  // Navbar notification bell: draft POs / pending shift corrections / pending
  // leave requests. Reuses the Terminal/Screens dropdown chrome (same open/
  // close/outside-click pattern). Stays hidden until _refreshNotifications()
  // reveals it for an account that can see at least one category.
  const notifControl  = document.getElementById('notifControl');
  const notifToggle   = document.getElementById('notifToggle');
  const notifDropdown = document.getElementById('notifDropdown');
  if (notifControl && notifToggle && notifDropdown) {
    const closeNotifMenu = () => {
      notifDropdown.classList.add('hidden');
      notifToggle.classList.remove('nav-terminal-btn--open');
      notifToggle.setAttribute('aria-expanded', 'false');
    };
    _closeNotifMenu = closeNotifMenu;
    notifToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      const opening = notifDropdown.classList.contains('hidden');
      notifDropdown.classList.toggle('hidden');
      notifToggle.classList.toggle('nav-terminal-btn--open', opening);
      notifToggle.setAttribute('aria-expanded', String(opening));
      if (opening) {
        _requestNotifRefresh();
        setTimeout(() => document.addEventListener('click', closeNotifMenu, { once: true }), 0);
      }
    });
  }
  NotifView._addHandlerCategoryClick(controlNotifOpenCategory);
  NotifView._addHandlerItemClick(controlNotifOpenItem);

  // Receipt Adjustments Panel
  DiscountView._addHandlerOpen(controlOpenDiscounts);
  DiscountView._addHandlerClose(controlCloseDiscounts);
  // Loyalty panel
  LoyaltyView._addHandlerOpen(controlOpenLoyalty);
  LoyaltyView._addHandlerClose(controlCloseLoyalty);
  LoyaltyView._addHandlerSettingsChange();
  LoyaltyView._addHandlerSettingsSave(controlSaveLoyaltySettings);
  LoyaltyView._addHandlerSettingsDiscard(controlDiscardLoyaltySettings);
  LoyaltyView._addHandlerDiscountRewardEdit();
  LoyaltyView._addHandlerDiscountRewardCancel();
  LoyaltyView._addHandlerDiscountRewardSave(controlSaveLoyaltySettings);
  LoyaltyView._addHandlerSearch(controlLoyaltySearch);
  LoyaltyView._addHandlerCustomerClick(controlOpenLoyaltyCustomer);
  LoyaltyView._addHandlerAddCustomer(controlLoyaltyShowAddForm);
  LoyaltyView._addHandlerAddCustomerSave(controlLoyaltyAddCustomer);
  LoyaltyView._addHandlerEditCustomer(controlLoyaltyEditCustomer);
  LoyaltyView._addHandlerRewardSearch(controlLoyaltyRewardSearch);
  LoyaltyView._addHandlerRewardPick(controlLoyaltyRewardPick);
  LoyaltyView._addHandlerRewardCancelPick(controlLoyaltyRewardCancelPick);
  LoyaltyView._addHandlerRewardAdd(controlAddLoyaltyReward);
  LoyaltyView._addHandlerRewardToggle(controlToggleLoyaltyReward);
  LoyaltyView._addHandlerRewardDelete(controlDeleteLoyaltyReward);
  // Promos panel
  PromoView._addHandlerOpen(controlOpenPromos);
  PromoView._addHandlerClose(controlClosePromos);
  PromoView._addHandlerNewCode(controlNewDiscountCode);
  PromoView._addHandlerSave(controlSaveDiscountCode);
  PromoView._addHandlerEdit(controlEditDiscountCode);
  PromoView._addHandlerDelete(controlDeleteDiscountCode);
  PromoView._addHandlerToggleStatus(controlToggleDiscountStatus);
  // Tax Settings + Tax-Exempt Products
  DiscountView._addHandlerSelectTaxMode(controlSetTaxMode);
  DiscountView._addHandlerSetTaxRate(controlSetTaxRate);
  DiscountView._addHandlerSetTaxLabel(controlSetTaxLabel);
  DiscountView._addHandlerSetExemptLabel(controlSetTaxExemptLabel);
  DiscountView._addHandlerSaveTaxSettings(controlSaveTaxSettingsDraft);
  DiscountView._addHandlerDiscardTaxSettings(controlDiscardTaxSettingsDraft);
  DiscountView._addHandlerScrollToAdjustments();
  DiscountView._addHandlerOpenExemptModal();
  DiscountView._addHandlerCloseExemptModal();
  DiscountView._addHandlerExemptMethodToggle();
  DiscountView._addHandlerExemptSearch();
  DiscountView._addHandlerExemptSearchPick();
  DiscountView._addHandlerExemptAutoSelect();
  DiscountView._addHandlerExemptCategorySelect();
  DiscountView._addHandlerExemptRemove();
  DiscountView._addHandlerExemptClear();
  DiscountView._addHandlerExemptSave(controlSaveExemptProducts);
  // Automatic Receipt Adjustments
  DiscountView._addHandlerAdjAdd();
  DiscountView._addHandlerAdjSave(controlSaveAdjustment);
  DiscountView._addHandlerAdjEdit(controlEditAdjFromPanel);
  DiscountView._addHandlerAdjDelete(controlDeleteAdjustment);
  DiscountView._addHandlerAdjToggle(controlToggleAdjustment);
  DiscountView._addHandlerAdjMoreToggle();

  // Dashboard stat cards double as Reports shortcuts. The number stays visible to
  // everyone; clicking through to Reports shows a "no access" message for anyone
  // without view_reports (controlOpenReports also enforces this as a backstop).
  const _openReportsIfAllowed = (section) => {
    if (model.hasPermission('view_reports')) controlOpenReports(section);
    else showToast("You don't have access to reports.", 'error');
  };

  // Today's Sales stat card → open Reports on Sales section
  const salesStatCard = document.querySelector(".home-sales-stat");
  if (salesStatCard) {
    salesStatCard.addEventListener("click", () => _openReportsIfAllowed("sales"));
    salesStatCard.addEventListener("keydown", e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); _openReportsIfAllowed("sales"); } });
  }

  // Transactions stat card → open Reports on Traffic section
  const transactionsCard = document.querySelector("#transactionsCard");
  if (transactionsCard) {
    transactionsCard.addEventListener("click", () => _openReportsIfAllowed("traffic"));
    transactionsCard.addEventListener("keydown", e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); _openReportsIfAllowed("traffic"); } });
  }

  // Avg. Serving stat → open Reports on Kitchen section
  const servingStatEl = document.querySelector(".home-dash-stat--serving");
  if (servingStatEl) {
    servingStatEl.addEventListener("click", () => _openReportsIfAllowed("kitchen"));
    servingStatEl.addEventListener("keydown", e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); _openReportsIfAllowed("kitchen"); } });
  }

  // Reports
  ReportsView._addHandlerOpen(controlOpenReports);
  ReportsView._addHandlerClose(controlCloseReports);
  ReportsView._addHandlerPeriodChange(controlReportsPeriodChange);
  ReportsView._addHandlerCustomRange(controlReportsPeriodChange);
  ReportsView._addHandlerExport(controlExportReports, controlExportReportsPDF);
  ReportsView._addHandlerCompareToggle(controlToggleCompare);
  ReportsView._addHandlerRunComparison(controlRunComparison);
  ReportsView._addHandlerCmpTopItemsSort(controlCmpSortTopItems);
  ReportsView._addHandlerTopItemsSort(controlSortTopItems);
  ReportsView._addHandlerStaffSort(controlSortStaff);
  ReportsView._addHandlerInfoTooltips();
  ReportsView._addHandlerServingMetricToggle();
  ReportsView._addHandlerChannelSort();
  ReportsView._addHandlerSections();
  ReportsView._addHandlerKpiToggle(controlToggleMetric);

  // Cashier switcher
  document.getElementById('cashierCard')?.addEventListener('click', controlSwitchCashier);

  // Staff
  StaffView._addHandlerOpen(controlOpenStaff);
  StaffView._addHandlerClose(controlCloseStaff);
  StaffView._addHandlerInvite(controlShowInviteForm);
  StaffView._addHandlerSaveInvite(controlInviteStaff);
  StaffView._addHandlerRemove(controlRemoveStaff);
  StaffView._addHandlerRemoveMFA(controlRemoveStaffMFA);
  StaffView._addHandlerEditRole(controlEditStaffRole);
  StaffView._addHandlerRoleAction(controlRoleAction);
  StaffView._addHandlerSetPin(controlSetStaffPin);

  // Timesheets (own top-level panel)
  TimesheetView._addHandlerOpen(controlOpenTimesheets);
  TimesheetView._addHandlerClose(controlCloseTimesheets);
  TimesheetView._addHandlerTimesheetPeriod(controlFetchTimesheets);
  TimesheetView._addHandlerGenerateTimeclockToken(controlGenerateTimeclockToken);
  TimesheetView._addHandlerReviewRequest(controlReviewRequest);
  TimesheetView._addHandlerReviewLeave(controlReviewLeave);
  TimesheetView._addHandlerDeleteShifts(controlDeleteShifts);
  TimesheetView._addHandlerRestoreShift(controlRestoreShift);
  TimesheetView._addHandlerRevokeDevice(controlRevokeDevice);
  TimesheetView._addHandlerSaveShift(controlSaveShift);

  // Inventory (own top-level panel)
  InventoryView._addHandlerOpen(controlOpenInventory);
  InventoryView._addHandlerClose(controlCloseInventory);
  InventoryView._addHandlerAdjust(controlAdjustStock);
  InventoryView._addHandlerNewItem(controlCreateInventoryItem);
  InventoryView._addHandlerEditItem(controlUpdateInventoryItem);
  InventoryView._addHandlerDeleteItem(controlDeleteInventoryItem);
  InventoryView._addHandlerHistoryFilter(controlInventoryHistoryFilter);
  InventoryView._addHandlerToggleEnabled(controlToggleInventoryEnabled);
  InventoryView._addHandlerAddItemSupplier(controlAddItemSupplier);
  InventoryView._addHandlerPreferSupplier(controlSetPreferredSupplier);
  InventoryView._addHandlerSupplierCost(controlUpdateSupplierCost);
  InventoryView._addHandlerRemoveItemSupplier(controlRemoveItemSupplier);
  InventoryView._addHandlerAddLink(controlAddProductLink);
  InventoryView._addHandlerUpdateLink(controlUpdateProductLink);
  InventoryView._addHandlerRemoveLink(controlRemoveProductLink);
  InventoryView._addHandlerEditProduct(controlEditLinkedProduct);
  InventoryView._addHandlerSaveSupplier(controlSaveSupplier);
  InventoryView._addHandlerDeleteSupplier(controlDeleteSupplier);
  InventoryView._addHandlerCreatePo(controlCreatePurchaseOrder);
  InventoryView._addHandlerUpdatePo(controlUpdatePurchaseOrder);
  InventoryView._addHandlerPoStatus(controlSetPoStatus);
  InventoryView._addHandlerReceivePo(controlReceivePurchaseOrder);
  InventoryView._addHandlerApplyCount(controlApplyStockCount);

  // Home sidebar Time Clock shortcut → Time Clock hub (or the kiosk for staff)
  document.getElementById('homeSideNavTimeclock')?.addEventListener('click', controlOpenTimeclockHub);

  // Getting Started card: one-time dismiss (business-level)
  _wireGettingStarted();
};

// Wait for supabase-js to establish the session from an OAuth redirect URL (implicit
// token in the hash, or a PKCE ?code=) before we read or strip it. supabase processes
// the URL asynchronously, so reading getSession() too early returns null and bounces
// the user back to the login screen. Resolves on the SIGNED_IN event, or as soon as a
// session exists, or after a short fallback so it can never hang.
const _awaitOAuthSession = () => new Promise((resolve) => {
  let settled = false;
  let sub = null;
  const finish = () => {
    if (settled) return;
    settled = true;
    try { sub?.subscription?.unsubscribe(); } catch { /* noop */ }
    resolve();
  };
  ({ data: sub } = supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') finish();
  }));
  supabase.auth.getSession().then(({ data }) => { if (data?.session) finish(); });
  setTimeout(finish, 4000);
});

const initAuth = async function () {
  // Capture ?source=<slug> off a tracking link before anything below strips the query
  // string. Best-effort/localStorage-only (see sourceTracking.js) - never throws.
  const urlSource = sourceTracking.captureSourceFromUrl();
  if (urlSource) history.replaceState(null, '', window.location.pathname + window.location.hash);

  AuthView._addHandlerSignIn(controlSignIn);
  AuthView._addHandlerSignUp(controlSignUp);
  AuthView._addHandlerFormStarted(controlSourceFormStarted);
  AuthView._addHandlerForgotPassword(controlForgotPassword);
  AuthView._addHandlerAcceptInvite(controlAcceptInvite);
  AuthView._addHandlerAcceptJoin(controlAcceptPendingInvite);
  AuthView._addHandlerDeclineJoin(controlDeclinePendingInvite);
  AuthView._addHandlerGoogleSignIn(controlGoogleSignIn);
  OnboardingView._addHandlerSubmit(controlOnboardingSubmit);
  OnboardingView._addHandlerTaxPrompt(controlAddSuggestedTax, controlSkipSuggestedTax);
  OnboardingView._addHandlerExit(async () => {
    await supabase.auth.signOut();
    OnboardingView.hide();
    AuthView.show();
  });
  document.getElementById('logoutBtn').addEventListener('click', controlSignOut);

  // Auth-aware Back/forward guard: the session is the source of truth, not the URL.
  // Like mainstream apps, navigating back/forward to a stale auth URL resolves by
  // session - signed in stays in the app (and the URL is cleaned), signed out shows
  // the sign-in screen. This is what keeps a "Back" from re-surfacing token hashes.
  window.addEventListener('popstate', async () => {
    if (window.location.hash) history.replaceState(null, '', window.location.pathname);
    const { data: { session: s } } = await supabase.auth.getSession();
    if (s) AuthView.hide();
    else AuthView.show();
  });

  // Capture any auth params from the URL hash, then immediately clean the URL so
  // session tokens (access_token / refresh_token) never linger in the address bar or
  // browser history. supabase-js has already parsed the hash into a session by the
  // time this runs, so these are just flags now - safe to strip.
  const hashParams  = new URLSearchParams(window.location.hash.slice(1));
  const authError   = hashParams.get('error');
  const errorDesc   = decodeURIComponent((hashParams.get('error_description') || '').replace(/\+/g, ' '));
  const isInvite    = window.location.hash.includes('type=invite');

  // (Password-reset links no longer land here - they go to the standalone reset.html
  // page. That removed the recovery race-handling that used to sit at the top of this
  // function.)

  // Returning from a Google/OAuth redirect: wait for supabase to finish reading the
  // session out of the URL BEFORE we strip the URL or call getSession() below, so the
  // sign-in doesn't silently bounce back to the login screen.
  const isOAuthReturn = window.location.hash.includes('access_token') || /[?&]code=/.test(window.location.search);
  if (isOAuthReturn && !authError) await _awaitOAuthSession();

  if (window.location.hash) history.replaceState(null, '', window.location.pathname);
  if (/[?&]code=/.test(window.location.search)) history.replaceState(null, '', window.location.pathname);

  // OAuth error callback: Supabase redirects back with #error=... on failure
  if (authError) {
    AuthView.show();
    const isEmailConflict = /already registered|already exists/i.test(errorDesc);
    AuthView.showError(
      isEmailConflict
        ? 'An account with this email already exists. Please sign in with your email and password.'
        : (errorDesc || 'Sign-in failed. Please try again.')
    );
    return;
  }

  // Invite acceptance: staff clicking their invite email link
  if (isInvite) {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      AuthView.show();
      AuthView.showInviteForm(session.user.user_metadata?.first_name);
      return;
    }
  }

  const { data: { session } } = await supabase.auth.getSession();
  if (session) {
    // 2FA gate for restored sessions still at aal1 (a code was never entered).
    // A fully-verified session is already aal2, so this returns immediately.
    const mfaOk = await _enforceMFA();
    if (!mfaOk) {
      await supabase.auth.signOut();
      AuthView.show();
      AuthView.showError('Two-factor verification is required to sign in.');
      return;
    }
    showLoadingScreen();
    try {
      await initApp(session.user);
    } catch (err) {
      if (err.code === 'PENDING_INVITE_CONSENT') {
        hideLoadingScreen();
        AuthView.show();
        AuthView.showJoinTeam(model.state.pendingInvites);
        return;
      }
      console.error('initApp failed:', err);
      hideLoadingScreen();
      await supabase.auth.signOut();
      AuthView.show();
      AuthView.showError(['STAFF_DEACTIVATED', 'ADMIN_ACCOUNT'].includes(err.code) ? err.message : 'Something went wrong. Please sign in again.');
      return;
    }
    hideLoadingScreen();
    _wireApp();
    _showLaunchPrompts();
  } else {
    AuthView.show();
    if (sessionStorage.getItem('pointbunny_deactivated')) {
      sessionStorage.removeItem('pointbunny_deactivated');
      AuthView.showError('Your account has been deactivated. Please contact your business owner.');
    } else if (urlSource) {
      // Arrived via a tracking link (this load's URL, not just a stale stored one) -
      // jump straight to sign-up instead of making them click through from login.
      const stored = sourceTracking.getStoredSource();
      const sessionId = sourceTracking.getOrCreateVisitorId();
      if (stored && !sourceTracking.hasFiredEvent('link_opened')) {
        sourceTracking.markEventFired('link_opened');
        model.logRegistrationEvent('link_opened', { sourceId: stored.source, sessionId });
      }
      AuthView.showSignUp();
      if (stored && !sourceTracking.hasFiredEvent('form_viewed')) {
        sourceTracking.markEventFired('form_viewed');
        model.logRegistrationEvent('form_viewed', { sourceId: stored.source, sessionId });
      }
    }
  }
};

// ── BroadcastChannel incoming messages ────────────────────────────────────────

channel.onmessage = function ({ data }) {
  switch (data.type) {
    case MSG.KDS_REQUEST_SYNC:
      channel.postMessage({
        type: MSG.KDS_QUEUE_SYNC,
        queue: modelState.orderQueue,
        thresholds: {
          yellow: model.state.settings.kdsYellowThreshold,
          red: model.state.settings.kdsRedThreshold,
        },
      });
      break;
    case MSG.KDS_ORDER_DONE: {
      // A KDS popup finished this order and already persisted it (wrote prepared_at);
      // just mirror the removal in the home Active Queue - don't re-write the DB or
      // start another undo window here.
      const i = modelState.orderQueue.findIndex(o => o.id === data.id);
      if (i !== -1) {
        modelState.orderQueue.splice(i, 1);
        KDSView.renderQueue(modelState.orderQueue);
        if (modelState.orderQueue.length === 0) _stopKDSTick();
      }
      break;
    }
    case MSG.KDS_ORDER_READY: {
      // A sibling screen marked this order ready for pickup - or reverted it
      // back to Preparing (readyAt: null) - and already persisted it; just
      // mirror the status locally.
      const order = modelState.orderQueue.find(o => o.id === data.id);
      if (order) {
        order.readyAt = data.readyAt ?? null;
        KDSView.renderQueue(modelState.orderQueue);
      }
      break;
    }
    case MSG.KDS_QUEUE_SYNC:
      model.state.orderQueue = data.queue;
      KDSView.renderQueue(model.state.orderQueue);
      if (model.state.orderQueue.length === 0) _stopKDSTick();
      else _ensureKDSTick();
      _requestActivityRefresh(); // a sale on another terminal is a feed event
      break;
    case MSG.CFD_REQUEST_SYNC:
      channel.postMessage({
        type: MSG.CFD_CART_UPDATE,
        terminalNumber: _terminalNumber(),
        cart: modelState.cart,
        total: modelState.cart.reduce((s, i) => s + i.totalPrice, 0),
      });
      break;
    case MSG.SHIFT_SYNC:
      // A time-clock device reported a clock in/out or break change - refresh the
      // on-shift bubble now rather than waiting for the next 60s poll.
      _refreshOnShift();
      _requestActivityRefresh();
      break;
    case MSG.INVENTORY_SYNC:
      // Another terminal changed stock (sale, void, restock, link edit). Refetch
      // and patch in place - order-screen badges and the panel, no re-renders
      // that would blink.
      model.loadInventory().then(() => {
        NewOrderView.updateStockBadges?.(model.productStockStatuses());
        if (InventoryView.isOpen()) {
          Promise.all([
            model.loadInventoryAdjustments(_inventoryHistoryFilter).catch(() => []),
            model.loadSuppliers().catch(() => []),
            model.loadPurchaseOrders().catch(() => []),
          ]).then(() => InventoryView.refreshIfOpen(_inventorySnapshot()));
        }
        _requestActivityRefresh(); // after the refetch so low-stock rows see fresh quantities
        _refreshNotifications(); // another terminal may have drafted/approved a PO
      }).catch(() => {});
      break;
  }
};

initAuth();
